import React, { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { EXPLORE_UNKNOWN_PORTAL_LAYOUT, EXPLORE_UNKNOWN_PORTAL_AUTHORING_SIZE } from './exploreUnknownPortalLayout.js';
import {
  resolveExploreUnknownAuthoringPointInCoverContainer,
  resolveExploreUnknownAuthoringRectInCoverContainer,
} from './utils/exploreUnknownPortalCoords';
import { requestPendingSeasonEndCheck } from './utils/requestPendingSeasonEndCheck';
import ExploreUnknownGridOverlay from './ExploreUnknownGridOverlay';
import ExploreUnknownArrivalPointsOverlay from './ExploreUnknownArrivalPointsOverlay';
import {
  computeMapSlicePointStatesForRoute,
  isAlternativePathEligibleSurface,
  needsRouteChoiceForSurface,
  resolveActiveRouteForSurface,
  resolveNextAvailableForRun,
  resolveNextMissionInSequence,
  filterAltPathEasyMissionIndices,
} from './utils/exploreUnknownAlternativePath';
import {
  buildCanonicalSurfaceMissionIndices,
  getAlternativePathDisplayLayout,
} from './utils/exploreUnknownAlternativePathLayout';
import { buildSurfaceRouteBundle, resolveDarknessBargainAuthoringPointForRoute } from './utils/exploreUnknownRouteLayout';
import {
  EXPLORE_SURFACE_CHAIN,
  arePrerequisiteSurfacesCompleteForTarget,
  getExploreSurfaceChainIndex,
  isSurfaceCompleteForActiveRoute,
  resolvePendingSurfaceExitTransition,
  resolveRouteDestinationPreview,
  shouldDeferAutoSurfaceAdvance,
  tryBuildSurfaceRouteExitPreview,
} from './utils/exploreUnknownSurfaceTransition';
import { getExploreUnknownSurfaceFirstMissionPreviewUrl } from './config/exploreUnknownMissionBattle';
import {
  exitPreviewDevPathModeForVariant,
  exitPreviewVariantFromDevPathMode,
  isExitPreviewDevPathMode,
  resolveExitPreviewVariantForMissionsRemoved,
  resolveRouteExitPreviewAuthoringPoint,
} from './utils/exploreUnknownRouteExitPreviewLayout';
import ExploreUnknownForkRouteDialog from './ExploreUnknownForkRouteDialog';
import ExploreUnknownMobileCollectibleDrawer from './ExploreUnknownMobileCollectibleDrawer';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ExploreUnknown.css';
import './exploreUnknownMobile.css';
import './BattleUI.css';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { stopAllAudio } from './hooks/musicState';
import { playSoundEffect } from './hooks/useSoundEffects';
import { isExploreUnknownKendrexColonySurface } from './config/exploreUnknownKendrexColony';
import { setExploreUnknownKendrexColonyMapAudioSuppressed } from './utils/exploreUnknownKendrexColonyAudio';
import { getEffectiveSoundOutputVolume } from './hooks/soundEffectsManager';
import { useContainerScale } from './hooks/useViewportScale';
import { EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS } from './config/exploreUnknownDarkKnowledge';
import {
  EXPLORE_UNKNOWN_HEART_METERS,
  EXPLORE_UNKNOWN_HEART_TARGET_PARTS,
} from './config/exploreUnknownHearts';
import {
  EXPLORE_UNKNOWN_EP_METERS,
  EXPLORE_UNKNOWN_EP_TARGET_PARTS,
} from './config/exploreUnknownEmergencyProtocol';
import {
  EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_METER,
  EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS,
} from './config/exploreUnknownRewriteExperience';
import { ExploreUnknownHeartProgressBar } from './components/ExploreUnknownHeartProgressBar';
import { computeMapSlicePointStates } from './utils/exploreUnknownMapSlice';
import { resolveOptionalSkipPlanForPlayer, applySkippingBonusToggleCascade } from './utils/exploreUnknownOptionalSkipPlan';
import { exploreUnknownXpBarPercent } from './utils/exploreUnknownXpBarPercent';
import ExploreUnknownLevelTree from './ExploreUnknownLevelTree';
import ExploreUnknownPostcards from './ExploreUnknownPostcards';
import ExploreUnknownLevel100DoorToast from './ExploreUnknownLevel100DoorToast';
import {
  EU_LEVEL100_DOOR_UNLOCKED_TOAST_KEY,
  markExploreUnknownLevel100DoorUnlockSeen,
} from './utils/exploreUnknownLevel100DoorUnlock';
import {
  readExploreUnknownStoredStrategyIdOrNull,
  resolveExploreUnknownStartScreenStrategyId,
  writeExploreUnknownStoredStrategyId,
} from './utils/exploreUnknownStrategyStorage';
import ExploreUnknownDarkKnowledgeRewardModal from './exploreUnknownRewardPopups/ExploreUnknownDarkKnowledgeRewardModal';
import ExploreUnknownDevExperiencePanel from './ExploreUnknownDevExperiencePanel';
import { API_URL, WS_URL, getWebSocketAuthMessage } from './config/apiConfig';
import { stashGameSocket } from './matchmaking/gameSocketHandoff';
import { needsTokenRefresh, refreshToken, isTokenValid } from './auth';
import CustomDropdown from './CustomDropdown';
import MenuPaintedButton from './components/MenuPaintedButton';
import MenuBackButton from './components/MenuBackButton';
import {
  BATTLE_UI_END_TURN_BUTTON_PATH,
  BATTLE_UI_RESIGN_BUTTON_PATH,
  EXPLORE_UNKNOWN_EXPLORE_BUTTON_PATH,
  EXPLORE_UNKNOWN_PORTAL_BACKGROUND_PATH,
  EXPLORE_UNKNOWN_MISSION_PANEL_SURFACE_FILTER,
  MENU_PAINTED_SURFACE_FILTER,
  RESIGN_DIALOG_BACKGROUND_PATH,
} from './config/welcomeAssets';
import { resolvePublicAssetBackgroundUrl, resolvePublicAssetUrl } from './utils/resolvePublicAssetUrl';
import './StrategyModal.css';
import { normalizeStrategyValidationResponse } from './utils/normalizeStrategyValidationResponse';
import { TutorialMessageBox, TutorialReplayButton, useMenuTutorialReplay, isPlayer1DevUser, resolvePlayer1DisplayUsername } from './tutorial/index';
import { useTutorialStatus } from './hooks/useTutorialStatus';
import { BATTLE_OVERVIEW_MODE_STICKER_KEYS } from './battleOverview/modeStickerKeys';
import { useDismissBattleOverviewModeSticker } from './battleOverview/useDismissBattleOverviewModeSticker';
import {
  loadDevWaypoints,
  saveDevWaypoints,
  clearDevWaypoints,
  getExpectedWaypointCount,
  getExpectedWaypointCountForPath,
} from './utils/exploreUnknownWaypointDevStorage';
import { useExploreUnknownMinigameSurface } from './exploreUnknownMinigames/useExploreUnknownMinigameSurface';
import ExploreUnknownMinigameHost from './exploreUnknownMinigames/ExploreUnknownMinigameHost';
import EmergencyProtocolModal from './EmergencyProtocolModal';
import ExploreUnknownDarknessMeter from './components/ExploreUnknownDarknessMeter';
import { buildExploreUnknownDevMockWinAfterBattleNavigateState } from './utils/exploreUnknownDevMockWinRewards';
import HideDevElementsForScreenshotButton from './devmode/HideDevElementsForScreenshotButton';
import { useHideDevElementsForScreenshot } from './devmode/useHideDevElementsForScreenshot';
import {
  tryGetExploreUnknownBattleBackgroundPath,
  resolveBattleBackgroundCssUrl,
} from './config/exploreUnknownMissionBattle';
import { preloadBattleBackgroundMedia, preloadImage } from './hooks/mediaPreload';

function getExploreUnknownMapPaintedPanelStyle() {
  return {
    '--explore-unknown-map-painted-panel-bg': resolvePublicAssetBackgroundUrl(RESIGN_DIALOG_BACKGROUND_PATH),
    '--explore-unknown-map-painted-panel-surface-filter': EXPLORE_UNKNOWN_MISSION_PANEL_SURFACE_FILTER,
  };
}

function getStrategyOrderCount(strategy) {
  if (!strategy || !Array.isArray(strategy.orders)) return 0;
  return strategy.orders.reduce((total, order) => total + (order.count || 0), 0);
}

function isValidOrderCount(strategy) {
  const orderCount = getStrategyOrderCount(strategy);
  return orderCount >= 50 && orderCount <= 100;
}

/** @param {unknown} raw */
function parseExploreUnknownDarknessPercent(raw) {
  if (raw === undefined || raw === null) {
    throw new Error('Explore Unknown API response missing darknessPercent');
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || n > 100) {
    throw new Error(`Explore Unknown: invalid darknessPercent (${String(raw)})`);
  }
  return Math.round(n);
}

/** @param {unknown} raw */
function parseExploreUnknownPlayerStartingHealth(raw) {
  if (raw === undefined || raw === null) {
    throw new Error('Explore Unknown API response missing playerStartingHealth');
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 5) {
    throw new Error(`Explore Unknown: invalid playerStartingHealth (${String(raw)})`);
  }
  return Math.round(n);
}

/** @param {unknown} raw */
function parseExploreUnknownBargainUsed(raw, fieldName) {
  if (raw !== true && raw !== false) {
    throw new Error(
      `Explore Unknown API: invalid darkness bargain used flag for ${fieldName} (${String(raw)})`
    );
  }
  return raw;
}

const EXPLORE_UNKNOWN_DARKNESS_BARGAIN_USED_FIELDS = Object.freeze([
  'darknessBargainM3Used',
  'darknessBargainM6Used',
  'darknessBargainM11Used',
  'darknessBargainM19Used',
  'darknessBargainM25Used',
  'darknessBargainM42Used',
  'darknessBargainM49Used',
  'darknessBargainM56Used',
  'darknessBargainM64Used',
]);

/**
 * @param {Record<string, unknown>} data
 * @param {Record<string, (value: boolean) => void>} setters
 */
function applyExploreUnknownDarknessBargainSetters(data, setters) {
  for (const field of EXPLORE_UNKNOWN_DARKNESS_BARGAIN_USED_FIELDS) {
    const setter = setters[field];
    if (typeof setter !== 'function') {
      throw new Error(`Explore Unknown: missing setter for ${field}`);
    }
    setter(parseExploreUnknownBargainUsed(data[field], field));
  }
}

/** @param {unknown} raw */
function parseExploreUnknownDarkKnowledgePartsCollected(raw) {
  if (raw === undefined || raw === null) {
    throw new Error('Explore Unknown API response missing darkKnowledgePartsCollected');
  }
  const n = Number(raw);
  if (
    !Number.isFinite(n) ||
    !Number.isInteger(n) ||
    n < 0 ||
    n > EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS
  ) {
    throw new Error(`Explore Unknown: invalid darkKnowledgePartsCollected (${String(raw)})`);
  }
  return n;
}

/** @param {unknown} raw */
function parseExploreUnknownHeartPartsCollected(raw, fieldName) {
  if (raw === undefined || raw === null) {
    throw new Error(`Explore Unknown API response missing ${fieldName}`);
  }
  const n = Number(raw);
  if (
    !Number.isFinite(n) ||
    !Number.isInteger(n) ||
    n < 0 ||
    n > EXPLORE_UNKNOWN_HEART_TARGET_PARTS
  ) {
    throw new Error(`Explore Unknown: invalid ${fieldName} (${String(raw)})`);
  }
  return n;
}

/** @param {unknown} raw */
function parseExploreUnknownHeartSetsCompleted(raw, fieldName) {
  if (raw === undefined || raw === null) {
    throw new Error(`Explore Unknown API response missing ${fieldName}`);
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
    throw new Error(`Explore Unknown: invalid ${fieldName} (${String(raw)})`);
  }
  return n;
}

/** @param {Record<string, unknown>} data */
function readHeartRunFromPayload(data) {
  return {
    weakHeartPartsCollected: parseExploreUnknownHeartPartsCollected(
      data.weakHeartPartsCollected,
      'weakHeartPartsCollected'
    ),
    heartPartsCollected: parseExploreUnknownHeartPartsCollected(
      data.heartPartsCollected,
      'heartPartsCollected'
    ),
    strongHeartPartsCollected: parseExploreUnknownHeartPartsCollected(
      data.strongHeartPartsCollected,
      'strongHeartPartsCollected'
    ),
    weakHeartSetsCompleted: parseExploreUnknownHeartSetsCompleted(
      data.weakHeartSetsCompleted,
      'weakHeartSetsCompleted'
    ),
    heartSetsCompleted: parseExploreUnknownHeartSetsCompleted(
      data.heartSetsCompleted,
      'heartSetsCompleted'
    ),
    strongHeartSetsCompleted: parseExploreUnknownHeartSetsCompleted(
      data.strongHeartSetsCompleted,
      'strongHeartSetsCompleted'
    ),
  };
}

/** @param {Record<string, unknown>} data */
function readEpRunFromPayload(data) {
  const parseParts = (raw, fieldName) => {
    const n = Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0 || n > EXPLORE_UNKNOWN_EP_TARGET_PARTS - 1) {
      throw new Error(`Explore Unknown: invalid ${fieldName} (${String(raw)})`);
    }
    return n;
  };
  const parseCharges = (raw, fieldName) => {
    const n = Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
      throw new Error(`Explore Unknown: invalid ${fieldName} (${String(raw)})`);
    }
    return n;
  };
  return {
    epGlobalPartsCollected: parseParts(data.epGlobalPartsCollected, 'epGlobalPartsCollected'),
    epGlobalCharges: parseCharges(data.epGlobalCharges, 'epGlobalCharges'),
    epZonePartsCollected: parseParts(data.epZonePartsCollected, 'epZonePartsCollected'),
    epZoneCharges: parseCharges(data.epZoneCharges, 'epZoneCharges'),
    epTargetPartsCollected: parseParts(data.epTargetPartsCollected, 'epTargetPartsCollected'),
    epTargetCharges: parseCharges(data.epTargetCharges, 'epTargetCharges'),
  };
}

function readRewriteExperienceRunFromPayload(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('readRewriteExperienceRunFromPayload: data is required');
  }
  const parseParts = (raw, fieldName) => {
    const n = Number(raw);
    if (
      !Number.isFinite(n) ||
      !Number.isInteger(n) ||
      n < 0 ||
      n > EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS - 1
    ) {
      throw new Error(`Explore Unknown: invalid ${fieldName} (${String(raw)})`);
    }
    return n;
  };
  const parseCharges = (raw, fieldName) => {
    const n = Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
      throw new Error(`Explore Unknown: invalid ${fieldName} (${String(raw)})`);
    }
    return n;
  };
  return {
    rewriteExperiencePartsCollected: parseParts(
      data.rewriteExperiencePartsCollected,
      'rewriteExperiencePartsCollected'
    ),
    rewriteExperienceCharges: parseCharges(
      data.rewriteExperienceCharges,
      'rewriteExperienceCharges'
    ),
  };
}

/** Current map surface → API tier key (milestones 3, 7, 14, 19, 25). */
const SURFACE_TO_DARKNESS_BARGAIN_TIER = {
  arrival: 'm3',
  cave: 'm6',
  desert: 'm11',
  fair: 'm19',
  lavender: 'm25',
  kendrex: 'm42',
  autobahn: 'm49',
  productionhell: 'm56',
  awakening: 'm64',
};

/** Must match `DARKNESS_BARGAIN_MIN_PRIOR_HEALTH` on server (5 + 5). */
const EXPLORE_UNKNOWN_BARGAIN_MIN_PRIOR_HEALTH = 10;

/** Chapter order for +5% darkness when advancing to a deeper map (`POST /explore-unknown/continue-surface`). */
const EXPLORE_SURFACE_DARKNESS_TIER = Object.freeze({
  arrival: 0,
  cave: 1,
  desert: 2,
  fair: 3,
  lavender: 4,
  treetop: 5,
  boat: 6,
  pixelpast: 7,
  kendrex: 8,
  autobahn: 9,
  productionhell: 10,
  awakening: 11,
  core: 12,
});

const EXPLORE_UNKNOWN_ALTERNATIVE_PATH_MIN_LEVEL = 20;

/** Surface keys → `exploreUnknownPage.*` map title keys (aligned with `exploreUnknownPortalLayout.js`). */
const EXPLORE_UNKNOWN_MAP_NAME_I18N_KEYS = Object.freeze({
  arrival: 'mapNameArrival',
  cave: 'mapNameCave',
  desert: 'mapNameDesert',
  fair: 'mapNameFair',
  lavender: 'mapNameLavender',
  treetop: 'mapNameTreetop',
  boat: 'mapNameBoat',
  pixelpast: 'mapNamePixelPast',
  kendrex: 'mapNameKendrex',
  autobahn: 'mapNameAutobahn',
  productionhell: 'mapNameProductionHell',
  awakening: 'mapNameAwakening',
  core: 'mapNameCore',
});

/**
 * @param {string} from
 * @param {string} to
 * @returns {boolean}
 */
function exploreSurfaceChargesDarkness(from, to) {
  const a = EXPLORE_SURFACE_DARKNESS_TIER[from];
  const b = EXPLORE_SURFACE_DARKNESS_TIER[to];
  if (a === undefined || b === undefined) {
    throw new Error(`Explore Unknown: invalid surface for darkness tier (${String(from)} → ${String(to)})`);
  }
  return b > a;
}

const EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY = 'exploreUnknownSurface';
const EXPLORE_UNKNOWN_NEGARA_DOOR_MISSION_INDEX = 75;
const STRATEGY_VALIDATION_TIMEOUT_MS = 12000;

const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  'Ra\'s Children': '#F4A460',
  'Asrah': '#8B0000',
  'The Sentients': '#ADD8E6',
  'Combined': '#FFA500',
};

const factionIconByName = {
  'Remnants of Humanity': 'remnants.png',
  'Exolot Dominion': 'exolot.png',
  'League of Shadows': 'league.png',
  'Ra\'s Children': 'ra.png',
  'Asrah': 'asrah.png',
  'The Conspiracy': 'conspiracy.png',
  'The Corrupted Core': 'core.png',
  'The Covenant': 'covenant.png',
  'The Divine Syndicate': 'syndicate.png',
  'The Forsaken Pact': 'forsaken.png',
  'The Hollow': 'hollow.png',
  'The Iron Alliance': 'alliance.png',
  'The Last Crusade': 'crusade.png',
  'The Shadow Consortium': 'consortium.png',
  'The Silent Accord': 'silent.png',
  'The Sentients': 'sentients.png',
};

const getFactionOrbBackground = (factionName) => {
  if (!factionName || factionName === 'Unknown') {
    return '#ccc';
  }

  if (factionName === 'The Conspiracy') {
    return 'linear-gradient(to right, #8B0000 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Corrupted Core') {
    return 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)';
  } else if (factionName === 'The Covenant') {
    return 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Divine Syndicate') {
    return 'linear-gradient(to right, #F4A460 50%, #4B0082 50%)';
  } else if (factionName === 'The Forsaken Pact') {
    return 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)';
  } else if (factionName === 'The Hollow') {
    return 'linear-gradient(to right, #F4A460 50%, #8B0000 50%)';
  } else if (factionName === 'The Iron Alliance') {
    return 'linear-gradient(to right, #556B2F 50%, #4B0082 50%)';
  } else if (factionName === 'The Last Crusade') {
    return 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)';
  } else if (factionName === 'The Shadow Consortium') {
    return 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)';
  } else if (factionName === 'The Silent Accord') {
    return 'linear-gradient(to right, #556B2F 50%, #1A1A1A 50%)';
  }

  return factionColors[factionName] || '#ccc';
};

const getFactionIconPath = (factionName) => {
  if (!factionName || factionName === 'Unknown') {
    return null;
  }

  const iconFile = factionIconByName[factionName];
  if (!iconFile) {
    throw new Error(`Explore Unknown faction icon mapping missing for faction: ${factionName}`);
  }

  return resolvePublicAssetUrl(`/factions/${iconFile}`);
};

function readExploreSurfaceFromStorage() {
  try {
    const s = sessionStorage.getItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
    if (
      s === 'cave' ||
      s === 'arrival' ||
      s === 'desert' ||
      s === 'fair' ||
      s === 'lavender' ||
      s === 'treetop' ||
      s === 'boat' ||
      s === 'pixelpast' ||
      s === 'kendrex' ||
      s === 'autobahn' ||
      s === 'productionhell' ||
      s === 'awakening' ||
      s === 'core'
    ) {
      return s;
    }
  } catch {
    /* ignore */
  }
  return 'arrival';
}

/**
 * Prefer the furthest known surface between server run row and sessionStorage (never regress on refresh).
 * @param {{ currentSurface?: string, initialSurface?: string }} data
 * @returns {string | null}
 */
function resolveExploreUnknownSurfaceFromRunPayload(data) {
  const fromServer =
    typeof data?.currentSurface === 'string' && data.currentSurface
      ? data.currentSurface
      : typeof data?.initialSurface === 'string' && data.initialSurface
        ? data.initialSurface
        : null;
  if (!fromServer || !EXPLORE_SURFACE_CHAIN.includes(fromServer)) {
    return null;
  }
  try {
    const stored = sessionStorage.getItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
    if (stored && EXPLORE_SURFACE_CHAIN.includes(stored)) {
      const storedIdx = EXPLORE_SURFACE_CHAIN.indexOf(stored);
      const serverIdx = EXPLORE_SURFACE_CHAIN.indexOf(fromServer);
      if (storedIdx > serverIdx) {
        return stored;
      }
    }
  } catch {
    /* ignore */
  }
  return fromServer;
}

/**
 * @param {{ wins?: number, losses?: number, attempts?: number, winRatePercent?: number | null } | null | undefined} entry
 * @param {(key: string, opts?: object) => string} t
 */
function formatExploreUnknownCompletionRateValue(entry, t) {
  if (!entry || !Number(entry.attempts) || entry.winRatePercent == null) {
    return t('exploreUnknownPage.missionCompletionRateNoData');
  }
  return t('exploreUnknownPage.missionCompletionRatePercent', { percent: entry.winRatePercent });
}

async function fetchJsonWithTimeout(url, options, timeoutMs) {
  const ac = new AbortController();
  const timeoutId = window.setTimeout(() => ac.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: ac.signal });
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function ExploreUnknown({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    scale: viewportScale,
    offsetX: viewportOffsetX,
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile,
  } = useContainerScale();

  const exploreContainerRef = useRef(null);
  const scannerVideoRef = useRef(null);
  const strategyHintTimerRef = useRef(null);
  const resumeHandledRef = useRef(false);
  const mapAnimTimerRef = useRef(null);
  const pendingResumeRunIdRef = useRef(null);
  /** After map return animation: refetch run row so darkness / mission count match the server. */
  const mapRefreshWasAnimatingRef = useRef(false);

  const [strategies, setStrategies] = useState([]);
  const [strategiesError, setStrategiesError] = useState(null);
  const [strategyValidations, setStrategyValidations] = useState({});
  const [selectedStrategyId, setSelectedStrategyId] = useState(() => {
    try {
      return readExploreUnknownStoredStrategyIdOrNull() ?? '';
    } catch (err) {
      console.error('Explore Unknown: failed to read stored strategy on init', err);
      return '';
    }
  });
  /** Set when the player enters the portal; strategy choice is final. */
  const [committedStrategyId, setCommittedStrategyId] = useState(null);
  const [strategyHintVisible, setStrategyHintVisible] = useState(false);
  /** null | 'scanner' | 'arrival' */
  const [sequencePhase, setSequencePhase] = useState(null);
  const sequencePhaseRef = useRef(sequencePhase);
  sequencePhaseRef.current = sequencePhase;

  const [missionProgress, setMissionProgress] = useState(null);
  const [missionProgressError, setMissionProgressError] = useState(null);
  const [exploreUnknownPostcardMissionIndices, setExploreUnknownPostcardMissionIndices] = useState([]);
  const missionProgressRunIdRef = useRef(null);
  const [startingMission, setStartingMission] = useState(false);
  const [missionStartError, setMissionStartError] = useState(null);
  const [mapRefreshAnimating, setMapRefreshAnimating] = useState(false);
  const [portalBackgroundNaturalSize, setPortalBackgroundNaturalSize] = useState(null);
  const [portalBackgroundPreloaded, setPortalBackgroundPreloaded] = useState(false);
  const [portalBackgroundPreloadError, setPortalBackgroundPreloadError] = useState(null);
  const [exploreWs, setExploreWs] = useState(null);
  const exploreWsPreserveRef = useRef(false);
  const [activeRunId, setActiveRunId] = useState(null);
  const [missionsCompletedInRun, setMissionsCompletedInRun] = useState(0);
  /** 0–100: darkness toward the eclipse; server adds % during battle (turn milestones) and on mission win (general attacks). */
  const [darknessPercent, setDarknessPercent] = useState(0);
  /** Fragments collected toward the Dark Knowledge legendary reward this run (0…target). */
  const [darkKnowledgePartsCollected, setDarkKnowledgePartsCollected] = useState(0);
  const [weakHeartPartsCollected, setWeakHeartPartsCollected] = useState(0);
  const [heartPartsCollected, setHeartPartsCollected] = useState(0);
  const [strongHeartPartsCollected, setStrongHeartPartsCollected] = useState(0);
  const [weakHeartSetsCompleted, setWeakHeartSetsCompleted] = useState(0);
  const [heartSetsCompleted, setHeartSetsCompleted] = useState(0);
  const [strongHeartSetsCompleted, setStrongHeartSetsCompleted] = useState(0);
  const [epGlobalPartsCollected, setEpGlobalPartsCollected] = useState(0);
  const [epGlobalCharges, setEpGlobalCharges] = useState(0);
  const [epZonePartsCollected, setEpZonePartsCollected] = useState(0);
  const [epZoneCharges, setEpZoneCharges] = useState(0);
  const [epTargetPartsCollected, setEpTargetPartsCollected] = useState(0);
  const [epTargetCharges, setEpTargetCharges] = useState(0);
  const [rewriteExperiencePartsCollected, setRewriteExperiencePartsCollected] = useState(0);
  const [rewriteExperienceCharges, setRewriteExperienceCharges] = useState(0);
  /** Player starting health for future EU missions this run (default 30; reduced by darkness bargain). */
  const [playerStartingHealth, setPlayerStartingHealth] = useState(30);
  const [darknessBargainM3Used, setDarknessBargainM3Used] = useState(false);
  const [darknessBargainM6Used, setDarknessBargainM6Used] = useState(false);
  const [darknessBargainM11Used, setDarknessBargainM11Used] = useState(false);
  const [darknessBargainM19Used, setDarknessBargainM19Used] = useState(false);
  const [darknessBargainM25Used, setDarknessBargainM25Used] = useState(false);
  const [darknessBargainM42Used, setDarknessBargainM42Used] = useState(false);
  const [darknessBargainM49Used, setDarknessBargainM49Used] = useState(false);
  const [darknessBargainM56Used, setDarknessBargainM56Used] = useState(false);
  const [darknessBargainM64Used, setDarknessBargainM64Used] = useState(false);
  const [darknessBargainModalTier, setDarknessBargainModalTier] = useState(null);
  const [darknessBargainPurchaseBusy, setDarknessBargainPurchaseBusy] = useState(false);
  const [darknessBargainPurchaseError, setDarknessBargainPurchaseError] = useState(null);
  const [nextMissionCompletionRates, setNextMissionCompletionRates] = useState(null);
  const [nextMissionCompletionRatesLoading, setNextMissionCompletionRatesLoading] = useState(false);
  const [nextMissionCompletionRatesError, setNextMissionCompletionRatesError] = useState(null);
  const [continueSurfaceBusy, setContinueSurfaceBusy] = useState(false);
  const [continueSurfaceError, setContinueSurfaceError] = useState(null);
  const [worldEntryRewardPopup, setWorldEntryRewardPopup] = useState(null);
  const [runSessionError, setRunSessionError] = useState(null);
  /** True when run id + mission count came from GET /explore-unknown/active-run (skip POST run-session). */
  const [runHydratedFromApi, setRunHydratedFromApi] = useState(false);
  /** False until we know whether to show intro (portal) or hydrate mid-run map. */
  const [pendingActiveRunCheck, setPendingActiveRunCheck] = useState(true);
  const [showTutorialExploreUnknown, setShowTutorialExploreUnknown] = useState(false);
  const { shouldShowTutorial, markSeenLocally } = useTutorialStatus(user);
  useDismissBattleOverviewModeSticker(user, BATTLE_OVERVIEW_MODE_STICKER_KEYS.exploreUnknown);
  const { isReplayRequest: isReplayingExploreTutorial } = useMenuTutorialReplay('explore_unknown', {
    enabled: Boolean(user) && !pendingActiveRunCheck,
    onReplay: () => setShowTutorialExploreUnknown(true),
  });
  const [exploreUnknownLevel, setExploreUnknownLevel] = useState(1);
  const [exploreUnknownTotalXp, setExploreUnknownTotalXp] = useState(0);
  const [exploreUnknownXpIntoLevel, setExploreUnknownXpIntoLevel] = useState(0);
  const [exploreUnknownXpToNext, setExploreUnknownXpToNext] = useState(50);
  const [exploreUnknownMilestoneBonuses, setExploreUnknownMilestoneBonuses] = useState({});
  const [exploreUnknownLevelDefinitions, setExploreUnknownLevelDefinitions] = useState({});
  const [exploreUnknownBonusSelections, setExploreUnknownBonusSelections] = useState({});
  const [showReactiveDoctrineModal, setShowReactiveDoctrineModal] = useState(false);
  const [exploreUnknownEndgameBonusId, setExploreUnknownEndgameBonusId] = useState(null);
  const [exploreUnknownSkippingBonuses, setExploreUnknownSkippingBonuses] = useState({});
  const [exploreUnknownResolvedBonuses, setExploreUnknownResolvedBonuses] = useState(null);
  const [skipMissionBusy, setSkipMissionBusy] = useState(false);
  const [skipMissionError, setSkipMissionError] = useState(null);
  const [showResignRunDialog, setShowResignRunDialog] = useState(false);
  const [resignRunBusy, setResignRunBusy] = useState(false);
  const [resignRunError, setResignRunError] = useState(null);
  const [resignRunDialogFocus, setResignRunDialogFocus] = useState('cancel');
  const [exploreEndGoHomeBusy, setExploreEndGoHomeBusy] = useState(false);
  const [exploreEndGoHomeError, setExploreEndGoHomeError] = useState(null);
  const [showNegaraDoorIntro, setShowNegaraDoorIntro] = useState(false);
  const [showLevel100DoorToast, setShowLevel100DoorToast] = useState(false);

  const skipMissionEnabled = exploreUnknownResolvedBonuses?.skipMissionEnabled === true;
  const skipMissionDarknessCost = Number(exploreUnknownResolvedBonuses?.skipMissionDarknessCost) || 0;
  const altPathEasierBossesEnabled = exploreUnknownResolvedBonuses?.altPathEasierBosses === true;

  const optionalSkipInitialSurface = useMemo(
    () =>
      resolveOptionalSkipPlanForPlayer(
        exploreUnknownLevel,
        exploreUnknownLevelDefinitions,
        exploreUnknownSkippingBonuses
      ).initialSurface,
    [exploreUnknownLevel, exploreUnknownLevelDefinitions, exploreUnknownSkippingBonuses]
  );
  const [exploreUnknownRefinedErrayCount, setExploreUnknownRefinedErrayCount] = useState(0);
  const [exploreUnknownRefinedErraySpare, setExploreUnknownRefinedErraySpare] = useState(0);
  const [exploreUnknownRefinedErrayAttachments, setExploreUnknownRefinedErrayAttachments] = useState({});
  const [exploreUnknownRefinedErrayAttachedBonusId, setExploreUnknownRefinedErrayAttachedBonusId] =
    useState(null);
  const [alternativePathPlan, setAlternativePathPlan] = useState({});
  const [routeChoices, setRouteChoices] = useState({});
  const [routeChoiceBusy, setRouteChoiceBusy] = useState(false);
  const [routeChoiceError, setRouteChoiceError] = useState(null);
  const [devPathEditMode, setDevPathEditMode] = useState('normal');
  const [skipTwoWorldsNotice, setSkipTwoWorldsNotice] = useState(null);
  const [skipWorldPendingBossStart, setSkipWorldPendingBossStart] = useState(null);
  /** After INSTINCT (73): show KINSHIP intro + Continue before starting mission 74. */
  const [kinshipBossQueued, setKinshipBossQueued] = useState(false);
  /** `arrival` 1–5 · `cave` 6–10 · `desert` 11–16 · `fair` 17–23 · `lavender` after fair (see `exploreUnknownPortalLayout.js`). */
  const [exploreSurface, setExploreSurface] = useState(readExploreSurfaceFromStorage);
  const exploreSurfaceRef = useRef(exploreSurface);
  exploreSurfaceRef.current = exploreSurface;

  /** Stable fairgrounds ambient pick for this map visit (avoid re-rolling on unrelated re-renders). */
  const fairAmbientTrackRef = useRef(null);
  /** Stable Pixel Past ambient pick for this map visit (avoid re-rolling on unrelated re-renders). */
  const pixelPastAmbientTrackRef = useRef(null);
  const exploreUnknownAmbientMusicPath = useMemo(() => {
    if (isExploreUnknownKendrexColonySurface(exploreSurface)) {
      fairAmbientTrackRef.current = null;
      pixelPastAmbientTrackRef.current = null;
      return null;
    }
    const onFairgroundsMapView = exploreSurface === 'fair' && sequencePhase === 'arrival';
    if (onFairgroundsMapView) {
      if (fairAmbientTrackRef.current == null) {
        fairAmbientTrackRef.current =
          Math.random() < 0.5 ? '/music/fair1.mp3' : '/music/fair2.mp3';
      }
      return fairAmbientTrackRef.current;
    }
    fairAmbientTrackRef.current = null;

    const onPixelPastMapView = exploreSurface === 'pixelpast' && sequencePhase === 'arrival';
    if (onPixelPastMapView) {
      if (pixelPastAmbientTrackRef.current == null) {
        pixelPastAmbientTrackRef.current =
          Math.random() < 0.5 ? '/music/pixel1.mp3' : '/music/pixel2.mp3';
      }
      return pixelPastAmbientTrackRef.current;
    }
    pixelPastAmbientTrackRef.current = null;

    return '/music/unknown.mp3';
  }, [exploreSurface, sequencePhase]);

  useMusic(exploreUnknownAmbientMusicPath);

  useEffect(() => {
    const suppressed = isExploreUnknownKendrexColonySurface(exploreSurface);
    setExploreUnknownKendrexColonyMapAudioSuppressed(suppressed);
    if (suppressed) {
      stopAllAudio();
    }
    return () => setExploreUnknownKendrexColonyMapAudioSuppressed(false);
  }, [exploreSurface]);

  const [devAutowinBusy, setDevAutowinBusy] = useState(false);
  const [devAutowinError, setDevAutowinError] = useState(null);
  const [devLevelRewardBusy, setDevLevelRewardBusy] = useState(null);
  const [devLevelRewardError, setDevLevelRewardError] = useState(null);
  const [devForceLossBusy, setDevForceLossBusy] = useState(false);
  const [devForceLossError, setDevForceLossError] = useState(null);
  /** Set from map `<img>` onLoad — aligns POI SVG with `object-fit: contain` (see ExploreUnknownArrivalPointsOverlay). */
  const [mapImageNaturalSize, setMapImageNaturalSize] = useState(null);
  const mapImageRef = useRef(null);
  if (!EXPLORE_UNKNOWN_PORTAL_LAYOUT) {
    throw new Error(
      'Explore Unknown: portal layout failed to load — check exploreUnknownPortalLayout.js for syntax errors, then hard-refresh the page (Ctrl+Shift+R).'
    );
  }
  const mapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.mapSliceMissionCount ?? 5;

  /** Global indices for the arrival map slice (e.g. 1–5). */
  const arrivalMissionIndices = useMemo(
    () => Array.from({ length: mapSliceMissionCount }, (_, i) => i + 1),
    [mapSliceMissionCount]
  );

  /** First global mission index on the desert map (after arrival + cave slices). */
  const desertFirstMissionIndex = useMemo(() => 2 * mapSliceMissionCount + 1, [mapSliceMissionCount]);

  /** Global mission indices for the cave map slice (e.g. 6–10 when mapSliceMissionCount is 5). */
  const caveMissionIndices = useMemo(
    () =>
      Array.from({ length: mapSliceMissionCount }, (_, i) => mapSliceMissionCount + 1 + i),
    [mapSliceMissionCount]
  );

  const desertMapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.desertMapSliceMissionCount ?? 6;
  const fairMapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.fairMapSliceMissionCount ?? 7;
  const lavenderMapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.lavenderMapSliceMissionCount ?? 5;
  const treetopMapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.treetopMapSliceMissionCount ?? 1;
  const boatMapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.boatMapSliceMissionCount ?? 5;
  const pixelPastMapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.pixelPastMapSliceMissionCount ?? 6;
  const kendrexMapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.kendrexMapSliceMissionCount ?? 6;
  const autobahnMapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.autobahnMapSliceMissionCount ?? 6;
  const productionHellMapSliceMissionCount =
    EXPLORE_UNKNOWN_PORTAL_LAYOUT.productionHellMapSliceMissionCount ?? 7;
  const awakeningMapSliceMissionCount =
    EXPLORE_UNKNOWN_PORTAL_LAYOUT.awakeningMapSliceMissionCount ?? 8;
  const coreMapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.coreMapSliceMissionCount ?? 5;

  /** Global mission indices on the fairgrounds map (e.g. 17–23). */
  const fairMissionIndices = useMemo(
    () =>
      Array.from({ length: fairMapSliceMissionCount }, (_, i) => desertFirstMissionIndex + desertMapSliceMissionCount + i),
    [fairMapSliceMissionCount, desertFirstMissionIndex, desertMapSliceMissionCount]
  );

  /** Global mission indices on the desert map (e.g. 11–16). */
  const desertMissionIndices = useMemo(
    () =>
      Array.from({ length: desertMapSliceMissionCount }, (_, i) => desertFirstMissionIndex + i),
    [desertMapSliceMissionCount, desertFirstMissionIndex]
  );

  /** Global mission indices on the lavender map (e.g. 24–28). */
  const lavenderMissionIndices = useMemo(
    () =>
      Array.from(
        { length: lavenderMapSliceMissionCount },
        (_, i) => desertFirstMissionIndex + desertMapSliceMissionCount + fairMapSliceMissionCount + i
      ),
    [lavenderMapSliceMissionCount, desertFirstMissionIndex, desertMapSliceMissionCount, fairMapSliceMissionCount]
  );

  /** Global mission indices on the treetop map (e.g. 29). */
  const treetopMissionIndices = useMemo(
    () =>
      Array.from(
        { length: treetopMapSliceMissionCount },
        (_, i) =>
          desertFirstMissionIndex +
          desertMapSliceMissionCount +
          fairMapSliceMissionCount +
          lavenderMapSliceMissionCount +
          i
      ),
    [
      treetopMapSliceMissionCount,
      desertFirstMissionIndex,
      desertMapSliceMissionCount,
      fairMapSliceMissionCount,
      lavenderMapSliceMissionCount,
    ]
  );

  /** Global mission indices on the boat map (e.g. 30–34). */
  const boatMissionIndices = useMemo(
    () =>
      Array.from(
        { length: boatMapSliceMissionCount },
        (_, i) =>
          desertFirstMissionIndex +
          desertMapSliceMissionCount +
          fairMapSliceMissionCount +
          lavenderMapSliceMissionCount +
          treetopMapSliceMissionCount +
          i
      ),
    [
      boatMapSliceMissionCount,
      desertFirstMissionIndex,
      desertMapSliceMissionCount,
      fairMapSliceMissionCount,
      lavenderMapSliceMissionCount,
      treetopMapSliceMissionCount,
    ]
  );

  /** Global mission indices on the Pixel Past map (e.g. 35–40). */
  const pixelPastMissionIndices = useMemo(
    () =>
      Array.from(
        { length: pixelPastMapSliceMissionCount },
        (_, i) =>
          desertFirstMissionIndex +
          desertMapSliceMissionCount +
          fairMapSliceMissionCount +
          lavenderMapSliceMissionCount +
          treetopMapSliceMissionCount +
          boatMapSliceMissionCount +
          i
      ),
    [
      pixelPastMapSliceMissionCount,
      desertFirstMissionIndex,
      desertMapSliceMissionCount,
      fairMapSliceMissionCount,
      lavenderMapSliceMissionCount,
      treetopMapSliceMissionCount,
      boatMapSliceMissionCount,
    ]
  );

  /** Global mission indices on the Kendrex Colony map (e.g. 41–46). */
  const kendrexMissionIndices = useMemo(
    () =>
      Array.from(
        { length: kendrexMapSliceMissionCount },
        (_, i) =>
          desertFirstMissionIndex +
          desertMapSliceMissionCount +
          fairMapSliceMissionCount +
          lavenderMapSliceMissionCount +
          treetopMapSliceMissionCount +
          boatMapSliceMissionCount +
          pixelPastMapSliceMissionCount +
          i
      ),
    [
      kendrexMapSliceMissionCount,
      desertFirstMissionIndex,
      desertMapSliceMissionCount,
      fairMapSliceMissionCount,
      lavenderMapSliceMissionCount,
      treetopMapSliceMissionCount,
      boatMapSliceMissionCount,
      pixelPastMapSliceMissionCount,
    ]
  );

  /** Global mission indices on the Autobahn map (e.g. 47–53). */
  const autobahnMissionIndices = useMemo(
    () =>
      Array.from(
        { length: autobahnMapSliceMissionCount },
        (_, i) =>
          desertFirstMissionIndex +
          desertMapSliceMissionCount +
          fairMapSliceMissionCount +
          lavenderMapSliceMissionCount +
          treetopMapSliceMissionCount +
          boatMapSliceMissionCount +
          pixelPastMapSliceMissionCount +
          kendrexMapSliceMissionCount +
          i
      ),
    [
      autobahnMapSliceMissionCount,
      desertFirstMissionIndex,
      desertMapSliceMissionCount,
      fairMapSliceMissionCount,
      lavenderMapSliceMissionCount,
      treetopMapSliceMissionCount,
      boatMapSliceMissionCount,
      pixelPastMapSliceMissionCount,
      kendrexMapSliceMissionCount,
    ]
  );

  /** Global mission indices on the Production Hell map (e.g. 54–60). */
  const productionHellMissionIndices = useMemo(
    () =>
      Array.from(
        { length: productionHellMapSliceMissionCount },
        (_, i) =>
          desertFirstMissionIndex +
          desertMapSliceMissionCount +
          fairMapSliceMissionCount +
          lavenderMapSliceMissionCount +
          treetopMapSliceMissionCount +
          boatMapSliceMissionCount +
          pixelPastMapSliceMissionCount +
          kendrexMapSliceMissionCount +
          autobahnMapSliceMissionCount +
          i
      ),
    [
      productionHellMapSliceMissionCount,
      desertFirstMissionIndex,
      desertMapSliceMissionCount,
      fairMapSliceMissionCount,
      lavenderMapSliceMissionCount,
      treetopMapSliceMissionCount,
      boatMapSliceMissionCount,
      pixelPastMapSliceMissionCount,
      kendrexMapSliceMissionCount,
      autobahnMapSliceMissionCount,
    ]
  );

  /** Global mission indices on The Awakening map (e.g. 61–68). */
  const awakeningMissionIndices = useMemo(
    () =>
      Array.from(
        { length: awakeningMapSliceMissionCount },
        (_, i) =>
          desertFirstMissionIndex +
          desertMapSliceMissionCount +
          fairMapSliceMissionCount +
          lavenderMapSliceMissionCount +
          treetopMapSliceMissionCount +
          boatMapSliceMissionCount +
          pixelPastMapSliceMissionCount +
          kendrexMapSliceMissionCount +
          autobahnMapSliceMissionCount +
          productionHellMapSliceMissionCount +
          i
      ),
    [
      awakeningMapSliceMissionCount,
      desertFirstMissionIndex,
      desertMapSliceMissionCount,
      fairMapSliceMissionCount,
      lavenderMapSliceMissionCount,
      treetopMapSliceMissionCount,
      boatMapSliceMissionCount,
      pixelPastMapSliceMissionCount,
      kendrexMapSliceMissionCount,
      autobahnMapSliceMissionCount,
      productionHellMapSliceMissionCount,
    ]
  );

  /** Global mission indices on The Core (e.g. 69–73). */
  const coreMissionIndices = useMemo(
    () =>
      Array.from(
        { length: coreMapSliceMissionCount },
        (_, i) =>
          desertFirstMissionIndex +
          desertMapSliceMissionCount +
          fairMapSliceMissionCount +
          lavenderMapSliceMissionCount +
          treetopMapSliceMissionCount +
          boatMapSliceMissionCount +
          pixelPastMapSliceMissionCount +
          kendrexMapSliceMissionCount +
          autobahnMapSliceMissionCount +
          productionHellMapSliceMissionCount +
          awakeningMapSliceMissionCount +
          i
      ),
    [
      coreMapSliceMissionCount,
      desertFirstMissionIndex,
      desertMapSliceMissionCount,
      fairMapSliceMissionCount,
      lavenderMapSliceMissionCount,
      treetopMapSliceMissionCount,
      boatMapSliceMissionCount,
      pixelPastMapSliceMissionCount,
      kendrexMapSliceMissionCount,
      autobahnMapSliceMissionCount,
      productionHellMapSliceMissionCount,
      awakeningMapSliceMissionCount,
    ]
  );

  /** Chained KINSHIP boss (mission 74) immediately after INSTINCT on The Core. */
  const kinshipMissionIndex = useMemo(
    () => (coreMissionIndices.length > 0 ? coreMissionIndices[coreMissionIndices.length - 1] + 1 : null),
    [coreMissionIndices]
  );

  const hasEnteredPortal = committedStrategyId !== null;

  const strategyDropdownOptions = useMemo(
    () =>
      strategies.map((s) => {
        if (!s || s.id == null) {
          throw new Error('Explore Unknown strategy dropdown: strategy id is required.');
        }
        if (!s.faction_name) {
          throw new Error(`Explore Unknown strategy dropdown: strategy faction is required for strategy ${String(s.id)}.`);
        }
        const idKey = String(s.id);
        const validation = strategyValidations[idKey];
        return {
          value: idKey,
          label: s.name,
          factionName: s.faction_name,
          showOwnershipWarning: validation != null && validation.valid !== true,
        };
      }),
    [strategies, strategyValidations]
  );

  const exploreBlockedByIncompleteOwnership = Boolean(
    selectedStrategyId &&
      strategyValidations[selectedStrategyId]?.valid !== true
  );

  const isExploreUnknownDevPlayer = useMemo(() => isPlayer1DevUser(user), [user]);
  const {
    hide: hideDevElementsForScreenshotNow,
    showDevControls: showExploreUnknownPlayer1DevUi,
  } = useHideDevElementsForScreenshot();

  const showExploreUnknownDevAutowin = isExploreUnknownDevPlayer && showExploreUnknownPlayer1DevUi;
  /** Account `player1` only: replay portal tutorial from the start screen without clearing seen state. */
  const showPlayer1PortalTutorialButton = isExploreUnknownDevPlayer && showExploreUnknownPlayer1DevUi;
  const showPlayer1MinigameDevGrid = isExploreUnknownDevPlayer && showExploreUnknownPlayer1DevUi;

  const showExploreUnknownDevWaypointEditor = isExploreUnknownDevPlayer && showExploreUnknownPlayer1DevUi;
  const showExploreUnknownMapDevControls = isExploreUnknownDevPlayer && showExploreUnknownPlayer1DevUi;
  const showExploreUnknownMapDevWaypointEditor = isExploreUnknownDevPlayer && showExploreUnknownPlayer1DevUi;
  const showPlayer1MinigameDevGridOnMap = isExploreUnknownDevPlayer && showExploreUnknownPlayer1DevUi;

  /** Dev-only: overrides layout POIs from localStorage; null = use bundled layout. */
  const [devWaypointOverride, setDevWaypointOverride] = useState(null);
  const [waypointEditMode, setWaypointEditMode] = useState(false);
  const [devWaypointStorageError, setDevWaypointStorageError] = useState(null);
  const [devWaypointUiError, setDevWaypointUiError] = useState(null);
  const [waypointCopyDone, setWaypointCopyDone] = useState(false);
  /** Dev-only: collapse waypoint editor so map Continue prompts stay clickable. */
  const [devWaypointBarHidden, setDevWaypointBarHidden] = useState(false);
  const [devExitPreviewOverride, setDevExitPreviewOverride] = useState({
    normal: null,
    altMinus1: null,
    altMinus2: null,
  });

  useLayoutEffect(() => {
    if (!showExploreUnknownDevWaypointEditor) return;
    setWaypointEditMode(false);
    setDevWaypointStorageError(null);
    if (isExitPreviewDevPathMode(devPathEditMode)) {
      setDevWaypointOverride(null);
      return;
    }
    try {
      setDevWaypointOverride(loadDevWaypoints(exploreSurface, devPathEditMode));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const expectedLen = getExpectedWaypointCountForPath(exploreSurface, devPathEditMode);
      if (msg.includes(`array of length ${expectedLen}`)) {
        // Waypoint count changed (e.g. lavender 4 -> 5): clear stale localStorage snapshot.
        try {
          clearDevWaypoints(exploreSurface, devPathEditMode);
          setDevWaypointOverride(null);
          setDevWaypointStorageError(null);
          return;
        } catch (clearErr) {
          const clearMsg = clearErr instanceof Error ? clearErr.message : String(clearErr);
          setDevWaypointStorageError(clearMsg);
          setDevWaypointOverride(null);
          return;
        }
      }
      setDevWaypointStorageError(msg);
      setDevWaypointOverride(null);
    }
  }, [exploreSurface, devPathEditMode, showExploreUnknownDevWaypointEditor]);

  useLayoutEffect(() => {
    if (!showExploreUnknownDevWaypointEditor) return;
    try {
      const normalStored = loadDevWaypoints(exploreSurface, 'exitPreviewNormal');
      let alt1Stored = loadDevWaypoints(exploreSurface, 'exitPreviewAltMinus1');
      if (alt1Stored == null) {
        alt1Stored = loadDevWaypoints(exploreSurface, 'exitPreviewAlternative');
      }
      const alt2Stored = loadDevWaypoints(exploreSurface, 'exitPreviewAltMinus2');
      setDevExitPreviewOverride({
        normal: normalStored?.[0] ?? null,
        altMinus1: alt1Stored?.[0] ?? null,
        altMinus2: alt2Stored?.[0] ?? null,
      });
    } catch {
      setDevExitPreviewOverride({ normal: null, alternative: null });
    }
  }, [exploreSurface, showExploreUnknownDevWaypointEditor]);

  const mapMissionOffset = useMemo(() => {
    if (exploreSurface === 'awakening') {
      return (
        2 * mapSliceMissionCount +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount +
        boatMapSliceMissionCount +
        pixelPastMapSliceMissionCount +
        kendrexMapSliceMissionCount +
        autobahnMapSliceMissionCount +
        productionHellMapSliceMissionCount
      );
    }
    if (exploreSurface === 'productionhell') {
      return (
        2 * mapSliceMissionCount +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount +
        boatMapSliceMissionCount +
        pixelPastMapSliceMissionCount +
        kendrexMapSliceMissionCount +
        autobahnMapSliceMissionCount
      );
    }
    if (exploreSurface === 'autobahn') {
      return (
        2 * mapSliceMissionCount +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount +
        boatMapSliceMissionCount +
        pixelPastMapSliceMissionCount +
        kendrexMapSliceMissionCount
      );
    }
    if (exploreSurface === 'kendrex') {
      return (
        2 * mapSliceMissionCount +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount +
        boatMapSliceMissionCount +
        pixelPastMapSliceMissionCount
      );
    }
    if (exploreSurface === 'pixelpast') {
      return (
        2 * mapSliceMissionCount +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount +
        boatMapSliceMissionCount
      );
    }
    if (exploreSurface === 'boat') {
      return (
        2 * mapSliceMissionCount +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount
      );
    }
    if (exploreSurface === 'treetop') {
      return (
        2 * mapSliceMissionCount +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount
      );
    }
    if (exploreSurface === 'lavender') {
      return 2 * mapSliceMissionCount + desertMapSliceMissionCount + fairMapSliceMissionCount;
    }
    if (exploreSurface === 'fair') {
      return 2 * mapSliceMissionCount + desertMapSliceMissionCount;
    }
    if (exploreSurface === 'desert') return 2 * mapSliceMissionCount;
    if (exploreSurface === 'cave') return mapSliceMissionCount;
    return 0;
  }, [
    exploreSurface,
    mapSliceMissionCount,
    desertMapSliceMissionCount,
    fairMapSliceMissionCount,
    lavenderMapSliceMissionCount,
    treetopMapSliceMissionCount,
    boatMapSliceMissionCount,
    pixelPastMapSliceMissionCount,
    kendrexMapSliceMissionCount,
    autobahnMapSliceMissionCount,
    productionHellMapSliceMissionCount,
    awakeningMapSliceMissionCount,
  ]);

  const layoutMapPoints = useMemo(() => {
    const surfaceLayoutPrefixById = {
      arrival: 'arrival',
      cave: 'cave',
      desert: 'desert',
      fair: 'fair',
      lavender: 'lavender',
      boat: 'boat',
      pixelpast: 'pixelPast',
      kendrex: 'kendrex',
      autobahn: 'autobahn',
      productionhell: 'productionHell',
      awakening: 'awakening',
    };
    if (
      showExploreUnknownDevWaypointEditor &&
      (devPathEditMode === 'altMinus1' || devPathEditMode === 'altMinus2')
    ) {
      const prefix = surfaceLayoutPrefixById[exploreSurface];
      if (prefix) {
        const altKey =
          devPathEditMode === 'altMinus2'
            ? `${prefix}AlternativeMinus2PointsOfInterest`
            : `${prefix}AlternativeMinus1PointsOfInterest`;
        const altPts = EXPLORE_UNKNOWN_PORTAL_LAYOUT[altKey];
        if (Array.isArray(altPts) && altPts.length > 0) {
          return altPts;
        }
        if (isAlternativePathEligibleSurface(exploreSurface)) {
          const missionsRemoved = devPathEditMode === 'altMinus2' ? 2 : 1;
          const canonical = buildCanonicalSurfaceMissionIndices(exploreSurface, mapMissionOffset);
          const { points } = getAlternativePathDisplayLayout(
            exploreSurface,
            missionsRemoved,
            mapMissionOffset + 1,
            canonical
          );
          return points;
        }
      }
    }
    if (exploreSurface === 'core') {
      return [];
    }
    if (exploreSurface === 'awakening') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.awakeningPointsOfInterest ?? [];
    }
    if (exploreSurface === 'productionhell') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.productionHellPointsOfInterest ?? [];
    }
    if (exploreSurface === 'autobahn') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.autobahnPointsOfInterest ?? [];
    }
    if (exploreSurface === 'kendrex') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.kendrexPointsOfInterest ?? [];
    }
    if (exploreSurface === 'pixelpast') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.pixelPastPointsOfInterest ?? [];
    }
    if (exploreSurface === 'boat') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.boatPointsOfInterest ?? [];
    }
    if (exploreSurface === 'treetop') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.treetopPointsOfInterest ?? [];
    }
    if (exploreSurface === 'lavender') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.lavenderPointsOfInterest ?? [];
    }
    if (exploreSurface === 'fair') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.fairPointsOfInterest ?? [];
    }
    if (exploreSurface === 'desert') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.desertPointsOfInterest;
    }
    if (exploreSurface === 'cave') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.cavePointsOfInterest;
    }
    return EXPLORE_UNKNOWN_PORTAL_LAYOUT.arrivalPointsOfInterest;
  }, [exploreSurface, showExploreUnknownDevWaypointEditor, devPathEditMode, mapMissionOffset]);

  const currentMapPoints = useMemo(() => {
    if (showExploreUnknownDevWaypointEditor && devWaypointOverride != null) {
      const expectedLen = getExpectedWaypointCountForPath(exploreSurface, devPathEditMode);
      if (devWaypointOverride.length === expectedLen) {
        return devWaypointOverride;
      }
    }
    return layoutMapPoints;
  }, [
    showExploreUnknownDevWaypointEditor,
    devWaypointOverride,
    layoutMapPoints,
    exploreSurface,
    devPathEditMode,
  ]);

  const slicePointStates = useMemo(() => {
    if (!missionProgress || !Array.isArray(currentMapPoints) || currentMapPoints.length < 1) return null;
    return computeMapSlicePointStates(
      missionProgress.completedIndices,
      missionProgress.nextAvailable,
      mapMissionOffset,
      currentMapPoints.length
    );
  }, [missionProgress, exploreSurface, mapMissionOffset, currentMapPoints]);

  /**
   * Without mission progress the real slice is null and the POI layer would not mount — dev waypoint
   * editing would be impossible. In development, fall back to all-unavailable so handles still render.
   */
  const effectiveSlicePointStates = useMemo(() => {
    if (slicePointStates != null) return slicePointStates;
    if (
      showExploreUnknownDevWaypointEditor &&
      Array.isArray(currentMapPoints) &&
      currentMapPoints.length > 0
    ) {
      return currentMapPoints.map(() => 'unavailable');
    }
    return null;
  }, [slicePointStates, showExploreUnknownDevWaypointEditor, currentMapPoints]);

  const canonicalSurfaceMissionIndices = useMemo(
    () => buildCanonicalSurfaceMissionIndices(exploreSurface, mapMissionOffset),
    [exploreSurface, mapMissionOffset]
  );

  const effectiveNextAvailable = useMemo(() => {
    if (!missionProgress || !Array.isArray(missionProgress.completedIndices)) return null;
    return resolveNextAvailableForRun(
      missionProgress.completedIndices,
      alternativePathPlan,
      routeChoices
    );
  }, [missionProgress, alternativePathPlan, routeChoices]);

  const surfaceRouteBundle = useMemo(() => {
    const devOverrides = {};
    if (showExploreUnknownDevWaypointEditor && devWaypointOverride != null) {
      if (devPathEditMode === 'normal') {
        devOverrides.normal = devWaypointOverride;
      } else if (devPathEditMode === 'altMinus1') {
        devOverrides.altMinus1 = devWaypointOverride;
      } else if (devPathEditMode === 'altMinus2') {
        devOverrides.altMinus2 = devWaypointOverride;
      }
    }
    return buildSurfaceRouteBundle(
      exploreSurface,
      mapMissionOffset,
      alternativePathPlan,
      devOverrides
    );
  }, [
    exploreSurface,
    mapMissionOffset,
    alternativePathPlan,
    showExploreUnknownDevWaypointEditor,
    devPathEditMode,
    devWaypointOverride,
  ]);

  const forkRouteChoicePending = useMemo(() => {
    if (sequencePhase !== 'arrival' || !isAlternativePathEligibleSurface(exploreSurface)) {
      return false;
    }
    if (exploreUnknownLevel < EXPLORE_UNKNOWN_ALTERNATIVE_PATH_MIN_LEVEL) {
      return false;
    }
    return needsRouteChoiceForSurface(
      exploreSurface,
      routeChoices,
      mapMissionOffset + 1,
      missionProgress?.completedIndices
    );
  }, [
    sequencePhase,
    exploreSurface,
    exploreUnknownLevel,
    mapMissionOffset,
    routeChoices,
    missionProgress,
  ]);

  const pathOverlayConfig = useMemo(() => {
    const mapNameForSurface = (surface) => {
      const leafKey = EXPLORE_UNKNOWN_MAP_NAME_I18N_KEYS[surface];
      if (!leafKey) {
        throw new Error(`Explore Unknown: unknown surface for map name (${String(surface)})`);
      }
      return t(`exploreUnknownPage.${leafKey}`);
    };
    const routeDestinationPreviewOptions = {
      exploreUnknownLevel,
      alternativePathMinLevel: EXPLORE_UNKNOWN_ALTERNATIVE_PATH_MIN_LEVEL,
      routeChoices,
    };
    const buildExitPreview = (missionIndices, route) =>
      tryBuildSurfaceRouteExitPreview(
        exploreSurface,
        route,
        missionIndices,
        mapNameForSurface,
        getExploreUnknownSurfaceFirstMissionPreviewUrl,
        routeDestinationPreviewOptions
      );
    const resolveExitPreviewAuthoring = (variant) =>
      resolveRouteExitPreviewAuthoringPoint(
        exploreSurface,
        variant,
        devExitPreviewOverride?.[variant]
      );

    const idleMapOverlay = {
      points: [],
      missionIndicesPerPoint: null,
      easyMissionIndices: [],
      alternativeBranch: null,
      additionalAlternativeBranches: null,
      mainRouteMissionClickOptions: null,
      mainRouteExitPreview: null,
      mainRouteExitPreviewAuthoringPoint: null,
      forkEditMode: false,
      pointStatesOverride: null,
      devEditNormal: false,
      devEditAlt1: false,
      devEditAlt2: false,
      showAltBossPacifierOnMainRoute: false,
    };
    if (sequencePhase !== 'arrival') {
      return idleMapOverlay;
    }
    if (!isAlternativePathEligibleSurface(exploreSurface)) {
      const canonicalMissionIndices = buildCanonicalSurfaceMissionIndices(
        exploreSurface,
        mapMissionOffset
      );
      return {
        points: currentMapPoints,
        missionIndicesPerPoint: null,
        easyMissionIndices: [],
        alternativeBranch: null,
        additionalAlternativeBranches: null,
        mainRouteMissionClickOptions: null,
        mainRouteExitPreview: buildExitPreview(canonicalMissionIndices, 'normal'),
        mainRouteExitPreviewAuthoringPoint: resolveExitPreviewAuthoring('normal'),
        forkEditMode: false,
        pointStatesOverride: null,
        devEditNormal: false,
        devEditAlt1: false,
        devEditAlt2: false,
        showAltBossPacifierOnMainRoute: false,
      };
    }

    const completedIndices = missionProgress?.completedIndices || [];
    const firstMissionIndex = mapMissionOffset + 1;
    const resolved = resolveActiveRouteForSurface(exploreSurface, alternativePathPlan, routeChoices);
    const routeActive = routeChoices[exploreSurface];
    const altLocked = exploreUnknownLevel < EXPLORE_UNKNOWN_ALTERNATIVE_PATH_MIN_LEVEL;
    const runAltEasy = filterAltPathEasyMissionIndices(
      exploreSurface,
      alternativePathPlan?.[exploreSurface]?.easyMissionIndices || []
    );
    const { normal, alt1, alt2, runAlt, runMissionsRemoved } = surfaceRouteBundle;

    const buildAltBranch = (layout, points, opts) => ({
      points,
      missionIndicesPerPoint: layout.missionIndices,
      forkPoint: layout.forkPoint,
      sharedStartPoint: points[0],
      pointStates: opts.pointStates,
      previewFactions: opts.previewFactions,
      active: opts.active === true,
      locked: opts.locked === true,
      devEditActive: opts.devEditActive === true,
      branchStroke: opts.branchStroke,
      missionClickOptions: { routeBranch: 'alternative' },
      easyMissionIndices: opts.easyMissionIndices ?? [],
      exitPreview: opts.exitPreview ?? null,
      exitPreviewAuthoringPoint: opts.exitPreviewAuthoringPoint ?? null,
      exitPreviewRouteVariant: opts.exitPreviewRouteVariant ?? null,
      onWaypointAuthoringChange: opts.onWaypointAuthoringChange,
    });

    const runAltNext = resolveNextMissionInSequence(completedIndices, runAlt.missionIndices);
    const computeRunAltPreviewStates = (layout) =>
      layout.missionIndices.map((missionIndex) => {
        if (completedIndices.includes(missionIndex)) return 'done';
        if (forkRouteChoicePending && !altLocked && missionIndex === runAltNext) {
          return 'available';
        }
        return 'unavailable';
      });

    const devExitPreviewLayout =
      showExploreUnknownDevWaypointEditor &&
      waypointEditMode === true &&
      isExitPreviewDevPathMode(devPathEditMode);

    const devDragAllRoutes =
      showExploreUnknownDevWaypointEditor &&
      waypointEditMode === true &&
      !devExitPreviewLayout;

    if (devExitPreviewLayout) {
      const unavailable = normal.points.map(() => 'unavailable');
      return {
        points: normal.points,
        missionIndicesPerPoint: normal.missionIndices,
        easyMissionIndices: [],
        pointStatesOverride: unavailable,
        alternativeBranch: buildAltBranch(alt1, alt1.points, {
          pointStates: alt1.missionIndices.map(() => 'unavailable'),
          previewFactions: true,
          active: false,
          locked: false,
          devEditActive: false,
          exitPreviewRouteVariant: 'altMinus1',
          branchStroke: 'rgba(120, 200, 255, 0.55)',
          exitPreview: buildExitPreview(alt1.missionIndices, 'alternative'),
          exitPreviewAuthoringPoint: resolveExitPreviewAuthoring('altMinus1'),
        }),
        additionalAlternativeBranches: [
          buildAltBranch(alt2, alt2.points, {
            pointStates: alt2.missionIndices.map(() => 'unavailable'),
            previewFactions: true,
            active: false,
            locked: false,
            devEditActive: false,
            exitPreviewRouteVariant: 'altMinus2',
            branchStroke: 'rgba(255, 180, 80, 0.55)',
            exitPreview: buildExitPreview(alt2.missionIndices, 'alternative'),
            exitPreviewAuthoringPoint: resolveExitPreviewAuthoring('altMinus2'),
          }),
        ],
        mainRouteMissionClickOptions: null,
        mainRouteExitPreview: buildExitPreview(normal.missionIndices, 'normal'),
        mainRouteExitPreviewAuthoringPoint: resolveExitPreviewAuthoring('normal'),
        forkEditMode: false,
        devEditNormal: false,
        devEditAlt1: false,
        devEditAlt2: false,
        showAltBossPacifierOnMainRoute: false,
      };
    }

    if (devDragAllRoutes) {
      const unavailable = normal.points.map(() => 'unavailable');
      return {
        points: normal.points,
        missionIndicesPerPoint: normal.missionIndices,
        easyMissionIndices: [],
        pointStatesOverride: unavailable,
        alternativeBranch: buildAltBranch(alt1, alt1.points, {
          pointStates: alt1.missionIndices.map(() => 'unavailable'),
          previewFactions: true,
          active: false,
          locked: false,
          devEditActive: devPathEditMode === 'altMinus1',
          exitPreviewRouteVariant: 'altMinus1',
          branchStroke: 'rgba(120, 200, 255, 0.55)',
          exitPreview: buildExitPreview(alt1.missionIndices, 'alternative'),
          exitPreviewAuthoringPoint: resolveExitPreviewAuthoring('altMinus1'),
        }),
        additionalAlternativeBranches: [
          buildAltBranch(alt2, alt2.points, {
            pointStates: alt2.missionIndices.map(() => 'unavailable'),
            previewFactions: true,
            active: false,
            locked: false,
            devEditActive: devPathEditMode === 'altMinus2',
            exitPreviewRouteVariant: 'altMinus2',
            branchStroke: 'rgba(255, 180, 80, 0.55)',
            exitPreview: buildExitPreview(alt2.missionIndices, 'alternative'),
            exitPreviewAuthoringPoint: resolveExitPreviewAuthoring('altMinus2'),
          }),
        ],
        mainRouteMissionClickOptions: null,
        mainRouteExitPreview: buildExitPreview(normal.missionIndices, 'normal'),
        mainRouteExitPreviewAuthoringPoint: resolveExitPreviewAuthoring('normal'),
        forkEditMode: false,
        devEditNormal: devPathEditMode === 'normal',
        devEditAlt1: devPathEditMode === 'altMinus1',
        devEditAlt2: devPathEditMode === 'altMinus2',
        showAltBossPacifierOnMainRoute: false,
      };
    }

    if (routeActive === 'alternative') {
      const altNext = effectiveNextAvailable;
      const altPointStates = computeMapSlicePointStatesForRoute(
        completedIndices,
        altNext,
        runAlt.missionIndices
      );
      return {
        points: runAlt.points,
        missionIndicesPerPoint: runAlt.missionIndices,
        easyMissionIndices: resolved.easyMissionIndices || runAltEasy,
        pointStatesOverride: altPointStates,
        alternativeBranch: null,
        additionalAlternativeBranches: null,
        mainRouteMissionClickOptions: null,
        mainRouteExitPreview: buildExitPreview(runAlt.missionIndices, 'alternative'),
        mainRouteExitPreviewAuthoringPoint: resolveExitPreviewAuthoring(
          resolveExitPreviewVariantForMissionsRemoved(runMissionsRemoved)
        ),
        forkEditMode: false,
        devEditNormal: false,
        devEditAlt1: false,
        devEditAlt2: false,
        showAltBossPacifierOnMainRoute: true,
      };
    }

    if (routeActive === 'normal') {
      const normalNext = effectiveNextAvailable;
      const normalPointStates = computeMapSlicePointStatesForRoute(
        completedIndices,
        normalNext,
        normal.missionIndices
      );
      return {
        points: normal.points,
        missionIndicesPerPoint: normal.missionIndices,
        easyMissionIndices: [],
        pointStatesOverride: normalPointStates,
        alternativeBranch: null,
        additionalAlternativeBranches: null,
        mainRouteMissionClickOptions: null,
        mainRouteExitPreview: buildExitPreview(normal.missionIndices, 'normal'),
        mainRouteExitPreviewAuthoringPoint: resolveExitPreviewAuthoring('normal'),
        forkEditMode: false,
        devEditNormal: false,
        devEditAlt1: false,
        devEditAlt2: false,
        showAltBossPacifierOnMainRoute: false,
      };
    }

    const normalNext = effectiveNextAvailable;
    const normalPointStates = computeMapSlicePointStatesForRoute(
      completedIndices,
      normalNext,
      normal.missionIndices
    );
    const assignedAltPreviewStates = computeRunAltPreviewStates(runAlt);

    return {
      points: normal.points,
      missionIndicesPerPoint: normal.missionIndices,
      easyMissionIndices: [],
      pointStatesOverride: normalPointStates,
      alternativeBranch: buildAltBranch(runAlt, runAlt.points, {
        pointStates: assignedAltPreviewStates,
        previewFactions: true,
        active: false,
        locked: altLocked,
        devEditActive: false,
        exitPreviewRouteVariant: resolveExitPreviewVariantForMissionsRemoved(runMissionsRemoved),
        branchStroke:
          runMissionsRemoved === 2
            ? altLocked
              ? 'rgba(140, 140, 150, 0.35)'
              : 'rgba(255, 180, 80, 0.45)'
            : altLocked
              ? 'rgba(140, 140, 150, 0.35)'
              : 'rgba(120, 200, 255, 0.45)',
        easyMissionIndices: filterAltPathEasyMissionIndices(
          exploreSurface,
          alternativePathPlan?.[exploreSurface]?.easyMissionIndices || []
        ),
        exitPreview: altLocked
          ? null
          : buildExitPreview(runAlt.missionIndices, 'alternative'),
        exitPreviewAuthoringPoint: altLocked
          ? null
          : resolveExitPreviewAuthoring(
              resolveExitPreviewVariantForMissionsRemoved(runMissionsRemoved)
            ),
      }),
      additionalAlternativeBranches: null,
      mainRouteMissionClickOptions: forkRouteChoicePending ? { routeBranch: 'normal' } : null,
      mainRouteExitPreview: buildExitPreview(normal.missionIndices, 'normal'),
      mainRouteExitPreviewAuthoringPoint: resolveExitPreviewAuthoring('normal'),
      forkEditMode: false,
      devEditNormal: false,
      devEditAlt1: false,
      devEditAlt2: false,
      showAltBossPacifierOnMainRoute: false,
    };
  }, [
    exploreSurface,
    currentMapPoints,
    mapMissionOffset,
    alternativePathPlan,
    routeChoices,
    exploreUnknownLevel,
    missionProgress,
    devPathEditMode,
    waypointEditMode,
    showExploreUnknownDevWaypointEditor,
    effectiveNextAvailable,
    sequencePhase,
    surfaceRouteBundle,
    forkRouteChoicePending,
    devExitPreviewOverride,
    exploreUnknownLevel,
    t,
  ]);

  const effectivePathSlicePointStates = useMemo(() => {
    const displayPoints = pathOverlayConfig.points ?? currentMapPoints;
    const routeMissionIndices = pathOverlayConfig.missionIndicesPerPoint;
    const completed = missionProgress?.completedIndices || [];

    const computeForDisplayRoute = () => {
      if (!Array.isArray(routeMissionIndices) || routeMissionIndices.length === 0) {
        return null;
      }
      const indices =
        Array.isArray(displayPoints) && routeMissionIndices.length > displayPoints.length
          ? routeMissionIndices.slice(0, displayPoints.length)
          : routeMissionIndices;
      return computeMapSlicePointStatesForRoute(completed, effectiveNextAvailable, indices);
    };

    if (pathOverlayConfig.pointStatesOverride != null) {
      const states = pathOverlayConfig.pointStatesOverride;
      if (Array.isArray(displayPoints) && states.length !== displayPoints.length) {
        return computeForDisplayRoute() ?? displayPoints.map(() => 'unavailable');
      }
      return states;
    }

    if (
      Array.isArray(displayPoints) &&
      Array.isArray(currentMapPoints) &&
      displayPoints.length !== currentMapPoints.length
    ) {
      return computeForDisplayRoute() ?? displayPoints.map(() => 'unavailable');
    }

    return effectiveSlicePointStates;
  }, [
    pathOverlayConfig.pointStatesOverride,
    pathOverlayConfig.points,
    pathOverlayConfig.missionIndicesPerPoint,
    effectiveSlicePointStates,
    currentMapPoints,
    missionProgress,
    effectiveNextAvailable,
  ]);

  const pathDisplayPoints = pathOverlayConfig.points ?? currentMapPoints;

  const nextSelectableMissionIndex = useMemo(() => {
    const idx = missionProgress?.nextAvailable;
    if (idx == null || typeof idx !== 'number' || !Number.isInteger(idx) || idx < 1) {
      return null;
    }
    return idx;
  }, [missionProgress]);

  const coreMissionIntroKey = useMemo(() => {
    if (nextSelectableMissionIndex == null || coreMissionIndices.length === 0) {
      return null;
    }
    const coreFirst = coreMissionIndices[0];
    const localIndex = nextSelectableMissionIndex - coreFirst;
    if (localIndex < 0 || localIndex >= coreMissionIndices.length) {
      return null;
    }
    return `exploreUnknownPage.coreMissionIntro${localIndex + 1}`;
  }, [nextSelectableMissionIndex, coreMissionIndices]);

  const showKinshipBossPrompt = useMemo(() => {
    if (sequencePhase !== 'arrival' || exploreSurface !== 'core') return false;
    if (kinshipMissionIndex == null) return false;
    return kinshipBossQueued || nextSelectableMissionIndex === kinshipMissionIndex;
  }, [
    sequencePhase,
    exploreSurface,
    kinshipMissionIndex,
    kinshipBossQueued,
    nextSelectableMissionIndex,
  ]);

  const pendingSurfaceExitTransition = useMemo(() => {
    if (sequencePhase !== 'arrival' || !missionProgress) return null;
    const ci = missionProgress.completedIndices;
    if (!Array.isArray(ci)) return null;
    return resolvePendingSurfaceExitTransition({
      exploreSurface,
      completedIndices: ci,
      alternativePathPlan,
      routeChoices,
      exploreUnknownLevel,
      alternativePathMinLevel: EXPLORE_UNKNOWN_ALTERNATIVE_PATH_MIN_LEVEL,
    });
  }, [
    sequencePhase,
    missionProgress,
    exploreSurface,
    alternativePathPlan,
    routeChoices,
    exploreUnknownLevel,
  ]);

  const showSurfaceExitTransitionPrompt =
    pendingSurfaceExitTransition != null &&
    pendingSurfaceExitTransition.fromSurface === exploreSurface;

  const ensureValidToken = useCallback(async () => {
    let token = localStorage.getItem('token');
    const refreshTokenValue = localStorage.getItem('refreshToken');

    if (token && refreshTokenValue) {
      if (!isTokenValid(token) || needsTokenRefresh(token)) {
        token = await refreshToken(refreshTokenValue);
        localStorage.setItem('token', token);
      }
    }

    if (!token) {
      throw new Error('No authentication token available');
    }

    return token;
  }, []);

  const handleMinigameDarknessPercentUpdated = useCallback((percent) => {
    setDarknessPercent(parseExploreUnknownDarknessPercent(percent));
  }, []);

  const handleRouteChoice = useCallback(
    async (route) => {
      if (activeRunId == null) {
        throw new Error('Explore Unknown: route choice requires active run');
      }
      setRouteChoiceBusy(true);
      setRouteChoiceError(null);
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/explore-unknown/route-choice`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ runId: activeRunId, surface: exploreSurface, route }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || `Route choice failed (${response.status})`);
        }
        if (data.routeChoices) {
          setRouteChoices(data.routeChoices);
          return data.routeChoices;
        }
        return null;
      } catch (err) {
        setRouteChoiceError(err instanceof Error ? err.message : String(err));
        throw err;
      } finally {
        setRouteChoiceBusy(false);
      }
    },
    [activeRunId, ensureValidToken, exploreSurface]
  );

  const ingestExploreUnknownRunPayload = useCallback((data) => {
    if (data == null || data.runId == null) {
      setActiveRunId(null);
      setMissionsCompletedInRun(0);
      setDarknessPercent(0);
      setDarkKnowledgePartsCollected(0);
      setWeakHeartPartsCollected(0);
      setHeartPartsCollected(0);
      setStrongHeartPartsCollected(0);
      setWeakHeartSetsCompleted(0);
      setHeartSetsCompleted(0);
      setStrongHeartSetsCompleted(0);
      setEpGlobalPartsCollected(0);
      setEpGlobalCharges(0);
      setEpZonePartsCollected(0);
      setEpZoneCharges(0);
      setEpTargetPartsCollected(0);
      setEpTargetCharges(0);
      setRewriteExperiencePartsCollected(0);
      setRewriteExperienceCharges(0);
      setPlayerStartingHealth(30);
      setDarknessBargainM3Used(false);
      setDarknessBargainM6Used(false);
      setDarknessBargainM11Used(false);
      setDarknessBargainM19Used(false);
      setDarknessBargainM25Used(false);
      setDarknessBargainM42Used(false);
      setDarknessBargainM49Used(false);
      setDarknessBargainM56Used(false);
      setDarknessBargainM64Used(false);
      try {
        sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, 'arrival');
      } catch {
        /* ignore */
      }
      setExploreSurface('arrival');
      setAlternativePathPlan({});
      setRouteChoices({});
      requestPendingSeasonEndCheck();
      return;
    }
    setActiveRunId(Number(data.runId));
    if (data.alternativePathPlan && typeof data.alternativePathPlan === 'object') {
      setAlternativePathPlan(data.alternativePathPlan);
    }
    if (data.routeChoices && typeof data.routeChoices === 'object') {
      setRouteChoices(data.routeChoices);
    }
    const missionsCompletedInRunVal =
      data.missionsCompletedInRun != null ? Number(data.missionsCompletedInRun) || 0 : null;
    if (missionsCompletedInRunVal != null) {
      setMissionsCompletedInRun(missionsCompletedInRunVal);
    }
    setDarknessPercent(parseExploreUnknownDarknessPercent(data.darknessPercent));
    setDarkKnowledgePartsCollected(parseExploreUnknownDarkKnowledgePartsCollected(data.darkKnowledgePartsCollected));
    const heartRun = readHeartRunFromPayload(data);
    setWeakHeartPartsCollected(heartRun.weakHeartPartsCollected);
    setHeartPartsCollected(heartRun.heartPartsCollected);
    setStrongHeartPartsCollected(heartRun.strongHeartPartsCollected);
    setWeakHeartSetsCompleted(heartRun.weakHeartSetsCompleted);
    setHeartSetsCompleted(heartRun.heartSetsCompleted);
    setStrongHeartSetsCompleted(heartRun.strongHeartSetsCompleted);
    const epRun = readEpRunFromPayload(data);
    setEpGlobalPartsCollected(epRun.epGlobalPartsCollected);
    setEpGlobalCharges(epRun.epGlobalCharges);
    setEpZonePartsCollected(epRun.epZonePartsCollected);
    setEpZoneCharges(epRun.epZoneCharges);
    setEpTargetPartsCollected(epRun.epTargetPartsCollected);
    setEpTargetCharges(epRun.epTargetCharges);
    const rewriteRun = readRewriteExperienceRunFromPayload(data);
    setRewriteExperiencePartsCollected(rewriteRun.rewriteExperiencePartsCollected);
    setRewriteExperienceCharges(rewriteRun.rewriteExperienceCharges);
    setPlayerStartingHealth(parseExploreUnknownPlayerStartingHealth(data.playerStartingHealth));
    applyExploreUnknownDarknessBargainSetters(data, {
      darknessBargainM3Used: setDarknessBargainM3Used,
      darknessBargainM6Used: setDarknessBargainM6Used,
      darknessBargainM11Used: setDarknessBargainM11Used,
      darknessBargainM19Used: setDarknessBargainM19Used,
      darknessBargainM25Used: setDarknessBargainM25Used,
      darknessBargainM42Used: setDarknessBargainM42Used,
      darknessBargainM49Used: setDarknessBargainM49Used,
      darknessBargainM56Used: setDarknessBargainM56Used,
      darknessBargainM64Used: setDarknessBargainM64Used,
    });

    if (typeof data.currentSurface === 'string' && data.currentSurface) {
      const resolvedSurface = resolveExploreUnknownSurfaceFromRunPayload(data);
      if (resolvedSurface) {
        try {
          sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, resolvedSurface);
        } catch {
          /* ignore */
        }
        setExploreSurface(resolvedSurface);
      }
    } else if (typeof data.initialSurface === 'string' && data.initialSurface) {
      try {
        sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, data.initialSurface);
      } catch {
        /* ignore */
      }
      setExploreSurface(data.initialSurface);
    } else if (missionsCompletedInRunVal === 0) {
      const completed = Array.isArray(data.completedIndices) ? data.completedIndices : [];
      if (completed.length === 0) {
        // Fresh run without optional skip: pin arrival (stale sessionStorage from a prior run).
        try {
          sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, 'arrival');
        } catch {
          /* ignore */
        }
        setExploreSurface('arrival');
      }
    }

    if (Array.isArray(data.completedIndices)) {
      setMissionProgress({
        completedIndices: data.completedIndices,
        nextAvailable: data.nextAvailable === undefined ? null : data.nextAvailable,
      });
    }

    if (data.worldEntryReward && data.worldEntryReward.popupId) {
      setWorldEntryRewardPopup(data.worldEntryReward);
    }
  }, []);

  const maybeShowLevel100DoorToast = useCallback((level) => {
    if (sequencePhaseRef.current !== null) return;
    if (Number(level) < 100) return;
    try {
      if (localStorage.getItem(EU_LEVEL100_DOOR_UNLOCKED_TOAST_KEY) === '1') return;
    } catch {
      return;
    }
    setShowLevel100DoorToast(true);
  }, []);

  const ingestLevelingFromProgress = useCallback((data) => {
    if (data.exploreUnknownLevel != null) {
      const lv = Number(data.exploreUnknownLevel);
      setExploreUnknownLevel(lv);
      maybeShowLevel100DoorToast(lv);
    }
    if (data.exploreUnknownTotalXp != null) {
      setExploreUnknownTotalXp(Number(data.exploreUnknownTotalXp));
    }
    if (data.exploreUnknownXpIntoLevel != null) {
      setExploreUnknownXpIntoLevel(Number(data.exploreUnknownXpIntoLevel));
    }
    if (data.exploreUnknownXpToNext != null) {
      setExploreUnknownXpToNext(Number(data.exploreUnknownXpToNext));
    }
    if (data.exploreUnknownMilestoneBonuses) {
      setExploreUnknownMilestoneBonuses(data.exploreUnknownMilestoneBonuses);
    }
    if (data.exploreUnknownLevelDefinitions) {
      setExploreUnknownLevelDefinitions(data.exploreUnknownLevelDefinitions);
    }
    if (data.exploreUnknownBonusSelections) {
      setExploreUnknownBonusSelections(data.exploreUnknownBonusSelections);
    }
    if (data.exploreUnknownEndgameBonusId !== undefined) {
      setExploreUnknownEndgameBonusId(data.exploreUnknownEndgameBonusId);
    }
    if (data.exploreUnknownSkippingBonuses) {
      setExploreUnknownSkippingBonuses(data.exploreUnknownSkippingBonuses);
    }
    if (data.exploreUnknownResolvedBonuses) {
      setExploreUnknownResolvedBonuses(data.exploreUnknownResolvedBonuses);
    }
    if (data.exploreUnknownRefinedErrayCount != null) {
      setExploreUnknownRefinedErrayCount(Number(data.exploreUnknownRefinedErrayCount) || 0);
    } else if (data.exploreUnknownRefinedErray != null) {
      setExploreUnknownRefinedErrayCount(data.exploreUnknownRefinedErray === true ? 1 : 0);
    }
    if (data.exploreUnknownRefinedErraySpare != null) {
      setExploreUnknownRefinedErraySpare(Number(data.exploreUnknownRefinedErraySpare) || 0);
    }
    if (data.exploreUnknownRefinedErrayAttachments) {
      setExploreUnknownRefinedErrayAttachments(data.exploreUnknownRefinedErrayAttachments);
    }
    if (data.exploreUnknownRefinedErrayAttachedBonusId !== undefined) {
      setExploreUnknownRefinedErrayAttachedBonusId(data.exploreUnknownRefinedErrayAttachedBonusId);
    }
    if (data.refinedErrayCount != null) {
      setExploreUnknownRefinedErrayCount(Number(data.refinedErrayCount) || 0);
    }
    if (data.refinedErraySpare != null) {
      setExploreUnknownRefinedErraySpare(Number(data.refinedErraySpare) || 0);
    }
    if (data.refinedErrayAttachments) {
      setExploreUnknownRefinedErrayAttachments(data.refinedErrayAttachments);
    }
    if (data.refinedErrayAttachedBonusId !== undefined) {
      setExploreUnknownRefinedErrayAttachedBonusId(data.refinedErrayAttachedBonusId);
    }
    if (Array.isArray(data.exploreUnknownPostcardMissionIndices)) {
      setExploreUnknownPostcardMissionIndices(data.exploreUnknownPostcardMissionIndices);
    }
  }, [maybeShowLevel100DoorToast]);

  const syncMissionProgressFromApi = useCallback(async () => {
    const token = await ensureValidToken();
    const response = await fetch(`${API_URL}/explore-unknown/progress`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || `Progress failed (${response.status})`);
    }
    if (!Array.isArray(data.completedIndices)) {
      throw new Error('Invalid explore-unknown progress response');
    }
    if (!Array.isArray(data.exploreUnknownPostcardMissionIndices)) {
      throw new Error('Invalid explore-unknown progress response: exploreUnknownPostcardMissionIndices');
    }
    setMissionProgress({
      completedIndices: data.completedIndices,
      nextAvailable: data.nextAvailable === undefined ? null : data.nextAvailable,
    });
    setExploreUnknownPostcardMissionIndices(data.exploreUnknownPostcardMissionIndices);
    setMissionProgressError(null);
    ingestLevelingFromProgress(data);
  }, [ensureValidToken, ingestLevelingFromProgress]);

  const reactiveDoctrineMetadata = exploreUnknownBonusSelections?.reactiveDoctrineMetadata || null;

  const persistBonusSelections = useCallback(
    async (selections, endgameId, reactiveMetadata = undefined) => {
      const token = await ensureValidToken();
      const payload = {
        bonusSelections: selections,
        endgameBonusId: endgameId,
      };
      if (reactiveMetadata !== undefined) {
        payload.reactiveDoctrineMetadata = reactiveMetadata;
      } else if (selections?.reactiveDoctrineMetadata) {
        payload.reactiveDoctrineMetadata = selections.reactiveDoctrineMetadata;
      }
      const response = await fetch(`${API_URL}/explore-unknown/bonus-selections`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || 'Failed to save bonus selections');
      }
      const data = await response.json();
      ingestLevelingFromProgress({
        exploreUnknownBonusSelections: data.bonusSelections,
        exploreUnknownEndgameBonusId: data.endgameBonusId,
      });
    },
    [ensureValidToken, ingestLevelingFromProgress]
  );

  const persistSkippingBonuses = useCallback(
    async (toggles) => {
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/explore-unknown/skipping-bonuses`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ toggles }),
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || 'Failed to save skipping bonuses');
      }
      const data = await response.json();
      ingestLevelingFromProgress({ exploreUnknownSkippingBonuses: data.skippingBonuses });
    },
    [ensureValidToken, ingestLevelingFromProgress]
  );

  const handleAttachRefinedErray = useCallback(
    async (bonusId) => {
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/explore-unknown/attach-refined-erray`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ bonusId }),
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || 'Attach failed');
      }
      const data = await response.json();
      ingestLevelingFromProgress({
        exploreUnknownRefinedErrayCount: data.refinedErrayCount,
        exploreUnknownRefinedErraySpare: data.refinedErraySpare,
        exploreUnknownRefinedErrayAttachments: data.refinedErrayAttachments,
        exploreUnknownRefinedErrayAttachedBonusId: data.refinedErrayAttachedBonusId,
      });
    },
    [ensureValidToken, ingestLevelingFromProgress]
  );

  const syncExploreUnknownRunAfterMinigame = useCallback(
    async ({ winReward, winRewards } = {}) => {
      if (activeRunId == null) {
        return;
      }
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/explore-unknown/active-run`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || `active-run sync failed (${response.status})`);
      }
      const data = await response.json();
      if (data != null && data.runId != null) {
        ingestExploreUnknownRunPayload(data);
      }
      const xpRewardIds = new Set([
        'extraXp300',
        'extraXp600',
        'extraXp1200',
        'extraXp2000',
        'extraXp4000',
        'extraXp5000',
        'extraXp4500',
        'extraXp6000',
        'extraXp5500',
      ]);
      const needsXpSync =
        (winReward && xpRewardIds.has(winReward.id)) ||
        (Array.isArray(winRewards) && winRewards.some((r) => r && xpRewardIds.has(r.id)));
      if (needsXpSync) {
        const progressResponse = await fetch(`${API_URL}/explore-unknown/progress`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const progressData = await progressResponse.json().catch(() => ({}));
        if (!progressResponse.ok) {
          throw new Error(progressData.error || `Progress sync failed (${progressResponse.status})`);
        }
        ingestLevelingFromProgress(progressData);
      }
    },
    [activeRunId, ensureValidToken, ingestExploreUnknownRunPayload, ingestLevelingFromProgress]
  );

  const {
    resolveError: minigameResolveError,
    showMinigameMarker,
    minigameMarkerPoint,
    minigameMarkerInteractive,
    minigameId: activeMinigameId,
    minigameChallenge,
    minigameModalOpen,
    minigameCompleteBusy,
    minigameCompleteError,
    minigameCompleteOutcome,
    minigameCompleteWinReward,
    minigameCompleteWinRewards,
    minigameCompleteGambleResolution,
    handleMinigameMarkerActivate,
    handleMinigameModalDismiss,
    handleMinigameComplete,
    handleDevStartBirdMinigameTest,
    devMinigameTestBusy,
    devMinigameTestError,
  } = useExploreUnknownMinigameSurface({
    activeRunId,
    exploreSurface,
    sequencePhase,
    nextAvailableMissionIndex: nextSelectableMissionIndex,
    ensureValidToken,
    onDarknessPercentUpdated: handleMinigameDarknessPercentUpdated,
    onRunStateSyncedAfterComplete: syncExploreUnknownRunAfterMinigame,
  });

  const applyContinueSurfaceTransition = useCallback(async (nextSurface) => {
    if (activeRunId == null) {
      throw new Error('Explore Unknown: activeRunId is required for continue-surface transition');
    }
    setContinueSurfaceBusy(true);
    setContinueSurfaceError(null);
    try {
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/explore-unknown/continue-surface`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ runId: activeRunId, targetSurface: nextSurface }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Continue surface failed (${response.status})`);
      }
      if (data.runEndedByDarkness === true) {
        const endedRunId = Number(data.runId ?? activeRunId);
        if (!Number.isInteger(endedRunId) || endedRunId < 1) {
          throw new Error(
            `Explore Unknown continue-surface eclipse: invalid runId (${String(data.runId)})`
          );
        }
        let endedMissionIndex = null;
        if (
          missionProgress?.nextAvailable != null &&
          Number.isInteger(missionProgress.nextAvailable) &&
          missionProgress.nextAvailable > 1
        ) {
          endedMissionIndex = missionProgress.nextAvailable - 1;
        } else if (
          Array.isArray(missionProgress?.completedIndices) &&
          missionProgress.completedIndices.length > 0
        ) {
          endedMissionIndex = Math.max(...missionProgress.completedIndices);
        }
        if (!Number.isInteger(endedMissionIndex) || endedMissionIndex < 1) {
          throw new Error('Explore Unknown continue-surface eclipse: could not resolve endedMissionIndex');
        }
        ingestExploreUnknownRunPayload(null);
        setRunHydratedFromApi(true);
        try {
          sessionStorage.removeItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
        } catch {
          /* ignore */
        }
        const lossAudioVolume = getEffectiveSoundOutputVolume(1);
        if (lossAudioVolume != null) {
          const lossAudio = new Audio('/sounds/loss.mp3');
          lossAudio.volume = lossAudioVolume;
          lossAudio.play().catch(() => {});
        }
        navigate('/after-battle', {
          replace: true,
          state: {
            result: 'loss',
            exploreUnknownRun: true,
            gameState: {
              exploreUnknownRunId: endedRunId,
              exploreUnknownMissionIndex: endedMissionIndex,
              exploreUnknownDarknessLoss: true,
              exploreUnknownRunDarknessPercent: data.darknessPercent ?? 100,
            },
            isFriendlyMatch: false,
            isTournamentMatch: false,
          },
        });
        return;
      }
      ingestExploreUnknownRunPayload(data);
      playSoundEffect('/sounds/click.mp3');
      try {
        sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, nextSurface);
      } catch {
        /* ignore */
      }
      setExploreSurface(nextSurface);
      setMapRefreshAnimating(true);
      if (mapAnimTimerRef.current) window.clearTimeout(mapAnimTimerRef.current);
      mapAnimTimerRef.current = window.setTimeout(() => {
        setMapRefreshAnimating(false);
        mapAnimTimerRef.current = null;
      }, 1400);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setContinueSurfaceError(msg);
      throw err;
    } finally {
      setContinueSurfaceBusy(false);
    }
  }, [
    activeRunId,
    ensureValidToken,
    ingestExploreUnknownRunPayload,
    missionProgress,
    navigate,
  ]);

  const applyNormalRouteSkipSurface = useCallback(
    async (fromSurface, targetSurface) => {
      if (activeRunId == null) {
        throw new Error('Explore Unknown: active run is required for normal-route world skip');
      }
      setContinueSurfaceBusy(true);
      setContinueSurfaceError(null);
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/explore-unknown/skip-surface`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            runId: activeRunId,
            fromSurface,
            targetSurface,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || `Skip surface failed (${response.status})`);
        }
        ingestExploreUnknownRunPayload(data);
        if (Array.isArray(data.completedIndices)) {
          setMissionProgress({
            completedIndices: data.completedIndices,
            nextAvailable: data.nextAvailable === undefined ? null : data.nextAvailable,
          });
        }
        const resumeSurface = data.skipResumeTargetSurface || data.targetSurface;
        playSoundEffect('/sounds/click.mp3');
        try {
          sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, resumeSurface);
        } catch {
          /* ignore */
        }
        setExploreSurface(resumeSurface);
        setMapRefreshAnimating(true);
        if (mapAnimTimerRef.current) window.clearTimeout(mapAnimTimerRef.current);
        mapAnimTimerRef.current = window.setTimeout(() => {
          setMapRefreshAnimating(false);
          mapAnimTimerRef.current = null;
        }, 1400);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setContinueSurfaceError(msg);
        throw err;
      } finally {
        setContinueSurfaceBusy(false);
      }
    },
    [activeRunId, ensureValidToken, ingestExploreUnknownRunPayload]
  );

  const handleSurfaceExitContinue = useCallback(async () => {
    if (continueSurfaceBusy || pendingSurfaceExitTransition == null) return;
    if (activeRunId == null) {
      setContinueSurfaceError('Explore Unknown: active run is required to continue to the next world');
      return;
    }
    try {
      if (pendingSurfaceExitTransition.kind === 'normalSkip') {
        await applyNormalRouteSkipSurface(
          pendingSurfaceExitTransition.fromSurface,
          pendingSurfaceExitTransition.targetSurface
        );
      } else {
        await applyContinueSurfaceTransition(pendingSurfaceExitTransition.targetSurface);
      }
    } catch {
      /* handlers set continueSurfaceError */
    }
  }, [
    continueSurfaceBusy,
    pendingSurfaceExitTransition,
    activeRunId,
    applyNormalRouteSkipSurface,
    applyContinueSurfaceTransition,
  ]);

  const handleExploreEndGoHome = useCallback(async () => {
    if (exploreEndGoHomeBusy) return;
    playSoundEffect('/sounds/click.mp3');
    setExploreEndGoHomeError(null);
    if (activeRunId == null) {
      try {
        sessionStorage.removeItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      navigate('/welcome');
      return;
    }
    setExploreEndGoHomeBusy(true);
    try {
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/explore-unknown/complete-explore-end-run`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ runId: activeRunId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Complete explore end run failed (${response.status})`);
      }
      try {
        sessionStorage.removeItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      const endedMissionIndex =
        data.endedMissionIndex != null ? Number(data.endedMissionIndex) : kinshipMissionIndex;
      if (!Number.isInteger(endedMissionIndex) || endedMissionIndex < 1) {
        throw new Error(
          `Explore Unknown go home: invalid endedMissionIndex (${String(data.endedMissionIndex)})`
        );
      }
      navigate('/after-battle', {
        replace: true,
        state: {
          result: 'win',
          exploreUnknownRun: true,
          exploreUnknownRunHomecoming: true,
          gameState: {
            exploreUnknownRunId: data.endedRunId != null ? Number(data.endedRunId) : activeRunId,
            exploreUnknownMissionIndex: endedMissionIndex,
          },
          isFriendlyMatch: false,
          isTournamentMatch: false,
        },
      });
    } catch (err) {
      console.error('Explore Unknown go home:', err);
      setExploreEndGoHomeError(err instanceof Error ? err.message : String(err));
    } finally {
      setExploreEndGoHomeBusy(false);
    }
  }, [activeRunId, ensureValidToken, exploreEndGoHomeBusy, navigate, kinshipMissionIndex]);

  const ensureActiveRunForNegaraDoor = useCallback(async () => {
    if (activeRunId != null && !Number.isNaN(Number(activeRunId))) {
      return Number(activeRunId);
    }
    const token = await ensureValidToken();
    const response = await fetch(`${API_URL}/explore-unknown/active-run`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || `active-run failed (${response.status})`);
    }
    if (data == null || data.runId == null) {
      throw new Error(t('exploreUnknownPage.exploreEndDoorNeedsActiveRun'));
    }
    const rid = Number(data.runId);
    setActiveRunId(rid);
    const sid = String(data.strategyId);
    writeExploreUnknownStoredStrategyId(sid);
    setCommittedStrategyId(sid);
    setSelectedStrategyId(sid);
    ingestExploreUnknownRunPayload(data);
    setRunHydratedFromApi(true);
    return rid;
  }, [activeRunId, ensureValidToken, ingestExploreUnknownRunPayload, t]);

  const firstMissionIndexForOverlay = useMemo(() => {
    if (exploreSurface === 'core') {
      return (
        desertFirstMissionIndex +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount +
        boatMapSliceMissionCount +
        pixelPastMapSliceMissionCount +
        kendrexMapSliceMissionCount +
        autobahnMapSliceMissionCount +
        productionHellMapSliceMissionCount +
        awakeningMapSliceMissionCount
      );
    }
    if (exploreSurface === 'awakening') {
      return (
        desertFirstMissionIndex +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount +
        boatMapSliceMissionCount +
        pixelPastMapSliceMissionCount +
        kendrexMapSliceMissionCount +
        autobahnMapSliceMissionCount +
        productionHellMapSliceMissionCount
      );
    }
    if (exploreSurface === 'productionhell') {
      return (
        desertFirstMissionIndex +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount +
        boatMapSliceMissionCount +
        pixelPastMapSliceMissionCount +
        kendrexMapSliceMissionCount +
        autobahnMapSliceMissionCount
      );
    }
    if (exploreSurface === 'autobahn') {
      return (
        desertFirstMissionIndex +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount +
        boatMapSliceMissionCount +
        pixelPastMapSliceMissionCount +
        kendrexMapSliceMissionCount
      );
    }
    if (exploreSurface === 'kendrex') {
      return (
        desertFirstMissionIndex +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount +
        boatMapSliceMissionCount +
        pixelPastMapSliceMissionCount
      );
    }
    if (exploreSurface === 'pixelpast') {
      return (
        desertFirstMissionIndex +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount +
        boatMapSliceMissionCount
      );
    }
    if (exploreSurface === 'boat') {
      return (
        desertFirstMissionIndex +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount
      );
    }
    if (exploreSurface === 'treetop') {
      return (
        desertFirstMissionIndex +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount
      );
    }
    if (exploreSurface === 'lavender') {
      return desertFirstMissionIndex + desertMapSliceMissionCount + fairMapSliceMissionCount;
    }
    if (exploreSurface === 'fair') {
      return desertFirstMissionIndex + desertMapSliceMissionCount;
    }
    if (exploreSurface === 'desert') return desertFirstMissionIndex;
    if (exploreSurface === 'cave') return mapSliceMissionCount + 1;
    return 1;
  }, [
    exploreSurface,
    desertFirstMissionIndex,
    desertMapSliceMissionCount,
    fairMapSliceMissionCount,
    lavenderMapSliceMissionCount,
    treetopMapSliceMissionCount,
    boatMapSliceMissionCount,
    pixelPastMapSliceMissionCount,
    kendrexMapSliceMissionCount,
    mapSliceMissionCount,
    autobahnMapSliceMissionCount,
    productionHellMapSliceMissionCount,
    awakeningMapSliceMissionCount,
  ]);

  const darknessBargainTierOnSurface = SURFACE_TO_DARKNESS_BARGAIN_TIER[exploreSurface];

  const darknessBargainMilestoneMission = useMemo(() => {
    switch (darknessBargainTierOnSurface) {
      case 'm3':
        return 3;
      case 'm6':
        return 7;
      case 'm11':
        return 14;
      case 'm19':
        return 19;
      case 'm25':
        return 25;
      case 'm42':
        return 42;
      case 'm49':
        return 49;
      case 'm56':
        return 56;
      case 'm64':
        return 64;
      default:
        return null;
    }
  }, [darknessBargainTierOnSurface]);

  const isDarknessBargainUsedOnSurface = useMemo(() => {
    switch (darknessBargainTierOnSurface) {
      case 'm3':
        return darknessBargainM3Used;
      case 'm6':
        return darknessBargainM6Used;
      case 'm11':
        return darknessBargainM11Used;
      case 'm19':
        return darknessBargainM19Used;
      case 'm25':
        return darknessBargainM25Used;
      case 'm42':
        return darknessBargainM42Used;
      case 'm49':
        return darknessBargainM49Used;
      case 'm56':
        return darknessBargainM56Used;
      case 'm64':
        return darknessBargainM64Used;
      default:
        return true;
    }
  }, [
    darknessBargainTierOnSurface,
    darknessBargainM3Used,
    darknessBargainM6Used,
    darknessBargainM11Used,
    darknessBargainM19Used,
    darknessBargainM25Used,
    darknessBargainM42Used,
    darknessBargainM49Used,
    darknessBargainM56Used,
    darknessBargainM64Used,
  ]);

  /** Local index (0-based) of the mission that unlocks the Rite on this surface (3→2 on arrival, 7→1 on cave, 14→3 on desert, …). */
  const darknessBargainMissionLocalIndex = useMemo(() => {
    if (darknessBargainMilestoneMission == null) return null;
    const indices = pathOverlayConfig.missionIndicesPerPoint;
    if (Array.isArray(indices) && indices.length > 0) {
      const localIndex = indices.indexOf(darknessBargainMilestoneMission);
      return localIndex >= 0 ? localIndex : null;
    }
    return darknessBargainMilestoneMission - firstMissionIndexForOverlay;
  }, [
    darknessBargainMilestoneMission,
    pathOverlayConfig.missionIndicesPerPoint,
    firstMissionIndexForOverlay,
  ]);

  const darknessBargainAnchorPoints = pathOverlayConfig.points ?? currentMapPoints;

  const showDarknessBargainMarker = useMemo(() => {
    if (activeRunId == null || missionProgress == null || darknessBargainMilestoneMission == null) {
      return false;
    }
    if (isDarknessBargainUsedOnSurface) return false;
    const requiredNextMission = darknessBargainMilestoneMission + 1;
    if (missionProgress.nextAvailable !== requiredNextMission) return false;
    const ci = missionProgress.completedIndices;
    if (!Array.isArray(ci) || !ci.includes(darknessBargainMilestoneMission)) return false;
    const li = darknessBargainMissionLocalIndex;
    if (li == null || !Number.isInteger(li) || li < 0) return false;
    if (!Array.isArray(darknessBargainAnchorPoints) || li >= darknessBargainAnchorPoints.length) return false;
    return true;
  }, [
    activeRunId,
    missionProgress,
    darknessBargainMilestoneMission,
    isDarknessBargainUsedOnSurface,
    darknessBargainMissionLocalIndex,
    darknessBargainAnchorPoints,
  ]);

  const darknessBargainAuthoringPoint = useMemo(() => {
    if (!showDarknessBargainMarker) return null;
    return resolveDarknessBargainAuthoringPointForRoute(
      exploreSurface,
      darknessBargainMilestoneMission,
      pathOverlayConfig.missionIndicesPerPoint,
      darknessBargainAnchorPoints
    );
  }, [
    showDarknessBargainMarker,
    exploreSurface,
    darknessBargainMilestoneMission,
    pathOverlayConfig.missionIndicesPerPoint,
    darknessBargainAnchorPoints,
  ]);

  const devAllRouteDarknessBargainMarkers = useMemo(() => {
    const devDragAllRoutes =
      showExploreUnknownDevWaypointEditor &&
      waypointEditMode &&
      sequencePhase === 'arrival' &&
      isAlternativePathEligibleSurface(exploreSurface);
    if (!devDragAllRoutes || darknessBargainMilestoneMission == null) {
      return [];
    }
    const { normal, alt1, alt2 } = surfaceRouteBundle;
    return [
      {
        routeKey: 'normal',
        point: resolveDarknessBargainAuthoringPointForRoute(
          exploreSurface,
          darknessBargainMilestoneMission,
          normal.missionIndices,
          normal.points
        ),
        stroke: 'rgba(196, 181, 253, 0.95)',
      },
      {
        routeKey: 'altMinus1',
        point: resolveDarknessBargainAuthoringPointForRoute(
          exploreSurface,
          darknessBargainMilestoneMission,
          alt1.missionIndices,
          alt1.points
        ),
        stroke: 'rgba(120, 200, 255, 0.85)',
      },
      {
        routeKey: 'altMinus2',
        point: resolveDarknessBargainAuthoringPointForRoute(
          exploreSurface,
          darknessBargainMilestoneMission,
          alt2.missionIndices,
          alt2.points
        ),
        stroke: 'rgba(255, 180, 80, 0.85)',
      },
    ].filter((marker) => marker.point != null);
  }, [
    showExploreUnknownDevWaypointEditor,
    waypointEditMode,
    sequencePhase,
    exploreSurface,
    darknessBargainMilestoneMission,
    surfaceRouteBundle,
  ]);

  const darknessBargainMarkerInteractive = useMemo(
    () =>
      showDarknessBargainMarker &&
      playerStartingHealth >= EXPLORE_UNKNOWN_BARGAIN_MIN_PRIOR_HEALTH,
    [showDarknessBargainMarker, playerStartingHealth]
  );

  const arrivalMapSrc = EXPLORE_UNKNOWN_PORTAL_LAYOUT.arrivalMapImageSrc ?? '/images/shadowverse_arrival.jpg';
  const caveMapSrc = EXPLORE_UNKNOWN_PORTAL_LAYOUT.caveMapImageSrc ?? '/images/shadowverse_cave.jpg';
  const desertMapSrc = EXPLORE_UNKNOWN_PORTAL_LAYOUT.desertMapImageSrc ?? '/images/shadowverse_desert.jpg';
  const fairMapSrc = EXPLORE_UNKNOWN_PORTAL_LAYOUT.fairMapImageSrc ?? '/images/shadowverse_fair.jpg';
  const lavenderMapSrc = EXPLORE_UNKNOWN_PORTAL_LAYOUT.lavenderMapImageSrc ?? '/images/shadowverse_lavender.jpg';
  const treetopMapSrc = EXPLORE_UNKNOWN_PORTAL_LAYOUT.treetopMapImageSrc ?? '/images/treetop.jpg';
  const boatMapSrc = EXPLORE_UNKNOWN_PORTAL_LAYOUT.boatMapImageSrc ?? '/images/boat.jpg';
  const pixelPastMapSrc =
    EXPLORE_UNKNOWN_PORTAL_LAYOUT.pixelPastMapImageSrc ?? '/images/shadowverse_pixelpast.jpg';
  const kendrexMapSrc =
    EXPLORE_UNKNOWN_PORTAL_LAYOUT.kendrexMapImageSrc ?? '/images/shadowverse_kendrex.jpg';
  const autobahnMapSrc =
    EXPLORE_UNKNOWN_PORTAL_LAYOUT.autobahnMapImageSrc ?? '/images/shadowverse_autobahn.jpg';
  const productionHellMapSrc =
    EXPLORE_UNKNOWN_PORTAL_LAYOUT.productionHellMapImageSrc ?? '/images/shadowverse_productionhell.jpg';
  const awakeningMapSrc =
    EXPLORE_UNKNOWN_PORTAL_LAYOUT.awakeningMapImageSrc ?? '/images/shadowverse_awakening.jpg';
  const coreHubBackgroundSrc = useMemo(() => {
    if (exploreSurface !== 'core' || nextSelectableMissionIndex == null) {
      return null;
    }
    const coreFirstMissionIndex = coreMissionIndices[0];
    const coreLastMissionIndex = coreMissionIndices[coreMissionIndices.length - 1];
    const missionIndexForBg =
      nextSelectableMissionIndex >= coreFirstMissionIndex &&
      nextSelectableMissionIndex <= coreLastMissionIndex
        ? nextSelectableMissionIndex
        : coreLastMissionIndex;
    const bgPath = tryGetExploreUnknownBattleBackgroundPath(missionIndexForBg);
    if (!bgPath) {
      throw new Error(
        `Explore Unknown: core surface missing battle background for mission ${missionIndexForBg}`
      );
    }
    return resolveBattleBackgroundCssUrl(bgPath);
  }, [exploreSurface, nextSelectableMissionIndex, coreMissionIndices]);

  const activeMapImageSrc =
    exploreSurface === 'core' && coreHubBackgroundSrc
      ? coreHubBackgroundSrc
      : exploreSurface === 'awakening'
      ? awakeningMapSrc
      : exploreSurface === 'productionhell'
      ? productionHellMapSrc
      : exploreSurface === 'autobahn'
      ? autobahnMapSrc
      : exploreSurface === 'kendrex'
      ? kendrexMapSrc
      : exploreSurface === 'pixelpast'
      ? pixelPastMapSrc
      : exploreSurface === 'boat'
      ? boatMapSrc
      : exploreSurface === 'treetop'
      ? treetopMapSrc
      : exploreSurface === 'lavender'
      ? lavenderMapSrc
      : exploreSurface === 'fair'
        ? fairMapSrc
        : exploreSurface === 'desert'
          ? desertMapSrc
          : exploreSurface === 'cave'
            ? caveMapSrc
            : arrivalMapSrc;

  useEffect(() => {
    setMapImageNaturalSize(null);
  }, [activeMapImageSrc]);

  const exploreMapNameForSurface = useCallback(
    (surface) => {
      const leafKey = EXPLORE_UNKNOWN_MAP_NAME_I18N_KEYS[surface];
      if (!leafKey) {
        throw new Error(`Explore Unknown: unknown surface for map name (${String(surface)})`);
      }
      return t(`exploreUnknownPage.${leafKey}`);
    },
    [t]
  );

  const activeMapDisplayName = useMemo(
    () => exploreMapNameForSurface(exploreSurface),
    [exploreSurface, exploreMapNameForSurface]
  );

  const forkRouteDestinationPreviews = useMemo(() => {
    if (!forkRouteChoicePending || !isAlternativePathEligibleSurface(exploreSurface)) {
      return null;
    }
    const previewOptions = {
      exploreUnknownLevel,
      alternativePathMinLevel: EXPLORE_UNKNOWN_ALTERNATIVE_PATH_MIN_LEVEL,
      routeChoices,
    };
    const normalPreview = resolveRouteDestinationPreview(exploreSurface, 'normal', previewOptions);
    const alternativePreview = resolveRouteDestinationPreview(
      exploreSurface,
      'alternative',
      previewOptions
    );
    const normalDestinationMapName = exploreMapNameForSurface(normalPreview.destinationSurface);
    const alternativeDestinationMapName = exploreMapNameForSurface(
      alternativePreview.destinationSurface
    );
    const normalSkippedMapName =
      normalPreview.kind === 'skip' && normalPreview.skippedSurface
        ? exploreMapNameForSurface(normalPreview.skippedSurface)
        : null;
    return {
      normalPreview,
      alternativePreview,
      normalDestinationMapName,
      normalSkippedMapName,
      alternativeDestinationMapName,
    };
  }, [forkRouteChoicePending, exploreSurface, exploreMapNameForSurface, exploreUnknownLevel, routeChoices]);

  const committedRouteChipLabel = useMemo(() => {
    const route = routeChoices[exploreSurface];
    if (!route || !isAlternativePathEligibleSurface(exploreSurface)) {
      return null;
    }
    const preview = resolveRouteDestinationPreview(exploreSurface, route, {
      exploreUnknownLevel,
      alternativePathMinLevel: EXPLORE_UNKNOWN_ALTERNATIVE_PATH_MIN_LEVEL,
      routeChoices,
    });
    const destinationMap = exploreMapNameForSurface(preview.destinationSurface);
    if (route === 'alternative') {
      return t('exploreUnknownPage.alternativePath.committedRouteAlternative', {
        destinationMap,
      });
    }
    if (preview.kind === 'skip' && preview.skippedSurface) {
      return t('exploreUnknownPage.alternativePath.committedRouteNormalSkip', {
        skippedMap: exploreMapNameForSurface(preview.skippedSurface),
      });
    }
    return t('exploreUnknownPage.alternativePath.committedRouteNormalContinue', {
      destinationMap,
    });
  }, [routeChoices, exploreSurface, exploreMapNameForSurface, exploreUnknownLevel, t]);

  const exploreUnknownXpBarPercentValue = useMemo(
    () => exploreUnknownXpBarPercent(exploreUnknownXpIntoLevel, exploreUnknownXpToNext),
    [exploreUnknownXpIntoLevel, exploreUnknownXpToNext]
  );

  const exploreUnknownTotalXpDisplay = useMemo(
    () => Number(exploreUnknownTotalXp || 0).toLocaleString(),
    [exploreUnknownTotalXp]
  );

  const exploreUnknownAtMaxLevel =
    exploreUnknownLevel >= 100 || Number(exploreUnknownXpToNext) <= 0;

  const handleDarknessBargainMarkerActivate = useCallback(() => {
    if (!darknessBargainMarkerInteractive || darknessBargainTierOnSurface == null) return;
    setDarknessBargainPurchaseError(null);
    setDarknessBargainModalTier(darknessBargainTierOnSurface);
  }, [darknessBargainMarkerInteractive, darknessBargainTierOnSurface]);

  const handleDarknessBargainModalDismiss = useCallback(() => {
    if (darknessBargainPurchaseBusy) return;
    setDarknessBargainModalTier(null);
    setDarknessBargainPurchaseError(null);
  }, [darknessBargainPurchaseBusy]);

  const handleConfirmDarknessBargainPurchase = useCallback(async () => {
    if (darknessBargainModalTier == null || activeRunId == null) return;
    setDarknessBargainPurchaseBusy(true);
    setDarknessBargainPurchaseError(null);
    try {
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/explore-unknown/darkness-bargain`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ runId: activeRunId, tier: darknessBargainModalTier }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Darkness bargain failed (${response.status})`);
      }
      setDarknessPercent(parseExploreUnknownDarknessPercent(data.darknessPercent));
      setPlayerStartingHealth(parseExploreUnknownPlayerStartingHealth(data.playerStartingHealth));
      applyExploreUnknownDarknessBargainSetters(data, {
        darknessBargainM3Used: setDarknessBargainM3Used,
        darknessBargainM6Used: setDarknessBargainM6Used,
        darknessBargainM11Used: setDarknessBargainM11Used,
        darknessBargainM19Used: setDarknessBargainM19Used,
        darknessBargainM25Used: setDarknessBargainM25Used,
        darknessBargainM42Used: setDarknessBargainM42Used,
        darknessBargainM49Used: setDarknessBargainM49Used,
        darknessBargainM56Used: setDarknessBargainM56Used,
        darknessBargainM64Used: setDarknessBargainM64Used,
      });
      setDarkKnowledgePartsCollected(parseExploreUnknownDarkKnowledgePartsCollected(data.darkKnowledgePartsCollected));
      const bargainHeartRun = readHeartRunFromPayload(data);
      setWeakHeartPartsCollected(bargainHeartRun.weakHeartPartsCollected);
      setHeartPartsCollected(bargainHeartRun.heartPartsCollected);
      setStrongHeartPartsCollected(bargainHeartRun.strongHeartPartsCollected);
      setWeakHeartSetsCompleted(bargainHeartRun.weakHeartSetsCompleted);
      setHeartSetsCompleted(bargainHeartRun.heartSetsCompleted);
      setStrongHeartSetsCompleted(bargainHeartRun.strongHeartSetsCompleted);
      const bargainEpRun = readEpRunFromPayload(data);
      setEpGlobalPartsCollected(bargainEpRun.epGlobalPartsCollected);
      setEpGlobalCharges(bargainEpRun.epGlobalCharges);
      setEpZonePartsCollected(bargainEpRun.epZonePartsCollected);
      setEpZoneCharges(bargainEpRun.epZoneCharges);
      setEpTargetPartsCollected(bargainEpRun.epTargetPartsCollected);
      setEpTargetCharges(bargainEpRun.epTargetCharges);
      const bargainRewriteRun = readRewriteExperienceRunFromPayload(data);
      setRewriteExperiencePartsCollected(bargainRewriteRun.rewriteExperiencePartsCollected);
      setRewriteExperienceCharges(bargainRewriteRun.rewriteExperienceCharges);
      setDarknessBargainModalTier(null);
    } catch (err) {
      setDarknessBargainPurchaseError(err instanceof Error ? err.message : String(err));
    } finally {
      setDarknessBargainPurchaseBusy(false);
    }
  }, [darknessBargainModalTier, activeRunId, ensureValidToken]);

  useEffect(() => {
    if (darknessBargainModalTier == null) {
      return undefined;
    }
    if (typeof document !== 'undefined' && document.hidden) {
      return undefined;
    }
    const v = getEffectiveSoundOutputVolume(1);
    if (v === null) {
      return undefined;
    }
    const audio = new Audio('/sounds/rite.mp3');
    audio.loop = true;
    audio.volume = v;
    const p = audio.play();
    if (p !== undefined) {
      p.catch(() => {
        /* Autoplay or decode policy — ignore */
      });
    }
    return () => {
      audio.pause();
      audio.currentTime = 0;
      audio.removeAttribute('src');
      audio.load();
    };
  }, [darknessBargainModalTier]);

  useEffect(() => {
    if (sequencePhase !== 'arrival' || nextSelectableMissionIndex == null) {
      setNextMissionCompletionRates(null);
      setNextMissionCompletionRatesError(null);
      setNextMissionCompletionRatesLoading(false);
      return undefined;
    }
    let cancelled = false;
    const missionIndex = nextSelectableMissionIndex;
    (async () => {
      setNextMissionCompletionRatesLoading(true);
      setNextMissionCompletionRatesError(null);
      setNextMissionCompletionRates(null);
      try {
        const token = await ensureValidToken();
        if (!token) {
          throw new Error('Explore Unknown mission completion rates: authentication token is required');
        }
        const response = await fetch(
          `${API_URL}/explore-unknown/mission-win-rates?indices=${encodeURIComponent(String(missionIndex))}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || `Failed to load mission completion rates (${response.status})`);
        }
        if (cancelled) return;
        const entry = data.missions?.[String(missionIndex)];
        if (!entry || typeof entry !== 'object') {
          throw new Error(
            `Explore Unknown mission completion rates: missing entry for mission ${missionIndex}`
          );
        }
        setNextMissionCompletionRates(entry);
      } catch (err) {
        if (!cancelled) {
          setNextMissionCompletionRatesError(
            err instanceof Error ? err.message : t('exploreUnknownPage.missionCompletionRatesLoadError')
          );
        }
      } finally {
        if (!cancelled) {
          setNextMissionCompletionRatesLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sequencePhase, nextSelectableMissionIndex, ensureValidToken, t]);

  useLayoutEffect(() => {
    const st = location.state;
    if (!st?.resumeArrival || resumeHandledRef.current) return;
    resumeHandledRef.current = true;
    const sid = st.exploreUnknownCommittedStrategyId;
    if (sid == null || sid === '') {
      throw new Error('Explore Unknown: resumeArrival requires exploreUnknownCommittedStrategyId');
    }
    const resumeStrategyId = String(sid);
    writeExploreUnknownStoredStrategyId(resumeStrategyId);
    setCommittedStrategyId(resumeStrategyId);
    setSelectedStrategyId(resumeStrategyId);
    if (st.exploreUnknownRunId != null && st.exploreUnknownRunId !== '') {
      const resumeRunId = Number(st.exploreUnknownRunId);
      pendingResumeRunIdRef.current = resumeRunId;
      setActiveRunId(resumeRunId);
      // After a battle win, map-refresh sync uses GET active-run (full run row). Skip POST
      // run-session here so a stale server build cannot omit new bargain tier fields.
      if (st.exploreUnknownMapRefresh) {
        setRunHydratedFromApi(true);
      }
    }
    setSequencePhase('arrival');
    setPendingActiveRunCheck(false);
    const returnToExploreEnd =
      st.exploreUnknownExploreEnd === true || st.exploreUnknownReturnToExploreEnd === true;
    if (st.exploreUnknownMapRefresh) {
      setMapRefreshAnimating(true);
      syncMissionProgressFromApi()
        .then(() => {
          if (returnToExploreEnd) {
            setSequencePhase('explore_end');
          }
        })
        .catch((err) => {
          console.error('Explore Unknown progress sync after battle:', err);
        });
      if (mapAnimTimerRef.current) window.clearTimeout(mapAnimTimerRef.current);
      mapAnimTimerRef.current = window.setTimeout(() => {
        setMapRefreshAnimating(false);
        mapAnimTimerRef.current = null;
      }, 1400);
    }
    if (st.exploreUnknownSkipResumeTargetSurface) {
      const resumeSurface = String(st.exploreUnknownSkipResumeTargetSurface);
      try {
        sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, resumeSurface);
      } catch {
        /* ignore */
      }
      setExploreSurface(resumeSurface);
    } else if (st.exploreUnknownResumeSurface) {
      const resumeSurface = String(st.exploreUnknownResumeSurface);
      try {
        sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, resumeSurface);
      } catch {
        /* ignore */
      }
      setExploreSurface(resumeSurface);
    }
    if (st.exploreUnknownExploreEnd === true || st.exploreUnknownReturnToExploreEnd === true) {
      setSequencePhase('explore_end');
    } else if (st.exploreUnknownChainImmediateBossMissionIndex != null) {
      const chainIdx = Number(st.exploreUnknownChainImmediateBossMissionIndex);
      if (Number.isInteger(chainIdx) && chainIdx === kinshipMissionIndex) {
        setKinshipBossQueued(true);
        try {
          sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, 'core');
        } catch {
          /* ignore */
        }
        setExploreSurface('core');
      }
    }
  }, [location.state, syncMissionProgressFromApi, kinshipMissionIndex]);

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    const load = async () => {
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/explore-unknown/active-run`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: ac.signal,
        });
        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.error || `active-run failed (${response.status})`);
        }
        const data = await response.json();
        if (cancelled) return;
        if (data != null && data.runId != null && data.strategyId != null) {
          const sid = String(data.strategyId);
          writeExploreUnknownStoredStrategyId(sid);
          setCommittedStrategyId(sid);
          setSelectedStrategyId(sid);
          ingestExploreUnknownRunPayload(data);
          setRunHydratedFromApi(true);
          const st = location.state;
          const resumeReturnToExploreEnd =
            st?.exploreUnknownExploreEnd === true || st?.exploreUnknownReturnToExploreEnd === true;
          if (!resumeReturnToExploreEnd && sequencePhaseRef.current !== 'explore_end') {
            setSequencePhase('arrival');
          }
          setRunSessionError(null);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Explore Unknown active-run:', err);
      } finally {
        if (!cancelled) {
          setPendingActiveRunCheck(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [ensureValidToken, ingestExploreUnknownRunPayload]);

  useEffect(() => {
    if (sequencePhase !== 'arrival' || !committedStrategyId) return;
    if (runHydratedFromApi) return;
    const ac = new AbortController();
    let cancelled = false;
    const load = async () => {
      setRunSessionError(null);
      try {
        const token = await ensureValidToken();
        const resumeId = pendingResumeRunIdRef.current;
        pendingResumeRunIdRef.current = null;
        const body =
          resumeId != null && !Number.isNaN(resumeId)
            ? { resumeRunId: resumeId }
            : { strategyId: committedStrategyId };
        const response = await fetch(`${API_URL}/explore-unknown/run-session`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
          signal: ac.signal,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || `Run session failed (${response.status})`);
        }
        if (data.runId == null) {
          throw new Error('Invalid run session response');
        }
        if (!cancelled) {
          ingestExploreUnknownRunPayload(data);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Explore Unknown run session:', err);
        if (!cancelled) {
          setRunSessionError(err.message || 'Run session failed');
        }
      }
    };
    load();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [sequencePhase, committedStrategyId, ensureValidToken, runHydratedFromApi, ingestExploreUnknownRunPayload]);

  useEffect(() => {
    if (mapRefreshAnimating) {
      mapRefreshWasAnimatingRef.current = true;
      return;
    }
    if (!mapRefreshWasAnimatingRef.current || sequencePhase !== 'arrival') {
      return;
    }
    mapRefreshWasAnimatingRef.current = false;
    let cancelled = false;
    const sync = async () => {
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/explore-unknown/active-run`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.error || `active-run sync failed (${response.status})`);
        }
        const data = await response.json();
        if (cancelled) return;
        ingestExploreUnknownRunPayload(data == null || data.runId == null ? null : data);
        await syncMissionProgressFromApi();
      } catch (err) {
        console.error('Explore Unknown active-run sync after map refresh:', err);
        if (!cancelled) {
          setRunSessionError(err instanceof Error ? err.message : String(err));
        }
      }
    };
    sync();
    return () => {
      cancelled = true;
    };
  }, [mapRefreshAnimating, sequencePhase, ensureValidToken, ingestExploreUnknownRunPayload, syncMissionProgressFromApi]);

  useEffect(() => {
    return () => {
      if (mapAnimTimerRef.current) window.clearTimeout(mapAnimTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/my-strategies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(`Failed to load strategies (${response.status})`);
        }
        const data = await response.json();
        if (!Array.isArray(data)) {
          throw new Error('Invalid strategies response');
        }
        const validStrategies = data.filter((strategy) => isValidOrderCount(strategy));
        const strategyIdsForValidation = validStrategies
          .filter((strategy) => strategy.id != null)
          .map((strategy) => String(strategy.id));

        const validationResults = await Promise.allSettled(
          strategyIdsForValidation.map(async (idKey) => {
            try {
              const currentToken = await ensureValidToken();
              const validationRes = await fetchJsonWithTimeout(
                `${API_URL}/strategy/${idKey}/validate`,
                { headers: { Authorization: `Bearer ${currentToken}` } },
                STRATEGY_VALIDATION_TIMEOUT_MS
              );
              const rawBody = await validationRes.json().catch(() => ({}));
              const normalized = normalizeStrategyValidationResponse(validationRes, rawBody);
              return [idKey, normalized];
            } catch (err) {
              return [
                idKey,
                {
                  valid: false,
                  insufficientOrders: [],
                  fetchError: true,
                  errorMessage:
                    err?.name === 'AbortError'
                      ? 'Strategy ownership check timed out. Please try again.'
                      : err instanceof Error
                        ? err.message
                        : String(err),
                },
              ];
            }
          })
        );

        const validationEntries = validationResults.map((result, idx) => {
          if (result.status === 'fulfilled') {
            return result.value;
          }
          const idKey = strategyIdsForValidation[idx];
          return [
            idKey,
            {
              valid: false,
              insufficientOrders: [],
              fetchError: true,
              errorMessage:
                result.reason instanceof Error
                  ? result.reason.message
                  : String(result.reason || 'Failed to validate strategy ownership'),
            },
          ];
        });
        if (!cancelled) {
          setStrategies(validStrategies);
          setStrategyValidations(Object.fromEntries(validationEntries));
          setStrategiesError(null);
        }
      } catch (err) {
        console.error('ExploreUnknown strategies:', err);
        if (!cancelled) {
          setStrategies([]);
          setStrategyValidations({});
          setStrategiesError(err.message || 'Failed to load strategies');
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [ensureValidToken]);

  useEffect(() => {
    if (pendingActiveRunCheck) return;
    if (committedStrategyId != null) return;
    if (strategies.length === 0) return;
    setSelectedStrategyId((prev) => {
      try {
        return resolveExploreUnknownStartScreenStrategyId(prev, strategies);
      } catch (err) {
        console.error('Explore Unknown: failed to restore stored strategy', err);
        return prev;
      }
    });
  }, [pendingActiveRunCheck, committedStrategyId, strategies]);

  useEffect(() => {
    if (pendingActiveRunCheck) return;
    if (!user) return;
    if (!shouldShowTutorial('explore_unknown')) return;
    if (sequencePhase === 'scanner') return;
    const timer = setTimeout(() => setShowTutorialExploreUnknown(true), 600);
    return () => clearTimeout(timer);
  }, [user, shouldShowTutorial, sequencePhase, pendingActiveRunCheck]);

  useEffect(() => {
    if (activeRunId == null) {
      missionProgressRunIdRef.current = null;
      setMissionProgress(null);
      setMissionProgressError(null);
      return undefined;
    }
    if (missionProgressRunIdRef.current !== activeRunId) {
      missionProgressRunIdRef.current = activeRunId;
      setMissionProgress(null);
      setMissionProgressError(null);
    }
    return undefined;
  }, [activeRunId]);

  useEffect(() => {
    const needsLevelingOnly = sequencePhase === null;
    const needsMapProgress = sequencePhase === 'arrival' && mapSliceMissionCount >= 1;
    if (!needsLevelingOnly && !needsMapProgress) {
      return undefined;
    }
    if (needsMapProgress && activeRunId == null) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/explore-unknown/progress`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || `Progress failed (${response.status})`);
        }
        if (cancelled) return;
        ingestLevelingFromProgress(data);
        if (needsMapProgress) {
          if (!data || !Array.isArray(data.completedIndices)) {
            throw new Error('Invalid explore-unknown progress response');
          }
          setMissionProgress({
            completedIndices: data.completedIndices,
            nextAvailable: data.nextAvailable === undefined ? null : data.nextAvailable,
          });
          setMissionProgressError(null);
        }
      } catch (err) {
        console.error('Explore Unknown progress:', err);
        if (!cancelled && needsMapProgress) {
          setMissionProgress(null);
          setMissionProgressError(err.message || 'Failed to load mission progress');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    sequencePhase,
    mapSliceMissionCount,
    ensureValidToken,
    activeRunId,
    missionsCompletedInRun,
    ingestLevelingFromProgress,
  ]);

  useEffect(() => {
    if (sequencePhase !== 'scanner' || !scannerVideoRef.current) return;
    const v = scannerVideoRef.current;
    if (getEffectiveSoundOutputVolume(1) === null) {
      v.muted = true;
    } else {
      v.muted = false;
      v.volume = 0.5;
    }
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    v.play().catch(() => {});
  }, [sequencePhase]);

  useEffect(() => {
    return () => {
      if (strategyHintTimerRef.current !== null) {
        window.clearTimeout(strategyHintTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      try {
        if (exploreWs && !exploreWsPreserveRef.current) exploreWs.close();
      } catch {
        /* ignore */
      }
    };
  }, [exploreWs]);

  const attemptEnterPortal = useCallback(() => {
    if (sequencePhase !== null) return;
    if (hasEnteredPortal) return;

    if (!selectedStrategyId) {
      setStrategyHintVisible(true);
      if (strategyHintTimerRef.current !== null) {
        window.clearTimeout(strategyHintTimerRef.current);
      }
      strategyHintTimerRef.current = window.setTimeout(() => {
        setStrategyHintVisible(false);
        strategyHintTimerRef.current = null;
      }, 3200);
      return;
    }

    const validation = strategyValidations[selectedStrategyId];
    if (!validation || validation.valid !== true) {
      return;
    }

    try {
      sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, optionalSkipInitialSurface);
    } catch {
      /* ignore */
    }
    setExploreSurface(optionalSkipInitialSurface);

    writeExploreUnknownStoredStrategyId(selectedStrategyId);
    setCommittedStrategyId(selectedStrategyId);
    setSequencePhase('scanner');
  }, [
    sequencePhase,
    hasEnteredPortal,
    selectedStrategyId,
    strategyValidations,
    optionalSkipInitialSurface,
  ]);

  const dismissScannerIntro = useCallback(() => {
    const v = scannerVideoRef.current;
    if (v) {
      v.pause();
    }
    setSequencePhase('arrival');
  }, []);

  const handleScannerEnded = () => {
    dismissScannerIntro();
  };

  useEffect(() => {
    if (sequencePhase !== 'scanner') return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      dismissScannerIntro();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [sequencePhase, dismissScannerIntro]);

  const handleScannerError = () => {
    throw new Error('scanner_drone.mp4 failed to load or play');
  };

  const handleMapImageError = () => {
    throw new Error('Explore Unknown map image failed to load');
  };

  const syncMapImageNaturalSize = useCallback((img) => {
    if (!img) {
      return;
    }
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) {
      return;
    }
    setMapImageNaturalSize({ w, h });
  }, []);

  const handleMapImageLoad = useCallback(
    (e) => {
      const img = e.currentTarget;
      if (!img.naturalWidth || !img.naturalHeight) {
        throw new Error('Explore Unknown map image has invalid natural dimensions');
      }
      syncMapImageNaturalSize(img);
    },
    [syncMapImageNaturalSize]
  );

  const handlePortalBackgroundLoad = useCallback((e) => {
    const img = e.currentTarget;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) {
      throw new Error(`${EXPLORE_UNKNOWN_PORTAL_BACKGROUND_PATH} has invalid natural dimensions`);
    }
    setPortalBackgroundNaturalSize({ w, h });
  }, []);

  useEffect(() => {
    let cancelled = false;
    preloadImage(EXPLORE_UNKNOWN_PORTAL_BACKGROUND_PATH)
      .then(() => {
        if (!cancelled) {
          setPortalBackgroundPreloaded(true);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setPortalBackgroundPreloadError(err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {
    if (!portalBackgroundPreloaded || sequencePhase !== null) {
      return;
    }
    const probe = new Image();
    probe.src = resolvePublicAssetUrl(EXPLORE_UNKNOWN_PORTAL_BACKGROUND_PATH);
    if (probe.complete && probe.naturalWidth && probe.naturalHeight) {
      setPortalBackgroundNaturalSize({
        w: probe.naturalWidth,
        h: probe.naturalHeight,
      });
    }
  }, [portalBackgroundPreloaded, sequencePhase]);

  const portalIntroLayout = useMemo(() => {
    if (!portalBackgroundNaturalSize) {
      return null;
    }
    const { w: imageW, h: imageH } = portalBackgroundNaturalSize;
    const { width: authoringW, height: authoringH } = EXPLORE_UNKNOWN_PORTAL_AUTHORING_SIZE;
    const [centerX, centerY] = EXPLORE_UNKNOWN_PORTAL_LAYOUT.portalCenterCanvasPx;
    const center = resolveExploreUnknownAuthoringPointInCoverContainer(
      containerBaseWidth,
      containerBaseHeight,
      imageW,
      imageH,
      authoringW,
      authoringH,
      centerX,
      centerY
    );
    let debugRect = null;
    if (EXPLORE_UNKNOWN_PORTAL_LAYOUT.debugOutline) {
      debugRect = resolveExploreUnknownAuthoringRectInCoverContainer(
        containerBaseWidth,
        containerBaseHeight,
        imageW,
        imageH,
        authoringW,
        authoringH,
        EXPLORE_UNKNOWN_PORTAL_LAYOUT.portalRectCanvasPx
      );
    }
    return { center, debugRect };
  }, [portalBackgroundNaturalSize, containerBaseWidth, containerBaseHeight]);

  useLayoutEffect(() => {
    const img = mapImageRef.current;
    if (img?.complete) {
      syncMapImageNaturalSize(img);
    }
  }, [activeMapImageSrc, syncMapImageNaturalSize]);

  const handleWaypointAuthoringChange = useCallback(
    (index, ax, ay) => {
      if (!isExploreUnknownDevPlayer) {
        throw new Error('Explore Unknown waypoint authoring change is player1 dev-only');
      }
      const axr = Math.round(ax);
      const ayr = Math.round(ay);
      setDevWaypointOverride((prev) => {
        const expectedLen = getExpectedWaypointCountForPath(exploreSurface, devPathEditMode);
        const layoutBase = layoutMapPoints.map((p) => [...p]);
        if (layoutBase.length !== expectedLen) {
          throw new Error(
            `Explore Unknown: layout waypoint count mismatch for ${devPathEditMode} (${layoutBase.length} vs ${expectedLen})`
          );
        }
        const base =
          prev != null && prev.length === expectedLen ? prev.map((p) => [...p]) : layoutBase;
        const next = [...base];
        next[index] = [axr, ayr];
        saveDevWaypoints(exploreSurface, next, devPathEditMode);
        return next;
      });
    },
    [exploreSurface, layoutMapPoints, devPathEditMode, isExploreUnknownDevPlayer]
  );

  const handleResetDevWaypoints = useCallback(() => {
    try {
      clearDevWaypoints(exploreSurface, devPathEditMode);
      if (isExitPreviewDevPathMode(devPathEditMode)) {
        const variant = exitPreviewVariantFromDevPathMode(devPathEditMode);
        setDevExitPreviewOverride((prev) => ({ ...prev, [variant]: null }));
      } else {
        setDevWaypointOverride(null);
      }
      setDevWaypointUiError(null);
    } catch (err) {
      setDevWaypointUiError(err instanceof Error ? err.message : String(err));
    }
  }, [exploreSurface, devPathEditMode]);

  const handleExitPreviewAuthoringChange = useCallback(
    (ax, ay) => {
      if (!isExploreUnknownDevPlayer) {
        throw new Error('Explore Unknown exit-preview authoring change is player1 dev-only');
      }
      const variant = exitPreviewVariantFromDevPathMode(devPathEditMode);
      const axr = Math.round(ax);
      const ayr = Math.round(ay);
      const pathMode = exitPreviewDevPathModeForVariant(variant);
      saveDevWaypoints(exploreSurface, [[axr, ayr]], pathMode);
      setDevExitPreviewOverride((prev) => ({
        ...prev,
        [variant]: [axr, ayr],
      }));
    },
    [exploreSurface, devPathEditMode, isExploreUnknownDevPlayer]
  );

  const handleCopyDevWaypoints = useCallback(async () => {
    if (isExitPreviewDevPathMode(devPathEditMode)) {
      const existing =
        EXPLORE_UNKNOWN_PORTAL_LAYOUT.routeExitPreviewBySurface?.[exploreSurface] ?? {};
      const normalPoint = resolveRouteExitPreviewAuthoringPoint(
        exploreSurface,
        'normal',
        devExitPreviewOverride?.normal
      );
      const altMinus1Point = resolveRouteExitPreviewAuthoringPoint(
        exploreSurface,
        'altMinus1',
        devExitPreviewOverride?.altMinus1
      );
      const altMinus2Point = resolveRouteExitPreviewAuthoringPoint(
        exploreSurface,
        'altMinus2',
        devExitPreviewOverride?.altMinus2
      );
      const nextEntry = { ...existing };
      delete nextEntry.alternative;
      if (normalPoint) {
        nextEntry.normal = normalPoint;
      }
      if (altMinus1Point) {
        nextEntry.altMinus1 = altMinus1Point;
      }
      if (altMinus2Point) {
        nextEntry.altMinus2 = altMinus2Point;
      }
      if (!nextEntry.normal && !nextEntry.altMinus1 && !nextEntry.altMinus2) {
        throw new Error('Explore Unknown: no exit-preview positions to copy');
      }
      const line = `    ${exploreSurface}: ${JSON.stringify(nextEntry)},`;
      if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
        throw new Error('Explore Unknown: clipboard API is not available');
      }
      await navigator.clipboard.writeText(line);
      setDevWaypointUiError(null);
      setWaypointCopyDone(true);
      window.setTimeout(() => setWaypointCopyDone(false), 2000);
      return;
    }
    const surfaceKeyMap = {
      awakening: 'awakeningPointsOfInterest',
      productionhell: 'productionHellPointsOfInterest',
      autobahn: 'autobahnPointsOfInterest',
      kendrex: 'kendrexPointsOfInterest',
      pixelpast: 'pixelPastPointsOfInterest',
      boat: 'boatPointsOfInterest',
      treetop: 'treetopPointsOfInterest',
      lavender: 'lavenderPointsOfInterest',
      fair: 'fairPointsOfInterest',
      desert: 'desertPointsOfInterest',
      cave: 'cavePointsOfInterest',
      arrival: 'arrivalPointsOfInterest',
    };
    const base = exploreSurface;
    let key = surfaceKeyMap[base] || 'arrivalPointsOfInterest';
    if (devPathEditMode === 'altMinus1') {
      key = key.replace('PointsOfInterest', 'AlternativeMinus1PointsOfInterest');
    } else if (devPathEditMode === 'altMinus2') {
      key = key.replace('PointsOfInterest', 'AlternativeMinus2PointsOfInterest');
    }
    const line = `  ${key}: ${JSON.stringify(currentMapPoints)},`;
    if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
      throw new Error('Explore Unknown: clipboard API is not available');
    }
    await navigator.clipboard.writeText(line);
    setDevWaypointUiError(null);
    setWaypointCopyDone(true);
    window.setTimeout(() => setWaypointCopyDone(false), 2000);
  }, [exploreSurface, currentMapPoints, devPathEditMode, devExitPreviewOverride]);

  const devMapExitPreviewDraggingActive =
    showExploreUnknownDevWaypointEditor &&
    waypointEditMode &&
    mapImageNaturalSize != null &&
    mapImageNaturalSize.w > 0 &&
    mapImageNaturalSize.h > 0 &&
    isExitPreviewDevPathMode(devPathEditMode);

  const exitPreviewDevVariant = exitPreviewVariantFromDevPathMode(devPathEditMode);

  const exitPreviewDevContent = useMemo(() => {
    if (!devMapExitPreviewDraggingActive) {
      return null;
    }
    const missionIndices =
      exitPreviewDevVariant === 'altMinus2'
        ? surfaceRouteBundle.alt2.missionIndices
        : exitPreviewDevVariant === 'altMinus1'
          ? surfaceRouteBundle.alt1.missionIndices
          : surfaceRouteBundle.normal.missionIndices;
    return tryBuildSurfaceRouteExitPreview(
      exploreSurface,
      exitPreviewDevVariant === 'normal' ? 'normal' : 'alternative',
      missionIndices,
      exploreMapNameForSurface,
      getExploreUnknownSurfaceFirstMissionPreviewUrl,
      {
        exploreUnknownLevel,
        alternativePathMinLevel: EXPLORE_UNKNOWN_ALTERNATIVE_PATH_MIN_LEVEL,
        routeChoices,
      }
    );
  }, [
    devMapExitPreviewDraggingActive,
    exitPreviewDevVariant,
    surfaceRouteBundle,
    exploreSurface,
    exploreMapNameForSurface,
    exploreUnknownLevel,
    routeChoices,
  ]);

  const exitPreviewDevAuthoringPoint = useMemo(() => {
    if (!devMapExitPreviewDraggingActive) {
      return null;
    }
    return resolveRouteExitPreviewAuthoringPoint(
      exploreSurface,
      exitPreviewDevVariant,
      devExitPreviewOverride?.[exitPreviewDevVariant]
    );
  }, [
    devMapExitPreviewDraggingActive,
    exploreSurface,
    exitPreviewDevVariant,
    devExitPreviewOverride,
  ]);

  const handleSkipNextMission = useCallback(async () => {
    if (activeRunId == null) {
      throw new Error('Explore Unknown skip mission: active run is required');
    }
    setSkipMissionError(null);
    setSkipMissionBusy(true);
    try {
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/explore-unknown/skip-next-mission`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ runId: activeRunId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Skip mission failed (${response.status})`);
      }
      if (!Array.isArray(data.completedIndices)) {
        throw new Error('Invalid skip mission response: completedIndices');
      }
      setMissionProgress({
        completedIndices: data.completedIndices,
        nextAvailable: data.nextAvailable === undefined ? null : data.nextAvailable,
      });
      if (data.runEndedByDarkness === true) {
        ingestExploreUnknownRunPayload(null);
        setRunHydratedFromApi(true);
      } else {
        ingestExploreUnknownRunPayload({
          runId: activeRunId,
          missionsCompletedInRun: data.missionsCompletedInRun,
          darknessPercent: data.darknessPercent,
          playerStartingHealth: data.playerStartingHealth,
          darknessBargainM3Used: data.darknessBargainM3Used,
          darknessBargainM6Used: data.darknessBargainM6Used,
          darknessBargainM11Used: data.darknessBargainM11Used,
          darknessBargainM19Used: data.darknessBargainM19Used,
          darknessBargainM25Used: data.darknessBargainM25Used,
          darknessBargainM42Used: data.darknessBargainM42Used,
          darknessBargainM49Used: data.darknessBargainM49Used,
          darknessBargainM56Used: data.darknessBargainM56Used,
          darknessBargainM64Used: data.darknessBargainM64Used,
          darkKnowledgePartsCollected: data.darkKnowledgePartsCollected,
          weakHeartPartsCollected: data.weakHeartPartsCollected,
          heartPartsCollected: data.heartPartsCollected,
          strongHeartPartsCollected: data.strongHeartPartsCollected,
          weakHeartSetsCompleted: data.weakHeartSetsCompleted,
          heartSetsCompleted: data.heartSetsCompleted,
          strongHeartSetsCompleted: data.strongHeartSetsCompleted,
          epGlobalPartsCollected: data.epGlobalPartsCollected,
          epGlobalCharges: data.epGlobalCharges,
          epZonePartsCollected: data.epZonePartsCollected,
          epZoneCharges: data.epZoneCharges,
          epTargetPartsCollected: data.epTargetPartsCollected,
          epTargetCharges: data.epTargetCharges,
          rewriteExperiencePartsCollected: data.rewriteExperiencePartsCollected,
          rewriteExperienceCharges: data.rewriteExperienceCharges,
        });
      }
      setMissionProgressError(null);
    } catch (err) {
      setSkipMissionError(err.message || String(err));
    } finally {
      setSkipMissionBusy(false);
    }
  }, [activeRunId, ensureValidToken, ingestExploreUnknownRunPayload]);

  const handleDevAutowinNext = useCallback(async () => {
    if (activeRunId == null) {
      throw new Error('Explore Unknown dev autowin: active run is required');
    }
    setDevAutowinError(null);
    setDevAutowinBusy(true);
    try {
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/explore-unknown/dev-autowin-next`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ runId: activeRunId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Dev autowin failed (${response.status})`);
      }
      if (!Array.isArray(data.completedIndices)) {
        throw new Error('Invalid dev autowin response: completedIndices');
      }
      setMissionProgress({
        completedIndices: data.completedIndices,
        nextAvailable: data.nextAvailable === undefined ? null : data.nextAvailable,
      });
      if (data.runEndedByDarkness === true) {
        ingestExploreUnknownRunPayload(null);
        setRunHydratedFromApi(true);
      } else {
        ingestExploreUnknownRunPayload({
          runId: activeRunId,
          missionsCompletedInRun: data.missionsCompletedInRun,
          darknessPercent: data.darknessPercent,
          playerStartingHealth: data.playerStartingHealth,
          darknessBargainM3Used: data.darknessBargainM3Used,
          darknessBargainM6Used: data.darknessBargainM6Used,
          darknessBargainM11Used: data.darknessBargainM11Used,
          darknessBargainM19Used: data.darknessBargainM19Used,
          darknessBargainM25Used: data.darknessBargainM25Used,
          darknessBargainM42Used: data.darknessBargainM42Used,
          darknessBargainM49Used: data.darknessBargainM49Used,
          darknessBargainM56Used: data.darknessBargainM56Used,
          darknessBargainM64Used: data.darknessBargainM64Used,
          darkKnowledgePartsCollected: data.darkKnowledgePartsCollected,
          weakHeartPartsCollected: data.weakHeartPartsCollected,
          heartPartsCollected: data.heartPartsCollected,
          strongHeartPartsCollected: data.strongHeartPartsCollected,
          weakHeartSetsCompleted: data.weakHeartSetsCompleted,
          heartSetsCompleted: data.heartSetsCompleted,
          strongHeartSetsCompleted: data.strongHeartSetsCompleted,
          epGlobalPartsCollected: data.epGlobalPartsCollected,
          epGlobalCharges: data.epGlobalCharges,
          epZonePartsCollected: data.epZonePartsCollected,
          epZoneCharges: data.epZoneCharges,
          epTargetPartsCollected: data.epTargetPartsCollected,
          epTargetCharges: data.epTargetCharges,
          rewriteExperiencePartsCollected: data.rewriteExperiencePartsCollected,
          rewriteExperienceCharges: data.rewriteExperienceCharges,
        });
      }
      setMissionProgressError(null);
      if (Number(data.missionIndex) === 73 && data.runEndedByDarkness !== true) {
        setKinshipBossQueued(true);
        try {
          sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, 'core');
        } catch {
          /* ignore */
        }
        setExploreSurface('core');
      }
      if (
        data.runEndedByDarkness !== true &&
        (data.exploreUnknownExploreEnd === true || data.exploreUnknownReturnToExploreEnd === true)
      ) {
        setSequencePhase('explore_end');
      }
    } catch (err) {
      console.error('Explore Unknown dev autowin:', err);
      setDevAutowinError(err instanceof Error ? err.message : String(err));
    } finally {
      setDevAutowinBusy(false);
    }
  }, [activeRunId, ensureValidToken, ingestExploreUnknownRunPayload]);

  const handleDevTestLevelReward = useCallback(
    async (rewardLevel) => {
      if (rewardLevel > 30 && activeRunId == null) {
        setDevLevelRewardError('Start or resume a run before testing levels 31–34');
        return;
      }
      setDevLevelRewardError(null);
      setDevLevelRewardBusy(rewardLevel);
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/explore-unknown/dev-test-level-reward`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            rewardLevel,
            runId: activeRunId ?? undefined,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || `Dev level ${rewardLevel} test failed (${response.status})`);
        }
        if (data.exploreUnknownLevel != null) {
          setExploreUnknownLevel(Number(data.exploreUnknownLevel));
        }
        if (data.flow === 'map_modal' && data.worldEntryReward?.popupId) {
          setWorldEntryRewardPopup(data.worldEntryReward);
          return;
        }
        if (data.flow === 'after_battle') {
          const uid =
            typeof localStorage !== 'undefined' ? localStorage.getItem('userId') : null;
          const numUid = uid != null ? parseInt(uid, 10) : NaN;
          const strategyId = committedStrategyId;
          if (strategyId == null || strategyId === '') {
            throw new Error('Select a strategy on the map before testing After Battle reward flows');
          }
          const runId = activeRunId ?? data.gameState?.exploreUnknownRunId ?? null;
          const strategyPayload = { strategy: { id: strategyId } };
          const gameState = {
            ...(data.gameState ?? {}),
            exploreUnknownRunId: runId,
            exploreUnknownMissionIndex: data.gameState?.exploreUnknownMissionIndex ?? 1,
            state: {},
          };
          if (uid) gameState.state[uid] = strategyPayload;
          if (!Number.isNaN(numUid)) {
            gameState.state[numUid] = strategyPayload;
            gameState.state[String(numUid)] = strategyPayload;
          }
          navigate('/after-battle', {
            state: {
              result: data.result || 'win',
              exploreUnknownRun: data.exploreUnknownRun === true,
              exploreUnknownXpGrant: data.exploreUnknownXpGrant ?? null,
              rewards: data.rewards,
              gameState,
              strategyId,
            },
          });
          return;
        }
        throw new Error(`Dev level ${rewardLevel} test: unexpected response flow`);
      } catch (err) {
        console.error(`Explore Unknown dev level ${rewardLevel} reward test:`, err);
        setDevLevelRewardError(err instanceof Error ? err.message : String(err));
      } finally {
        setDevLevelRewardBusy(null);
      }
    },
    [activeRunId, committedStrategyId, ensureValidToken, navigate]
  );

  const handleDevMockEuWinRewards = useCallback(() => {
    playSoundEffect('/sounds/click.mp3');
    navigate('/after-battle', {
      state: buildExploreUnknownDevMockWinAfterBattleNavigateState({
        activeRunId,
        committedStrategyId,
      }),
    });
  }, [activeRunId, committedStrategyId, navigate]);

  const handleDevForceRunLoss = useCallback(async () => {
    if (activeRunId == null) {
      throw new Error('Explore Unknown dev force loss: active run is required');
    }
    setDevForceLossError(null);
    setDevForceLossBusy(true);
    try {
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/explore-unknown/dev-force-run-loss`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ runId: activeRunId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Dev force loss failed (${response.status})`);
      }
      ingestExploreUnknownRunPayload(null);
      setRunHydratedFromApi(true);
    } catch (err) {
      console.error('Explore Unknown dev force loss:', err);
      setDevForceLossError(err instanceof Error ? err.message : String(err));
    } finally {
      setDevForceLossBusy(false);
    }
  }, [activeRunId, ensureValidToken, ingestExploreUnknownRunPayload]);

  const handleOpenResignRunDialog = useCallback(() => {
    if (activeRunId == null || resignRunBusy || startingMission) return;
    playSoundEffect('/sounds/click.mp3');
    setResignRunError(null);
    setResignRunDialogFocus('cancel');
    setShowResignRunDialog(true);
  }, [activeRunId, resignRunBusy, startingMission]);

  const handleCancelResignRun = useCallback(() => {
    if (resignRunBusy) return;
    setShowResignRunDialog(false);
  }, [resignRunBusy]);

  const handleConfirmResignRun = useCallback(async () => {
    if (activeRunId == null) {
      setResignRunError(t('exploreUnknownPage.resignRunMissingRun'));
      return;
    }
    setResignRunError(null);
    setResignRunBusy(true);
    try {
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/explore-unknown/resign-run`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ runId: activeRunId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Resign run failed (${response.status})`);
      }
      const endedRunId = Number(data.endedRunId ?? activeRunId);
      const resignedMissionIndex =
        Number(data.resignedMissionIndex) ||
        Number(nextSelectableMissionIndex) ||
        Math.max(1, (Number(missionsCompletedInRun) || 0) + 1);
      setShowResignRunDialog(false);
      try {
        sessionStorage.removeItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      const lossAudioVolume = getEffectiveSoundOutputVolume(1);
      if (lossAudioVolume != null) {
        const lossAudio = new Audio('/sounds/loss.mp3');
        lossAudio.volume = lossAudioVolume;
        lossAudio.play().catch(() => {});
      }
      navigate('/after-battle', {
        replace: true,
        state: {
          result: 'loss',
          exploreUnknownRun: true,
          gameState: {
            exploreUnknownRunId: endedRunId,
            exploreUnknownMissionIndex: resignedMissionIndex,
          },
        },
      });
    } catch (err) {
      console.error('Explore Unknown resign run:', err);
      setResignRunError(err instanceof Error ? err.message : String(err));
    } finally {
      setResignRunBusy(false);
    }
  }, [
    activeRunId,
    ensureValidToken,
    missionsCompletedInRun,
    navigate,
    nextSelectableMissionIndex,
    t,
  ]);

  useEffect(() => {
    if (continueSurfaceBusy) return;

    // New run: `missions_completed` is 0 but progress fetch can still return the *previous* run's
    // completion list for one frame — do not reconcile surfaces (would call continue-surface and
    // add +5% darkness). Ingest + portal already pin arrival for this case.
    if (missionsCompletedInRun === 0 && activeRunId != null) {
      if (mapRefreshAnimating) {
        return;
      }
      const ciEarly = missionProgress?.completedIndices;
      if (!Array.isArray(ciEarly)) {
        return;
      }
      if (ciEarly.length === 0) {
        try {
          sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, 'arrival');
        } catch {
          /* ignore */
        }
        if (exploreSurfaceRef.current !== 'arrival') {
          setExploreSurface('arrival');
        }
        return;
      }
    }

    if (!missionProgress) return;

    const applySurfaceTarget = (nextSurface) => {
      const from = exploreSurfaceRef.current;
      const ci = missionProgress?.completedIndices;
      if (
        Array.isArray(ci) &&
        shouldDeferAutoSurfaceAdvance(from, nextSurface, {
          currentSurface: from,
          completedIndices: ci,
          alternativePathPlan,
          routeChoices,
          exploreUnknownLevel,
        })
      ) {
        return;
      }
      if (from === nextSurface) {
        try {
          sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, nextSurface);
        } catch {
          /* ignore */
        }
        setExploreSurface(nextSurface);
        return;
      }
      if (activeRunId != null && exploreSurfaceChargesDarkness(from, nextSurface)) {
        void applyContinueSurfaceTransition(nextSurface).catch((err) => {
          console.error('Explore Unknown mission-progress surface transition:', err);
        });
        return;
      }
      try {
        sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, nextSurface);
      } catch {
        /* ignore */
      }
      setExploreSurface(nextSurface);
    };

    const { completedIndices, nextAvailable } = missionProgress;
    const caveFloor = mapSliceMissionCount + 1;
    const desertFloor = 2 * mapSliceMissionCount + 1;
    const ci = Array.isArray(completedIndices) ? completedIndices : [];

    const arrivalCompleteForRoute = isSurfaceCompleteForActiveRoute(
      'arrival',
      ci,
      alternativePathPlan,
      routeChoices
    );
    const caveCompleteForRoute = isSurfaceCompleteForActiveRoute(
      'cave',
      ci,
      alternativePathPlan,
      routeChoices
    );
    const preDesertCompleteForRoute = arrivalCompleteForRoute && caveCompleteForRoute;

    const surfaceCompleteForRoute = (surface) =>
      isSurfaceCompleteForActiveRoute(surface, ci, alternativePathPlan, routeChoices);

    let storedSurface = null;
    try {
      storedSurface = sessionStorage.getItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
    } catch {
      /* ignore */
    }

    if (
      storedSurface &&
      EXPLORE_SURFACE_CHAIN.includes(storedSurface) &&
      arePrerequisiteSurfacesCompleteForTarget(
        storedSurface,
        ci,
        alternativePathPlan,
        routeChoices
      )
    ) {
      applySurfaceTarget(storedSurface);
      return;
    }

    if (storedSurface && EXPLORE_SURFACE_CHAIN.includes(storedSurface)) {
      storedSurface = null;
    }

    const desertIdx = getExploreSurfaceChainIndex('desert');
    const onOrPastDesert = Math.max(
      getExploreSurfaceChainIndex(storedSurface),
      getExploreSurfaceChainIndex(exploreSurfaceRef.current)
    ) > desertIdx;
    const desertCompleteForRoute = surfaceCompleteForRoute('desert');

    const coreFirstMissionIndex = coreMissionIndices[0];
    const coreLastMissionIndex = coreMissionIndices[coreMissionIndices.length - 1];
    const hasExploreEndReached =
      (kinshipMissionIndex != null && ci.includes(kinshipMissionIndex)) ||
      (kinshipMissionIndex != null &&
        nextAvailable != null &&
        nextAvailable > kinshipMissionIndex) ||
      ci.includes(EXPLORE_UNKNOWN_NEGARA_DOOR_MISSION_INDEX);

    if (hasExploreEndReached) {
      setSequencePhase('explore_end');
      return;
    }

    const hasCoreHubProgress =
      nextAvailable != null &&
      nextAvailable >= coreFirstMissionIndex &&
      nextAvailable <= coreLastMissionIndex;

    if (
      storedSurface === 'core' &&
      !surfaceCompleteForRoute('awakening') &&
      !hasCoreHubProgress
    ) {
      try {
        sessionStorage.removeItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      storedSurface = null;
    }

    if (hasCoreHubProgress) {
      applySurfaceTarget('core');
      return;
    }

    const hasKinshipBossPending =
      kinshipMissionIndex != null &&
      nextAvailable != null &&
      nextAvailable === kinshipMissionIndex &&
      !ci.includes(kinshipMissionIndex);
    if (hasKinshipBossPending) {
      applySurfaceTarget('core');
      return;
    }

    if (!onOrPastDesert) {
      if (storedSurface === 'desert') {
        const desertAllowed =
          ci.some((idx) => idx >= desertFloor) ||
          (nextAvailable != null && nextAvailable > desertFloor) ||
          (preDesertCompleteForRoute && nextAvailable === desertFloor);
        if (!desertAllowed) {
          try {
            sessionStorage.removeItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
          } catch {
            /* ignore */
          }
          storedSurface = null;
        }
      }

      if (
        !desertCompleteForRoute &&
        (ci.some((idx) => idx >= desertFloor) ||
          (nextAvailable != null && nextAvailable > desertFloor) ||
          (preDesertCompleteForRoute &&
            nextAvailable === desertFloor &&
            (storedSurface === 'desert' || exploreSurfaceRef.current === 'desert')))
      ) {
        applySurfaceTarget('desert');
        return;
      }

      if (
        storedSurface === 'desert' &&
        nextAvailable === desertFloor &&
        preDesertCompleteForRoute
      ) {
        applySurfaceTarget('desert');
        return;
      }
    }

    // Stay on arrival while next mission is exactly caveFloor so the "Enter cave" prompt can show.
    if (ci.some((idx) => idx >= caveFloor) || (nextAvailable != null && nextAvailable > caveFloor)) {
      if (
        !onOrPastDesert &&
        preDesertCompleteForRoute &&
        !desertCompleteForRoute &&
        (storedSurface === 'desert' || exploreSurfaceRef.current === 'desert')
      ) {
        applySurfaceTarget('desert');
        return;
      }
      if (!onOrPastDesert) {
        applySurfaceTarget('cave');
        return;
      }
    }

    const impliesArrivalOnly =
      !ci.some((idx) => idx >= caveFloor) &&
      (nextAvailable == null || nextAvailable <= mapSliceMissionCount + 1);

    // Only sync down to arrival when the UI is still on arrival. If the player already clicked
    // "Enter" (surface is cave) but the next mission is still the cave floor, impliesArrivalOnly
    // stays true — re-applying arrival would undo that transition when this effect re-runs (e.g.
    // after continue-surface clears `continueSurfaceBusy`).
    if (impliesArrivalOnly && exploreSurfaceRef.current === 'arrival') {
      applySurfaceTarget('arrival');
    }
  }, [
    missionProgress,
    mapSliceMissionCount,
    desertMissionIndices,
    fairMissionIndices,
    lavenderMissionIndices,
    treetopMissionIndices,
    boatMissionIndices,
    pixelPastMissionIndices,
    kendrexMissionIndices,
    autobahnMissionIndices,
    productionHellMissionIndices,
    awakeningMissionIndices,
    coreMissionIndices,
    kinshipMissionIndex,
    continueSurfaceBusy,
    activeRunId,
    missionsCompletedInRun,
    mapRefreshAnimating,
    applyContinueSurfaceTransition,
    exploreUnknownLevel,
    routeChoices,
    alternativePathPlan,
  ]);

  const handleAvailableMissionClick = useCallback(
    async (missionIndex, exploreUnknownDevDirectBossMission = false, missionStartOptions = {}) => {
      const startOptions = missionStartOptions ?? {};
      setMissionStartError(null);
      if (!committedStrategyId) {
        throw new Error('Explore Unknown: committed strategy is required to start a mission');
      }
      const validation = strategyValidations[committedStrategyId];
      if (!validation) {
        setMissionStartError(t('battleOverview.ownershipStillCheckingBody'));
        return;
      }
      if (validation.valid !== true) {
        if (validation.fetchError) {
          setMissionStartError(validation.errorMessage || t('battleOverview.ownershipCheckFailedBody'));
          return;
        }
        setMissionStartError(t('battleOverview.cannotStartBattleUnowned'));
        return;
      }
      if (exploreWs) return;
      if (startingMission) return;
      if (activeRunId == null) {
        setMissionStartError(t('exploreUnknownPage.runSessionNotReady'));
        return;
      }

      setStartingMission(true);

      const euBattleBackground = tryGetExploreUnknownBattleBackgroundPath(missionIndex);
      if (euBattleBackground) {
        preloadBattleBackgroundMedia(resolveBattleBackgroundCssUrl(euBattleBackground)).catch((err) => {
          console.error('Explore Unknown: battle background preload failed', err);
        });
      }

      let token = await ensureValidToken();
      const refreshTokenValue = localStorage.getItem('refreshToken');
      if (token && refreshTokenValue && (!isTokenValid(token) || needsTokenRefresh(token))) {
        try {
          token = await refreshToken(refreshTokenValue);
          localStorage.setItem('token', token);
        } catch (err) {
          console.error('Explore Unknown mission: token refresh failed', err);
          setMissionStartError(t('battleOverview.tokenRefreshFailed') || String(err.message || err));
          setStartingMission(false);
          return;
        }
      } else if (!token) {
        setMissionStartError(t('battleOverview.noToken') || 'No authentication token.');
        setStartingMission(false);
        return;
      }

      // Same flow as Battle Overview "Rejoin": WebSocket auth → reconnect → use server gameState.
      // If the active session is this Explore Unknown mission for this run, resume instead of start-bot-game.
      const didResumeInPlaceBattle = await new Promise((resolve) => {
        let settled = false;
        let timeoutId = null;
        const finish = (value) => {
          if (settled) return;
          settled = true;
          if (timeoutId != null) {
            window.clearTimeout(timeoutId);
            timeoutId = null;
          }
          resolve(value);
        };
        const probe = new window.WebSocket(WS_URL);
        timeoutId = window.setTimeout(() => {
          try {
            probe.close();
          } catch {
            /* ignore */
          }
          finish(false);
        }, 12000);
        probe.onopen = () => {
          const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
          const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
          probe.send(JSON.stringify(getWebSocketAuthMessage(token, normalizedLanguage)));
        };
        probe.onmessage = (event) => {
          let data;
          try {
            data = JSON.parse(event.data);
          } catch {
            return;
          }
          if (data.type === 'auth-success') {
            probe.send(
              JSON.stringify({
                type: 'reconnect',
                token,
              })
            );
          }
          if (data.type === 'reconnect-success') {
            const gs = data.gameState;
            const winnerPresent =
              gs &&
              gs.winner != null &&
              gs.winner !== '' &&
              !(typeof gs.winner === 'number' && Number.isNaN(gs.winner));
            const sameExploreUnknownSession =
              gs &&
              gs.exploreUnknownMissionIndex != null &&
              gs.exploreUnknownRunId != null &&
              !winnerPresent &&
              Number(gs.exploreUnknownMissionIndex) === Number(missionIndex) &&
              Number(gs.exploreUnknownRunId) === Number(activeRunId);
            if (sameExploreUnknownSession && data.gameId != null && String(data.gameId).trim() !== '') {
              const selectedGeneral = localStorage.getItem('selectedGeneral') || '';
              const selectedAdvisor = localStorage.getItem('selectedAdvisor') || '';
              exploreWsPreserveRef.current = true;
              stashGameSocket(data.gameId, probe);
              navigate(`/battle/${data.gameId}`, {
                state: {
                  gameId: data.gameId,
                  gameState: data.gameState,
                  yourTurn: data.yourTurn,
                  selectedGeneral,
                  selectedAdvisor,
                  strategyId: committedStrategyId,
                  userId: localStorage.getItem('userId'),
                  token,
                  isBotGame: true,
                  handedOffGameSocket: true,
                },
              });
              finish(true);
            } else {
              try {
                probe.close();
              } catch {
                /* ignore */
              }
              finish(false);
            }
          }
          if (data.type === 'error') {
            try {
              probe.close();
            } catch {
              /* ignore */
            }
            finish(false);
          }
        };
        probe.onerror = () => {
          try {
            probe.close();
          } catch {
            /* ignore */
          }
          finish(false);
        };
      });

      if (didResumeInPlaceBattle) {
        setStartingMission(false);
        return;
      }

      const socket = new window.WebSocket(WS_URL);
      setExploreWs(socket);

      socket.onopen = () => {
        const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        socket.send(JSON.stringify(getWebSocketAuthMessage(token, normalizedLanguage)));
      };

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'auth-success') {
          const startPayload = {
            type: 'start-bot-game',
            strategyId: committedStrategyId,
            exploreUnknownMissionIndex: missionIndex,
            exploreUnknownRunId: activeRunId,
            developerMode: false,
          };
          if (exploreUnknownDevDirectBossMission) {
            startPayload.exploreUnknownDevDirectBossMission = true;
          }
          if (startOptions.skipWorldResumeSurface) {
            startPayload.exploreUnknownSkipWorldResumeSurface = String(
              startOptions.skipWorldResumeSurface
            );
          }
          socket.send(JSON.stringify(startPayload));
        }
        if (data.type === 'match-found') {
          if (data.gameId == null || String(data.gameId).trim() === '') {
            exploreWsPreserveRef.current = false;
            setStartingMission(false);
            setMissionStartError(t('battleOverview.error'));
            return;
          }
          const selectedGeneral = localStorage.getItem('selectedGeneral') || '';
          const selectedAdvisor = localStorage.getItem('selectedAdvisor') || '';
          exploreWsPreserveRef.current = true;
          stashGameSocket(data.gameId, socket);
          setExploreWs(null);
          navigate(`/battle/${data.gameId}`, {
            state: {
              strategyId: committedStrategyId,
              userId: localStorage.getItem('userId'),
              token: localStorage.getItem('token'),
              selectedGeneral,
              selectedAdvisor,
              isBotGame: true,
              gameState: data.gameState,
              yourTurn: data.yourTurn,
              handedOffGameSocket: true,
            },
          });
          setStartingMission(false);
        }
        if (data.type === 'error') {
          exploreWsPreserveRef.current = false;
          setStartingMission(false);
          setMissionStartError(data.message || t('battleOverview.error'));
          try {
            socket.close();
          } catch {
            /* ignore */
          }
          setExploreWs(null);
        }
      };

      socket.onerror = () => {
        exploreWsPreserveRef.current = false;
        setStartingMission(false);
        setMissionStartError(t('battleOverview.websocketError'));
        setExploreWs(null);
      };

      socket.onclose = () => {
        setExploreWs(null);
      };
    },
    [
      committedStrategyId,
      strategyValidations,
      exploreWs,
      startingMission,
      activeRunId,
      ensureValidToken,
      navigate,
      t,
    ]
  );

  const handleMapMissionClick = useCallback(
    async (missionIndex, exploreUnknownDevDirectBossMission = false, missionStartOptions = {}) => {
      const startOptions = missionStartOptions ?? {};
      let choicesForNext = routeChoices;
      if (forkRouteChoicePending && !routeChoiceBusy) {
        const branch = startOptions.routeBranch;
        const committedRoute = routeChoices?.[exploreSurface];
        try {
          if (branch === 'alternative' || branch === 'normal') {
            const updatedChoices = await handleRouteChoice(branch);
            choicesForNext =
              updatedChoices ?? { ...routeChoices, [exploreSurface]: branch };
          } else if (committedRoute === 'alternative' || committedRoute === 'normal') {
            choicesForNext = routeChoices;
          } else {
            setMissionStartError(
              'Choose normal or alternative route at the fork before starting the next mission.'
            );
            return;
          }
        } catch (err) {
          setMissionStartError(err instanceof Error ? err.message : String(err));
          return;
        }
      }
      const completed = missionProgress?.completedIndices;
      if (Array.isArray(completed)) {
        const expectedNext = resolveNextAvailableForRun(
          completed,
          alternativePathPlan,
          choicesForNext
        );
        if (expectedNext != null && Number(missionIndex) !== Number(expectedNext)) {
          setMissionStartError(
            `Explore Unknown mission ${expectedNext} must be completed first`
          );
          return;
        }
      }
      return handleAvailableMissionClick(
        missionIndex,
        exploreUnknownDevDirectBossMission,
        startOptions
      );
    },
    [
      forkRouteChoicePending,
      routeChoiceBusy,
      handleRouteChoice,
      handleAvailableMissionClick,
      routeChoices,
      exploreSurface,
      missionProgress,
      alternativePathPlan,
    ]
  );

  useEffect(() => {
    if (!skipWorldPendingBossStart) return;
    const { missionIndex, bossFightSurface, skipWorldResumeSurface } = skipWorldPendingBossStart;
    setSkipWorldPendingBossStart(null);
    setExploreSurface(bossFightSurface);
    void handleAvailableMissionClick(missionIndex, false, {
      skipWorldResumeSurface,
    }).catch(() => {});
  }, [skipWorldPendingBossStart, handleAvailableMissionClick]);

  const mapAlternativeBranch = useMemo(() => {
    const branch = pathOverlayConfig.alternativeBranch;
    if (!branch) return null;
    if (!branch.devEditActive) return branch;
    return { ...branch, onWaypointAuthoringChange: handleWaypointAuthoringChange };
  }, [pathOverlayConfig.alternativeBranch, handleWaypointAuthoringChange]);

  const mapAdditionalAlternativeBranches = useMemo(() => {
    const branches = pathOverlayConfig.additionalAlternativeBranches;
    if (!Array.isArray(branches) || branches.length === 0) return null;
    return branches.map((branch) =>
      branch.devEditActive
        ? { ...branch, onWaypointAuthoringChange: handleWaypointAuthoringChange }
        : branch
    );
  }, [pathOverlayConfig.additionalAlternativeBranches, handleWaypointAuthoringChange]);

  const devMapWaypointDraggingActive =
    showExploreUnknownMapDevWaypointEditor &&
    waypointEditMode &&
    mapImageNaturalSize != null &&
    mapImageNaturalSize.w > 0 &&
    mapImageNaturalSize.h > 0 &&
    !isExitPreviewDevPathMode(devPathEditMode);

  if (portalBackgroundPreloadError) {
    throw portalBackgroundPreloadError;
  }

  const waitingForPortalBackground = sequencePhase === null && !portalBackgroundPreloaded;

  if (pendingActiveRunCheck || waitingForPortalBackground) {
    return (
      <div className="explore-unknown-viewport-wrapper explore-unknown-bootstrap-loading" aria-busy="true">
        <div className="explore-unknown-bootstrap-loading-inner">{t('exploreUnknownPage.loadingExploreUnknown')}</div>
      </div>
    );
  }

  return (
    <>
    <div className="explore-unknown-viewport-wrapper">
      <div
        className={`explore-unknown-viewport-container${isContainerMobile ? ' mobile' : ''}`}
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className="explore-unknown-container" ref={exploreContainerRef}>
          {sequencePhase === null ? (
            <img
              src={resolvePublicAssetUrl(EXPLORE_UNKNOWN_PORTAL_BACKGROUND_PATH)}
              alt=""
              className="explore-unknown-portal-background"
              aria-hidden
              onLoad={handlePortalBackgroundLoad}
              onError={() => {
                throw new Error(`${EXPLORE_UNKNOWN_PORTAL_BACKGROUND_PATH} failed to load`);
              }}
            />
          ) : null}
          {EXPLORE_UNKNOWN_PORTAL_LAYOUT.showLayoutGrid && sequencePhase === null ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
            />
          ) : null}
          <div className="explore-unknown-page-chrome">
            <FriendsIcon />
            <SettingsMenu
              onLogout={() => {
                onLogout();
                navigate('/');
              }}
            />
            <span className="explore-unknown-page-chrome-username">
              {resolvePlayer1DisplayUsername(user)}
            </span>
          </div>

          <MenuBackButton to="/battleoverview" className="explore-unknown-back-button" />

          {sequencePhase === null ? (
            <>
              <ExploreUnknownLevelTree
                level={exploreUnknownLevel}
                xpIntoLevel={exploreUnknownXpIntoLevel}
                xpToNext={exploreUnknownXpToNext}
                totalXp={exploreUnknownTotalXp}
                milestoneBonuses={exploreUnknownMilestoneBonuses}
                levelDefinitions={exploreUnknownLevelDefinitions}
                bonusSelections={exploreUnknownBonusSelections}
                endgameBonusId={exploreUnknownEndgameBonusId}
                skippingBonuses={exploreUnknownSkippingBonuses}
                refinedErrayCount={exploreUnknownRefinedErrayCount}
                refinedErraySpare={exploreUnknownRefinedErraySpare}
                refinedErrayAttachments={exploreUnknownRefinedErrayAttachments}
                onBonusSelectionChange={(groupId, optionIndex) => {
                  const next = { ...exploreUnknownBonusSelections };
                  if (optionIndex == null) {
                    delete next[groupId];
                  } else {
                    next[groupId] = optionIndex;
                  }
                  void persistBonusSelections(next, exploreUnknownEndgameBonusId).catch(() => {});
                }}
                onBonusSelectionsPatch={(patch) => {
                  const next = { ...exploreUnknownBonusSelections, ...patch };
                  void persistBonusSelections(next, exploreUnknownEndgameBonusId).catch(() => {});
                }}
                onEndgameChange={(id) => {
                  void persistBonusSelections(exploreUnknownBonusSelections, id).catch(() => {});
                }}
                onSkippingToggle={(bonusId, enabled) => {
                  const next = applySkippingBonusToggleCascade(
                    exploreUnknownSkippingBonuses,
                    bonusId,
                    enabled,
                    exploreUnknownLevel,
                    exploreUnknownLevelDefinitions
                  );
                  ingestLevelingFromProgress({ exploreUnknownSkippingBonuses: next });
                  void persistSkippingBonuses({ [bonusId]: enabled }).catch(() => {});
                }}
                onAttachRefinedErray={(bonusId) => {
                  void handleAttachRefinedErray(bonusId).catch(() => {});
                }}
                reactiveDoctrineMetadata={reactiveDoctrineMetadata}
                onReactiveDoctrineConfigure={() => setShowReactiveDoctrineModal(true)}
                isMobile={isContainerMobile}
              />
              <EmergencyProtocolModal
                isOpen={showReactiveDoctrineModal}
                onClose={() => setShowReactiveDoctrineModal(false)}
                initialData={reactiveDoctrineMetadata}
                onSave={(metadata) => {
                  const nextSelections = {
                    ...exploreUnknownBonusSelections,
                    reactiveDoctrineMetadata: metadata,
                  };
                  setExploreUnknownBonusSelections(nextSelections);
                  setShowReactiveDoctrineModal(false);
                  void persistBonusSelections(
                    nextSelections,
                    exploreUnknownEndgameBonusId,
                    metadata
                  ).catch(() => {});
                }}
              />
              {showExploreUnknownMapDevControls && typeof document !== 'undefined'
                ? createPortal(
                    <ExploreUnknownDevExperiencePanel
                      currentLevel={exploreUnknownLevel}
                      currentTotalXp={exploreUnknownTotalXp}
                      ensureValidToken={ensureValidToken}
                      onApplied={ingestLevelingFromProgress}
                    />,
                    document.body
                  )
                : null}
              <ExploreUnknownPostcards
                earnedMissionIndices={exploreUnknownPostcardMissionIndices}
                isMobile={isContainerMobile}
              />
            </>
          ) : null}

          {sequencePhase === 'arrival' && forkRouteDestinationPreviews ? (
            <ExploreUnknownForkRouteDialog
              normalPreview={forkRouteDestinationPreviews.normalPreview}
              alternativePreview={forkRouteDestinationPreviews.alternativePreview}
              normalDestinationMapName={forkRouteDestinationPreviews.normalDestinationMapName}
              alternativeDestinationMapName={
                forkRouteDestinationPreviews.alternativeDestinationMapName
              }
              altMissionCount={surfaceRouteBundle.runAlt.missionIndices.length}
              altEasyMissionCount={
                filterAltPathEasyMissionIndices(
                  exploreSurface,
                  alternativePathPlan?.[exploreSurface]?.easyMissionIndices || []
                ).length
              }
              busy={routeChoiceBusy}
              isMobile={isContainerMobile}
              onChoose={(choice) => {
                void handleRouteChoice(choice).catch(() => {});
              }}
            />
          ) : null}
          {sequencePhase === 'arrival' && routeChoiceError ? (
            <div className="explore-unknown-run-session-error" role="alert">
              {routeChoiceError}
            </div>
          ) : null}

          {skipTwoWorldsNotice ? (
            <div
              className="explore-unknown-skip-world-overlay"
              role="dialog"
              aria-modal="true"
            >
              <div className="explore-unknown-skip-world-dialog">
                <h2>{t('exploreUnknownPage.skipTwoWorldsNotice.title')}</h2>
                <p>
                  {skipTwoWorldsNotice.pendingBoss
                    ? t('exploreUnknownPage.skipTwoWorldsNotice.bodyWithBoss', {
                        targetMap: t(
                          `exploreUnknownPage.${EXPLORE_UNKNOWN_MAP_NAME_I18N_KEYS[skipTwoWorldsNotice.targetSurface]}`
                        ),
                      })
                    : t('exploreUnknownPage.skipTwoWorldsNotice.body', {
                        targetMap: t(
                          `exploreUnknownPage.${EXPLORE_UNKNOWN_MAP_NAME_I18N_KEYS[skipTwoWorldsNotice.targetSurface]}`
                        ),
                      })}
                </p>
                <button
                  type="button"
                  className="explore-unknown-skip-world-choice"
                  onClick={() => skipTwoWorldsNotice.onDismiss?.()}
                >
                  {t('exploreUnknownPage.skipTwoWorldsNotice.continue')}
                </button>
              </div>
            </div>
          ) : null}

          {worldEntryRewardPopup ? (
            <ExploreUnknownDarkKnowledgeRewardModal
              popupId={worldEntryRewardPopup.popupId}
              rewardType={worldEntryRewardPopup.rewardType}
              orderId={worldEntryRewardPopup.orderId}
              amount={worldEntryRewardPopup.amount}
              onDismiss={() => setWorldEntryRewardPopup(null)}
            />
          ) : null}

          {sequencePhase === null && !isContainerMobile && showPlayer1PortalTutorialButton ? (
            <TutorialReplayButton user={user} onReplay={() => setShowTutorialExploreUnknown(true)} />
          ) : null}

          {sequencePhase === null && portalIntroLayout ? (
            <div
              className={`explore-unknown-portal${hasEnteredPortal ? ' explore-unknown-portal--entered' : ''}`}
              style={{
                left: `${portalIntroLayout.center.x}px`,
                top: `${portalIntroLayout.center.y}px`,
                transform: 'translate(-50%, -50%)',
                ...(portalIntroLayout.debugRect
                  ? {
                      width: `${portalIntroLayout.debugRect.width}px`,
                      height: `${portalIntroLayout.debugRect.height}px`,
                      outline: '2px dashed rgba(0, 255, 200, 0.75)',
                      outlineOffset: '2px',
                    }
                  : {}),
              }}
            >
              <div className="explore-unknown-portal-stack">
                <div className="explore-unknown-strategy-start-block">
                <h1 className="explore-unknown-title" id="explore-unknown-heading">
                  {t('battleOverview.exploreUnknown')}
                </h1>
                <span className="explore-unknown-strategy-label" id="explore-unknown-strategy-label">
                  {t('exploreUnknownPage.strategyLabel')}
                </span>
                <div
                  className="explore-unknown-strategy-dropdown-wrap"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  <CustomDropdown
                    options={strategyDropdownOptions}
                    value={selectedStrategyId}
                    onChange={(v) => {
                      setSelectedStrategyId(v);
                      if (v == null || v === '') {
                        return;
                      }
                      try {
                        writeExploreUnknownStoredStrategyId(v);
                      } catch (err) {
                        console.error('Explore Unknown: failed to persist strategy selection', err);
                      }
                    }}
                    placeholder={t('exploreUnknownPage.strategyPlaceholder')}
                    disabled={hasEnteredPortal}
                    renderOption={(option) => {
                      if (!option?.factionName) {
                        throw new Error('Explore Unknown strategy dropdown: option factionName is required.');
                      }
                      const factionIconPath = getFactionIconPath(option.factionName);
                      return (
                        <div className="explore-unknown-strategy-option">
                          <div
                            className="faction-orb"
                            style={{ background: getFactionOrbBackground(option.factionName) }}
                            title={option.factionName}
                          >
                            {factionIconPath && (
                              <img
                                src={factionIconPath}
                                alt=""
                                className="faction-orb-icon"
                                draggable={false}
                                aria-hidden="true"
                              />
                            )}
                          </div>
                          {option.showOwnershipWarning === true ? (
                            <span className="explore-unknown-strategy-ownership-warning" aria-hidden="true">
                              ⚠️
                            </span>
                          ) : null}
                          <span>{option.label}</span>
                        </div>
                      );
                    }}
                  />
                </div>
                {strategiesError ? (
                  <p className="explore-unknown-strategy-error" role="alert">
                    {strategiesError}
                  </p>
                ) : null}
                {!strategiesError && strategies.length === 0 ? (
                  <p className="explore-unknown-strategy-empty">{t('exploreUnknownPage.noStrategies')}</p>
                ) : null}
                {strategyHintVisible ? (
                  <p className="explore-unknown-strategy-hint" role="status">
                    {t('exploreUnknownPage.selectStrategyFirst')}
                  </p>
                ) : null}
                {sequencePhase === null && !hasEnteredPortal ? (
                  <MenuPaintedButton
                    className="explore-unknown-explore-btn"
                    imagePath={EXPLORE_UNKNOWN_EXPLORE_BUTTON_PATH}
                    disabled={exploreBlockedByIncompleteOwnership}
                    onClick={(event) => {
                      event.stopPropagation();
                      attemptEnterPortal();
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    {t('exploreUnknownPage.exploreButton')}
                  </MenuPaintedButton>
                ) : null}
                {sequencePhase === null &&
                !hasEnteredPortal &&
                selectedStrategyId &&
                strategyValidations[selectedStrategyId] != null &&
                strategyValidations[selectedStrategyId].valid !== true ? (
                  <p className="explore-unknown-strategy-unowned-warning" role="status">
                    {t('strategy.unownedOrdersWarning')}
                  </p>
                ) : null}
              </div>
            </div>
            </div>
          ) : null}

          {sequencePhase === 'scanner' ? (
            <div
              className="explore-unknown-sequence-overlay explore-unknown-sequence-overlay--scanner-skippable"
              role="presentation"
              onClick={dismissScannerIntro}
            >
              <video
                ref={scannerVideoRef}
                className="explore-unknown-sequence-video"
                playsInline
                controls={false}
                src="/images/scanner_drone.mp4"
                onEnded={handleScannerEnded}
                onError={handleScannerError}
              />
            </div>
          ) : null}
          {sequencePhase === 'arrival' ? (
            <div
              className={`explore-unknown-sequence-overlay${mapRefreshAnimating ? ' explore-unknown-sequence-overlay--map-refresh' : ''}${
                showExploreUnknownMapDevWaypointEditor &&
                waypointEditMode &&
                mapImageNaturalSize != null &&
                mapImageNaturalSize.w > 0 &&
                mapImageNaturalSize.h > 0
                  ? ' explore-unknown-sequence-overlay--waypoint-edit'
                  : ''
              }`}
              role="img"
              aria-label={`${t('battleOverview.exploreUnknown')}: ${activeMapDisplayName}`}
              data-committed-strategy-id={committedStrategyId ?? undefined}
            >
              <div className="explore-unknown-map-view">
                <img
                  ref={mapImageRef}
                  className={`explore-unknown-sequence-image${mapRefreshAnimating ? ' explore-unknown-sequence-image--map-refresh' : ''}`}
                  src={activeMapImageSrc}
                  alt=""
                  onLoad={handleMapImageLoad}
                  onError={handleMapImageError}
                />
                {Array.isArray(pathDisplayPoints) &&
                pathDisplayPoints.length > 0 &&
                effectivePathSlicePointStates ? (
                  <ExploreUnknownArrivalPointsOverlay
                    logicalWidth={containerBaseWidth}
                    logicalHeight={containerBaseHeight}
                    naturalWidth={mapImageNaturalSize?.w}
                    naturalHeight={mapImageNaturalSize?.h}
                    mapImageObjectFit={isContainerMobile ? 'cover' : 'contain'}
                    points={pathDisplayPoints}
                    pointStates={effectivePathSlicePointStates}
                    firstMissionIndex={firstMissionIndexForOverlay}
                    missionIndicesPerPoint={pathOverlayConfig.missionIndicesPerPoint}
                    easyMissionIndices={pathOverlayConfig.easyMissionIndices}
                    altPathEasierBossesEnabled={altPathEasierBossesEnabled}
                    showAltBossPacifierOnMainRoute={pathOverlayConfig.showAltBossPacifierOnMainRoute === true}
                    alternativeBranch={mapAlternativeBranch}
                    additionalAlternativeBranches={mapAdditionalAlternativeBranches}
                    mainRouteMissionClickOptions={pathOverlayConfig.mainRouteMissionClickOptions}
                    mainRouteExitPreview={pathOverlayConfig.mainRouteExitPreview}
                    mainRouteExitPreviewAuthoringPoint={
                      pathOverlayConfig.mainRouteExitPreviewAuthoringPoint
                    }
                    exitPreviewDevEdit={devMapExitPreviewDraggingActive}
                    exitPreviewDevVariant={exitPreviewDevVariant}
                    exitPreviewDevAuthoringPoint={exitPreviewDevAuthoringPoint}
                    exitPreviewDevPreview={exitPreviewDevContent}
                    onExitPreviewAuthoringChange={handleExitPreviewAuthoringChange}
                    onAvailableMissionClick={handleMapMissionClick}
                    pathClassName={mapRefreshAnimating ? 'explore-unknown-arrival-poi-overlay--map-refresh' : ''}
                    zIndex={
                      devMapWaypointDraggingActive || devMapExitPreviewDraggingActive ? 150000 : 1
                    }
                    waypointEditMode={devMapWaypointDraggingActive && pathOverlayConfig.devEditNormal === true}
                    layoutAuthoringActive={
                      devMapExitPreviewDraggingActive ||
                      (devMapWaypointDraggingActive &&
                        (pathOverlayConfig.devEditNormal === true ||
                          pathOverlayConfig.devEditAlt1 === true ||
                          pathOverlayConfig.devEditAlt2 === true))
                    }
                    onWaypointAuthoringChange={handleWaypointAuthoringChange}
                    darknessBargainAuthoringPoint={showDarknessBargainMarker ? darknessBargainAuthoringPoint : null}
                    darknessBargainVisible={showDarknessBargainMarker}
                    darknessBargainPolylineInsertAfterIndex={
                      showDarknessBargainMarker && darknessBargainMissionLocalIndex != null
                        ? darknessBargainMissionLocalIndex
                        : null
                    }
                    darknessBargainInteractive={darknessBargainMarkerInteractive}
                    onDarknessBargainClick={handleDarknessBargainMarkerActivate}
                    additionalDarknessBargainMarkers={devAllRouteDarknessBargainMarkers}
                    minigameAuthoringPoint={showMinigameMarker ? minigameMarkerPoint : null}
                    minigameVisible={showMinigameMarker}
                    minigameInteractive={minigameMarkerInteractive}
                    onMinigameClick={handleMinigameMarkerActivate}
                    bossMissionIndices={EXPLORE_UNKNOWN_PORTAL_LAYOUT.bossMissionIndices}
                  />
                ) : null}
                <div
                  className="explore-unknown-map-name-banner explore-unknown-map-painted-panel"
                  style={getExploreUnknownMapPaintedPanelStyle()}
                >
                  <span className="explore-unknown-map-name">{activeMapDisplayName}</span>
                  {committedRouteChipLabel ? (
                    <span className="explore-unknown-committed-route-chip">{committedRouteChipLabel}</span>
                  ) : null}
                </div>
                {nextSelectableMissionIndex != null ? (
                  <div
                    className="explore-unknown-next-mission-panel explore-unknown-map-painted-panel"
                    style={getExploreUnknownMapPaintedPanelStyle()}
                    role="region"
                    aria-label={t('exploreUnknownPage.missionMapPanelTitle', {
                      missionIndex: nextSelectableMissionIndex,
                    })}
                    aria-busy={nextMissionCompletionRatesLoading}
                  >
                    <h2 className="explore-unknown-next-mission-panel-title">
                      {t('exploreUnknownPage.missionMapPanelTitle', {
                        missionIndex: nextSelectableMissionIndex,
                      })}
                    </h2>
                    {nextMissionCompletionRatesError ? (
                      <p className="explore-unknown-next-mission-panel-error" role="alert">
                        {nextMissionCompletionRatesError}
                      </p>
                    ) : null}
                    <dl className="explore-unknown-next-mission-panel-rates">
                      <div className="explore-unknown-next-mission-panel-row">
                        <dt>{t('exploreUnknownPage.missionGlobalCompletionRate')}</dt>
                        <dd>
                          {nextMissionCompletionRatesLoading
                            ? '…'
                            : formatExploreUnknownCompletionRateValue(
                                nextMissionCompletionRates?.global,
                                t
                              )}
                        </dd>
                      </div>
                      <div className="explore-unknown-next-mission-panel-row">
                        <dt>{t('exploreUnknownPage.missionYourCompletionRate')}</dt>
                        <dd>
                          {nextMissionCompletionRatesLoading
                            ? '…'
                            : formatExploreUnknownCompletionRateValue(
                                nextMissionCompletionRates?.individual,
                                t
                              )}
                        </dd>
                      </div>
                    </dl>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
          {sequencePhase === 'arrival' && minigameResolveError ? (
            <div className="explore-unknown-minigame-resolve-error" role="alert">
              {minigameResolveError}
            </div>
          ) : null}
          {sequencePhase === 'arrival' && minigameModalOpen && activeMinigameId != null ? (
            <ExploreUnknownMinigameHost
              minigameId={activeMinigameId}
              challenge={minigameChallenge}
              open={minigameModalOpen}
              onDismiss={handleMinigameModalDismiss}
              onPickTree={handleMinigameComplete}
              completeBusy={minigameCompleteBusy}
              completeError={minigameCompleteError}
              completeOutcome={minigameCompleteOutcome}
              completeWinReward={minigameCompleteWinReward}
              completeWinRewards={minigameCompleteWinRewards}
              completeGambleResolution={minigameCompleteGambleResolution}
              showPlayer1DevGrid={showPlayer1MinigameDevGridOnMap}
              isMobile={isContainerMobile}
            />
          ) : null}
          {sequencePhase === 'arrival' && darknessBargainModalTier != null ? (
            <div
              className="explore-unknown-darkness-bargain-modal-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby="explore-unknown-darkness-bargain-title"
            >
              <div className="explore-unknown-darkness-bargain-modal">
                <div className="explore-unknown-darkness-bargain-modal-visual">
                  <img
                    src="/images/rite_of_respite.jpg"
                    alt=""
                    className="explore-unknown-darkness-bargain-modal-image"
                  />
                  <div className="explore-unknown-darkness-bargain-modal-overlay-stack">
                    <div className="explore-unknown-darkness-bargain-modal-overlay-top">
                      <h2 id="explore-unknown-darkness-bargain-title" className="explore-unknown-darkness-bargain-modal-title">
                        {t('exploreUnknownPage.darknessBargainTitle')}
                      </h2>
                    </div>
                    <div className="explore-unknown-darkness-bargain-modal-overlay-bottom">
                      <p className="explore-unknown-darkness-bargain-modal-tagline">
                        {t('exploreUnknownPage.darknessBargainTagline')}
                      </p>
                      {darknessBargainPurchaseError ? (
                        <p className="explore-unknown-darkness-bargain-modal-error" role="alert">
                          {darknessBargainPurchaseError}
                        </p>
                      ) : null}
                      <div className="explore-unknown-darkness-bargain-modal-actions">
                        <button
                          type="button"
                          className="explore-unknown-darkness-bargain-modal-button explore-unknown-darkness-bargain-modal-button--confirm"
                          disabled={darknessBargainPurchaseBusy}
                          onClick={() => handleConfirmDarknessBargainPurchase()}
                        >
                          {darknessBargainPurchaseBusy ? '…' : t('exploreUnknownPage.darknessBargainConfirm')}
                        </button>
                        <button
                          type="button"
                          className="explore-unknown-darkness-bargain-modal-button explore-unknown-darkness-bargain-modal-button--cancel"
                          disabled={darknessBargainPurchaseBusy}
                          onClick={() => handleDarknessBargainModalDismiss()}
                        >
                          {t('exploreUnknownPage.darknessBargainCancel')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          {sequencePhase === 'arrival' && showSurfaceExitTransitionPrompt ? (
            <div
              className={
                pendingSurfaceExitTransition.promptSurface === 'arrival'
                  ? 'explore-unknown-cave-entrance'
                  : `explore-unknown-cave-exit${
                      pendingSurfaceExitTransition.promptSurface === 'desert'
                        ? ' explore-unknown-desert-fair'
                        : pendingSurfaceExitTransition.promptSurface === 'fair'
                          ? ' explore-unknown-fair-lavender'
                          : pendingSurfaceExitTransition.promptSurface === 'lavender'
                            ? ' explore-unknown-lavender-treetop'
                            : pendingSurfaceExitTransition.promptSurface === 'treetop'
                              ? ' explore-unknown-treetop-boat'
                              : pendingSurfaceExitTransition.promptSurface === 'boat'
                                ? ' explore-unknown-boat-pixelpast'
                                : pendingSurfaceExitTransition.promptSurface === 'pixelpast'
                                  ? ' explore-unknown-pixelpast-kendrex'
                                  : pendingSurfaceExitTransition.promptSurface === 'kendrex'
                                    ? ' explore-unknown-kendrex-autobahn'
                                    : pendingSurfaceExitTransition.promptSurface === 'autobahn'
                                      ? ' explore-unknown-autobahn-productionhell'
                                      : pendingSurfaceExitTransition.promptSurface === 'productionhell'
                                        ? ' explore-unknown-productionhell-awakening'
                                        : pendingSurfaceExitTransition.promptSurface === 'awakening'
                                          ? ' explore-unknown-awakening-core'
                                          : ''
                    }`
              }
              role="region"
              aria-label={t(`exploreUnknownPage.${pendingSurfaceExitTransition.regionKey}`)}
            >
              <p
                className={
                  pendingSurfaceExitTransition.promptSurface === 'arrival'
                    ? 'explore-unknown-cave-entrance-text'
                    : 'explore-unknown-cave-exit-text'
                }
              >
                {t(`exploreUnknownPage.${pendingSurfaceExitTransition.bodyKey}`)}
              </p>
              <button
                type="button"
                className={
                  pendingSurfaceExitTransition.promptSurface === 'arrival'
                    ? 'explore-unknown-cave-entrance-button'
                    : 'explore-unknown-cave-exit-button'
                }
                onClick={() => {
                  void handleSurfaceExitContinue();
                }}
                disabled={continueSurfaceBusy}
              >
                {t(`exploreUnknownPage.${pendingSurfaceExitTransition.continueKey}`)}
              </button>
            </div>
          ) : null}
          {sequencePhase === 'arrival' &&
          exploreSurface === 'core' &&
          nextSelectableMissionIndex != null &&
          (!Array.isArray(currentMapPoints) || currentMapPoints.length === 0) &&
          !showKinshipBossPrompt ? (
            <div
              className="explore-unknown-cave-exit explore-unknown-core-start"
              role="region"
              aria-label={t('exploreUnknownPage.coreStartMissionRegion')}
            >
              {coreMissionIntroKey && i18n.exists(coreMissionIntroKey) ? (
                <p className="explore-unknown-cave-exit-text">{t(coreMissionIntroKey)}</p>
              ) : null}
              <button
                type="button"
                className="explore-unknown-cave-exit-button"
                onClick={() => handleAvailableMissionClick(nextSelectableMissionIndex)}
                disabled={startingMission || continueSurfaceBusy}
              >
                {t('exploreUnknownPage.coreStartMission')}
              </button>
            </div>
          ) : null}
          {showKinshipBossPrompt && kinshipMissionIndex != null ? (
            <div
              className="explore-unknown-cave-exit explore-unknown-core-start explore-unknown-kinship-start"
              role="region"
              aria-label={t('exploreUnknownPage.kinshipStartMissionRegion')}
            >
              {i18n.exists('exploreUnknownPage.kinshipMissionIntro') ? (
                <p className="explore-unknown-cave-exit-text">{t('exploreUnknownPage.kinshipMissionIntro')}</p>
              ) : null}
              <button
                type="button"
                className="explore-unknown-cave-exit-button"
                onClick={() => {
                  setKinshipBossQueued(false);
                  void handleAvailableMissionClick(kinshipMissionIndex).catch(() => {});
                }}
                disabled={startingMission || continueSurfaceBusy}
              >
                {t('exploreUnknownPage.kinshipStartMission')}
              </button>
            </div>
          ) : null}
          {sequencePhase === 'explore_end' && !showNegaraDoorIntro ? (
            <div
              className="explore-unknown-sequence-overlay explore-unknown-explore-end"
              role="region"
              aria-label={t('exploreUnknownPage.exploreEndRegion')}
            >
              <div className="explore-unknown-map-view">
                <img
                  className="explore-unknown-sequence-image"
                  src={
                    exploreUnknownLevel >= 100
                      ? '/images/explore_end_100.jpg'
                      : '/images/explore_end.jpg'
                  }
                  alt=""
                />
              </div>
              <div className="explore-unknown-explore-end-message" role="doc-subtitle">
                <p className="explore-unknown-explore-end-message-text">
                  {t(
                    exploreUnknownLevel >= 100
                      ? 'exploreUnknownPage.exploreEndMessageDoorOpen'
                      : 'exploreUnknownPage.exploreEndMessageDoorClosed'
                  )}
                </p>
              </div>
              <button
                type="button"
                className="explore-unknown-explore-end-action explore-unknown-explore-end-action--home"
                onClick={handleExploreEndGoHome}
                disabled={exploreEndGoHomeBusy}
              >
                <span className="explore-unknown-explore-end-action-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" focusable="false">
                    <path
                      d="M3 10.5 12 3l9 7.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M5 9.75V20h14V9.75"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9 20v-6h6v6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="explore-unknown-explore-end-action-label">
                  {exploreEndGoHomeBusy ? '…' : t('exploreUnknownPage.exploreEndGoHome')}
                </span>
              </button>
              <button
                type="button"
                className="explore-unknown-explore-end-action explore-unknown-explore-end-action--door"
                disabled={exploreUnknownLevel < 100}
                title={
                  exploreUnknownLevel < 100
                    ? t('exploreUnknownPage.exploreEndDoorLocked')
                    : undefined
                }
                onClick={() => {
                  if (exploreUnknownLevel >= 100) {
                    playSoundEffect('/sounds/click.mp3');
                    setMissionStartError(null);
                    setShowNegaraDoorIntro(true);
                  }
                }}
              >
                <span className="explore-unknown-explore-end-action-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" focusable="false">
                    <rect
                      x="5"
                      y="11"
                      width="14"
                      height="10"
                      rx="2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                    />
                    <path
                      d="M8 11V8a4 4 0 0 1 8 0v3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="explore-unknown-explore-end-action-label">
                  {t('exploreUnknownPage.exploreEndDoor')}
                </span>
              </button>
              {exploreEndGoHomeError ? (
                <p className="explore-unknown-explore-end-error" role="alert">
                  {exploreEndGoHomeError}
                </p>
              ) : null}
              {missionStartError ? (
                <p className="explore-unknown-explore-end-error" role="alert">
                  {missionStartError}
                </p>
              ) : null}
            </div>
          ) : null}
          {sequencePhase === 'explore_end' && showNegaraDoorIntro ? (
            <div
              className="explore-unknown-sequence-overlay explore-unknown-negara-door-intro"
              role="region"
              aria-label={t('exploreUnknownPage.negaraDoorIntroRegion')}
            >
              <div
                className="explore-unknown-map-view explore-unknown-negara-door-intro-bg"
                style={{
                  backgroundImage:
                    'url(/unknown_backgrounds/shadow_negara.jpg)',
                }}
              />
              <div className="explore-unknown-cave-exit explore-unknown-negara-door-intro-panel">
                <p className="explore-unknown-cave-exit-text explore-unknown-negara-door-intro-text">
                  {t('exploreUnknownPage.negaraDoorIntroBody')
                    .split('\n')
                    .map((line, i) => (
                      <React.Fragment key={`${i}-${line}`}>
                        {i > 0 ? <br /> : null}
                        {line}
                      </React.Fragment>
                    ))}
                </p>
                <button
                  type="button"
                  className="explore-unknown-cave-exit-button"
                  onClick={() => {
                    setMissionStartError(null);
                    void (async () => {
                      try {
                        await ensureActiveRunForNegaraDoor();
                        await handleAvailableMissionClick(
                          EXPLORE_UNKNOWN_NEGARA_DOOR_MISSION_INDEX
                        );
                      } catch (err) {
                        setMissionStartError(
                          err instanceof Error ? err.message : String(err)
                        );
                      }
                    })();
                  }}
                  disabled={startingMission || exploreEndGoHomeBusy}
                >
                  {t('exploreUnknownPage.negaraDoorIntroContinue')}
                </button>
                {startingMission ? (
                  <p className="explore-unknown-negara-door-intro-status" role="status">
                    {t('exploreUnknownPage.startingMission')}
                  </p>
                ) : null}
                {missionStartError ? (
                  <p className="explore-unknown-explore-end-error" role="alert">
                    {missionStartError}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
          {showLevel100DoorToast && sequencePhase === null ? (
            <ExploreUnknownLevel100DoorToast
              onDismiss={() => {
                markExploreUnknownLevel100DoorUnlockSeen();
                setShowLevel100DoorToast(false);
              }}
            />
          ) : null}
          {sequencePhase === 'arrival' && continueSurfaceError ? (
            <div className="explore-unknown-run-session-error" role="alert">
              {continueSurfaceError}
            </div>
          ) : null}
          {sequencePhase === 'arrival' && runSessionError ? (
            <div className="explore-unknown-run-session-error" role="alert">
              {runSessionError}
            </div>
          ) : null}
          {sequencePhase === 'arrival' && missionProgressError ? (
            <div className="explore-unknown-mission-progress-error" role="alert">
              {missionProgressError}
            </div>
          ) : null}
          {sequencePhase === 'arrival' && activeRunId != null ? (
            <div className="explore-unknown-map-resign-wrap">
              <MenuPaintedButton
                className="resign-button"
                imagePath={BATTLE_UI_RESIGN_BUTTON_PATH}
                onClick={handleOpenResignRunDialog}
                disabled={resignRunBusy || startingMission || continueSurfaceBusy}
                style={{ margin: 0, position: 'relative' }}
              >
                {resignRunBusy ? t('exploreUnknownPage.resignRunBusy') : t('battleUI.resign')}
              </MenuPaintedButton>
              {resignRunError && !showResignRunDialog ? (
                <p className="explore-unknown-map-resign-error" role="alert">
                  {resignRunError}
                </p>
              ) : null}
            </div>
          ) : null}
          {sequencePhase === 'arrival' && activeRunId != null && !runSessionError ? (
            <>
            <div className="explore-unknown-map-run-meters">
              <ExploreUnknownDarknessMeter
                fillPercent={darknessPercent}
                tooltipBelow
                tooltipTitle={t('exploreUnknownPage.darknessMeterTooltipTitle', {
                  percent: darknessPercent,
                })}
                regionLabel={t('exploreUnknownPage.darknessMeterRegion')}
              />
              <div
                className="explore-unknown-xp-bar-wrap explore-unknown-xp-bar-tooltip-host"
                role="group"
                aria-label={t('exploreUnknownPage.xpMeterRegion')}
                tabIndex={0}
              >
                <div className="explore-unknown-xp-tooltip" role="tooltip" aria-hidden="true">
                  <p className="explore-unknown-xp-tooltip-title">
                    {t('exploreUnknownPage.xpMeterTooltipTitle', {
                      level: exploreUnknownLevel,
                      xp: exploreUnknownTotalXpDisplay,
                    })}
                  </p>
                  {exploreUnknownAtMaxLevel ? (
                    <p className="explore-unknown-xp-tooltip-body">
                      {t('exploreUnknownPage.xpMeterTooltipMaxLevel')}
                    </p>
                  ) : (
                    <p className="explore-unknown-xp-tooltip-body">
                      {t('exploreUnknownPage.xpMeterTooltipProgress', {
                        current: Number(exploreUnknownXpIntoLevel || 0).toLocaleString(),
                        needed: Number(
                          (exploreUnknownXpIntoLevel || 0) + (exploreUnknownXpToNext || 0)
                        ).toLocaleString(),
                      })}
                    </p>
                  )}
                </div>
                <span className="explore-unknown-xp-bar-label" aria-hidden="true">
                  XP
                </span>
                <div
                  className="explore-unknown-xp-track"
                  role="progressbar"
                  aria-valuenow={exploreUnknownAtMaxLevel ? 100 : exploreUnknownXpBarPercentValue}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={t('exploreUnknownPage.xpMeterTooltipTitle', {
                    level: exploreUnknownLevel,
                    xp: exploreUnknownTotalXpDisplay,
                  })}
                >
                  <div
                    className="explore-unknown-xp-fill"
                    style={{ width: `${exploreUnknownXpBarPercentValue}%` }}
                  />
                </div>
                <span
                  className="explore-unknown-xp-total-slot"
                  title={exploreUnknownTotalXpDisplay}
                  aria-label={t('exploreUnknownPage.levelTree.totalXp', {
                    xp: exploreUnknownTotalXpDisplay,
                  })}
                >
                  <span className="explore-unknown-xp-total">{exploreUnknownTotalXpDisplay}</span>
                </span>
              </div>
            </div>
            {skipMissionEnabled && missionProgress?.nextAvailable != null ? (
              <div className="explore-unknown-skip-mission-wrap">
                <button
                  type="button"
                  className="explore-unknown-skip-mission-button"
                  disabled={skipMissionBusy || startingMission || devAutowinBusy || devForceLossBusy}
                  onClick={() => {
                    void handleSkipNextMission();
                  }}
                >
                  {skipMissionBusy
                    ? t('exploreUnknownPage.skipMissionBusy')
                    : t('exploreUnknownPage.skipMissionButton', {
                        darknessCost: skipMissionDarknessCost,
                        missionIndex: missionProgress.nextAvailable,
                      })}
                </button>
                {skipMissionError ? (
                  <p className="explore-unknown-skip-mission-error" role="alert">
                    {skipMissionError}
                  </p>
                ) : null}
              </div>
            ) : null}
            <ExploreUnknownMobileCollectibleDrawer
              isMobile={isContainerMobile}
              tabLabel={t('exploreUnknownPage.collectibleMetersDrawerTab')}
            >
              <div
                className="explore-unknown-dark-knowledge-bar-wrap explore-unknown-dark-knowledge-bar-tooltip-host"
                role="group"
                aria-label={t('exploreUnknownBattle.darkKnowledgeMeterRegion')}
                tabIndex={0}
              >
                <div className="explore-unknown-dark-knowledge-tooltip" role="tooltip" aria-hidden="true">
                  <p className="explore-unknown-dark-knowledge-tooltip-title">
                    {t('exploreUnknownBattle.darkKnowledgeMeterTooltipTitle', {
                      collected: darkKnowledgePartsCollected,
                      target: EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS,
                    })}
                  </p>
                  <p className="explore-unknown-dark-knowledge-tooltip-body">
                    {t('exploreUnknownBattle.darkKnowledgeMeterFlashBody', {
                      target: EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS,
                    })}
                  </p>
                </div>
                <img src="/images/dark_knowledge.jpg" alt="" className="explore-unknown-dark-knowledge-bar-icon" />
                <div className="explore-unknown-dark-knowledge-track">
                  <div
                    className="explore-unknown-dark-knowledge-fill"
                    style={{
                      width: `${Math.min(
                        100,
                        (Math.max(0, darkKnowledgePartsCollected) /
                          EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS) *
                          100
                      )}%`,
                    }}
                  />
                </div>
                <span className="explore-unknown-dark-knowledge-count-slot" aria-hidden="true">
                  <span className="explore-unknown-dark-knowledge-count">
                    {darkKnowledgePartsCollected}/{EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS}
                  </span>
                </span>
              </div>
              {EXPLORE_UNKNOWN_HEART_METERS.map((meter) => {
                const collected =
                  meter.apiKey === 'weakHeartPartsCollected'
                    ? weakHeartPartsCollected
                    : meter.apiKey === 'heartPartsCollected'
                      ? heartPartsCollected
                      : strongHeartPartsCollected;
                const setsCompleted =
                  meter.apiKeySetsCompleted === 'weakHeartSetsCompleted'
                    ? weakHeartSetsCompleted
                    : meter.apiKeySetsCompleted === 'heartSetsCompleted'
                      ? heartSetsCompleted
                      : strongHeartSetsCompleted;
                const i18nPrefix = `${meter.i18nKey}Meter`;
                return (
                  <ExploreUnknownHeartProgressBar
                    key={meter.id}
                    variant={meter.variant}
                    imageSrc={meter.image}
                    collected={collected}
                    setsCompleted={setsCompleted}
                    targetParts={EXPLORE_UNKNOWN_HEART_TARGET_PARTS}
                    regionLabel={t(`exploreUnknownBattle.${i18nPrefix}Region`)}
                    tooltipTitle={t(`exploreUnknownBattle.${i18nPrefix}TooltipTitle`, {
                      collected,
                      target: EXPLORE_UNKNOWN_HEART_TARGET_PARTS,
                    })}
                    tooltipBody={t(`exploreUnknownBattle.${i18nPrefix}TooltipBody`, {
                      setsCompleted,
                      target: EXPLORE_UNKNOWN_HEART_TARGET_PARTS,
                      healthBonus: meter.healthBonus,
                    })}
                    setsCompletedAriaLabel={t(`exploreUnknownBattle.${i18nPrefix}SetsCompleted`, {
                      count: setsCompleted,
                    })}
                    mode="map"
                  />
                );
              })}
              {EXPLORE_UNKNOWN_EP_METERS.map((meter) => {
                const partsByKey = {
                  epGlobalPartsCollected,
                  epZonePartsCollected,
                  epTargetPartsCollected,
                };
                const chargesByKey = {
                  epGlobalCharges,
                  epZoneCharges,
                  epTargetCharges,
                };
                const collected = partsByKey[meter.apiKeyParts];
                const charges = chargesByKey[meter.apiKeyCharges];
                const i18nPrefix = `${meter.i18nKey}Meter`;
                return (
                  <ExploreUnknownHeartProgressBar
                    key={meter.id}
                    variant={meter.variant}
                    imageSrc={meter.image}
                    collected={collected}
                    charges={charges}
                    targetParts={EXPLORE_UNKNOWN_EP_TARGET_PARTS}
                    regionLabel={t(`exploreUnknownBattle.${i18nPrefix}Region`)}
                    tooltipTitle={t(`exploreUnknownBattle.${i18nPrefix}TooltipTitle`, {
                      collected,
                      target: EXPLORE_UNKNOWN_EP_TARGET_PARTS,
                    })}
                    tooltipBody={t(`exploreUnknownBattle.${i18nPrefix}TooltipBody`, {
                      charges,
                      target: EXPLORE_UNKNOWN_EP_TARGET_PARTS,
                    })}
                    chargesAriaLabel={t(`exploreUnknownBattle.${i18nPrefix}Charges`, { count: charges })}
                    mode="map"
                  />
                );
              })}
              <ExploreUnknownHeartProgressBar
                key={EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_METER.id}
                variant={EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_METER.variant}
                imageSrc={EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_METER.image}
                collected={rewriteExperiencePartsCollected}
                charges={rewriteExperienceCharges}
                targetParts={EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS}
                regionLabel={t('exploreUnknownBattle.rewriteExperienceMeterRegion')}
                tooltipTitle={t('exploreUnknownBattle.rewriteExperienceMeterTooltipTitle', {
                  collected: rewriteExperiencePartsCollected,
                  target: EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS,
                })}
                tooltipBody={t('exploreUnknownBattle.rewriteExperienceMeterTooltipBody', {
                  charges: rewriteExperienceCharges,
                  target: EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS,
                  darknessPercent: 10,
                })}
                chargesAriaLabel={t('exploreUnknownBattle.rewriteExperienceMeterCharges', {
                  count: rewriteExperienceCharges,
                })}
                mode="map"
              />
            </ExploreUnknownMobileCollectibleDrawer>
            </>
          ) : null}
          {sequencePhase === 'arrival' &&
          showExploreUnknownMapDevControls &&
          typeof document !== 'undefined'
            ? createPortal(
            <div className="explore-unknown-dev-right-stack">
              {activeRunId != null &&
              (exploreSurface === 'arrival' ||
                exploreSurface === 'cave' ||
                exploreSurface === 'desert' ||
                exploreSurface === 'fair' ||
                exploreSurface === 'lavender' ||
                exploreSurface === 'treetop' ||
                exploreSurface === 'boat' ||
                exploreSurface === 'pixelpast' ||
                exploreSurface === 'autobahn') ? (
                <div className="explore-unknown-dev-minigame-test">
                  <button
                    type="button"
                    className="explore-unknown-dev-minigame-test-button"
                    disabled={
                      devMinigameTestBusy ||
                      minigameCompleteBusy ||
                      devAutowinBusy ||
                      devForceLossBusy ||
                      startingMission
                    }
                    onClick={() => handleDevStartBirdMinigameTest()}
                  >
                    {devMinigameTestBusy
                      ? '…'
                      : exploreSurface === 'cave'
                        ? t('exploreUnknownPage.minigame.wombafleks.devButton')
                        : exploreSurface === 'desert'
                          ? t('exploreUnknownPage.minigame.rewardMemory.devButton')
                          : exploreSurface === 'fair'
                            ? t('exploreUnknownPage.minigame.balloonBooth.devButton')
                            : exploreSurface === 'lavender'
                              ? t('exploreUnknownPage.minigame.theGamble.devButton')
                              : exploreSurface === 'treetop'
                                ? t('exploreUnknownPage.minigame.spottingObjects.devButton')
                                : exploreSurface === 'boat'
                                  ? t('exploreUnknownPage.minigame.boatSharkCrossing.devButton')
                                  : exploreSurface === 'pixelpast'
                                  ? t('exploreUnknownPage.minigame.pixelPastRescue.devButton')
                                  : exploreSurface === 'autobahn'
                                    ? t('exploreUnknownPage.minigame.autobahnLaneSimon.devButton')
                                    : t('exploreUnknownPage.minigame.beyondBird.devButton')}
                  </button>
                  {devMinigameTestError ? (
                    <p className="explore-unknown-dev-minigame-test-error" role="alert">
                      {devMinigameTestError}
                    </p>
                  ) : null}
                </div>
              ) : null}
              <div
                className="explore-unknown-dev-level-reward-test"
                role="region"
                aria-label="Dev: test level 30–34 rewards"
              >
                {[30, 31, 32, 33, 34].map((level) => (
                  <button
                    key={`dev-level-reward-${level}`}
                    type="button"
                    className="explore-unknown-dev-level-reward-test-button"
                    disabled={
                      devLevelRewardBusy != null ||
                      devAutowinBusy ||
                      devForceLossBusy ||
                      startingMission ||
                      (level > 30 && activeRunId == null) ||
                      (level !== 31 && (committedStrategyId == null || committedStrategyId === ''))
                    }
                    onClick={() => {
                      void handleDevTestLevelReward(level).catch(() => {});
                    }}
                  >
                    {devLevelRewardBusy === level ? '…' : `Dev: L${level} reward`}
                  </button>
                ))}
                {devLevelRewardError ? (
                  <p className="explore-unknown-dev-level-reward-test-error" role="alert">
                    {devLevelRewardError}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="explore-unknown-dev-mock-win-rewards-button"
                disabled={
                  devLevelRewardBusy != null ||
                  devAutowinBusy ||
                  devForceLossBusy ||
                  startingMission
                }
                onClick={handleDevMockEuWinRewards}
              >
                Dev: mock EU win rewards
              </button>
            </div>,
            document.body
              )
            : null}
          {sequencePhase === 'arrival' &&
          showExploreUnknownMapDevControls &&
          activeRunId != null &&
          typeof document !== 'undefined'
            ? createPortal(
            <div className="explore-unknown-dev-autowin">
              <div className="explore-unknown-dev-boss-jumps" aria-label="Dev: jump to boss mission">
                {(EXPLORE_UNKNOWN_PORTAL_LAYOUT.bossMissionIndices || []).map((bossIdx) => (
                  <button
                    key={`dev-boss-${bossIdx}`}
                    type="button"
                    className="explore-unknown-dev-boss-jump-link"
                    disabled={devAutowinBusy || devForceLossBusy || startingMission}
                    onClick={() => handleAvailableMissionClick(bossIdx, true)}
                  >
                    Boss {bossIdx}
                  </button>
                ))}
                {(EXPLORE_UNKNOWN_PORTAL_LAYOUT.devSurfaceEntryMissionIndices || []).map(
                  (entryIdx) => (
                    <button
                      key={`dev-entry-${entryIdx}`}
                      type="button"
                      className="explore-unknown-dev-boss-jump-link explore-unknown-dev-boss-jump-link--entry"
                      disabled={devAutowinBusy || devForceLossBusy || startingMission}
                      onClick={() => handleAvailableMissionClick(entryIdx, true)}
                    >
                      Core {entryIdx}
                    </button>
                  )
                )}
              </div>
              <button
                type="button"
                className="explore-unknown-dev-autowin-button"
                disabled={devAutowinBusy || devForceLossBusy || startingMission}
                onClick={() => handleDevAutowinNext()}
              >
                {devAutowinBusy ? '…' : 'Dev: autowin next mission'}
              </button>
              <button
                type="button"
                className="explore-unknown-dev-autowin-button explore-unknown-dev-autowin-button--loss"
                disabled={devAutowinBusy || devForceLossBusy || startingMission}
                onClick={() => handleDevForceRunLoss()}
              >
                {devForceLossBusy ? '…' : 'Dev: force run loss'}
              </button>
              {devAutowinError ? (
                <p className="explore-unknown-dev-autowin-error" role="alert">
                  {devAutowinError}
                </p>
              ) : null}
              {devForceLossError ? (
                <p className="explore-unknown-dev-autowin-error" role="alert">
                  {devForceLossError}
                </p>
              ) : null}
            </div>,
            document.body
              )
            : null}
          {sequencePhase === 'arrival' && missionStartError ? (
            <div className="explore-unknown-mission-start-error" role="alert">
              {missionStartError}
            </div>
          ) : null}
          {startingMission ? (
            <div className="explore-unknown-starting-mission" role="status">
              {t('exploreUnknownPage.startingMission')}
            </div>
          ) : null}
          {sequencePhase === 'arrival' &&
          exploreSurface === 'arrival' &&
          EXPLORE_UNKNOWN_PORTAL_LAYOUT.showArrivalLayoutGrid ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
              zIndex={13000}
              tooltipFooter={t('exploreUnknownPage.arrivalGridHint', {
                gridSize: EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20,
              })}
            />
          ) : null}
          {sequencePhase === 'arrival' &&
          exploreSurface === 'cave' &&
          EXPLORE_UNKNOWN_PORTAL_LAYOUT.showCaveLayoutGrid ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
              zIndex={13200}
              tooltipFooter={t('exploreUnknownPage.caveGridHint', {
                gridSize: EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20,
              })}
            />
          ) : null}
          {sequencePhase === 'arrival' &&
          exploreSurface === 'desert' &&
          EXPLORE_UNKNOWN_PORTAL_LAYOUT.showDesertLayoutGrid ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
              zIndex={13200}
              tooltipFooter={t('exploreUnknownPage.desertGridHint', {
                gridSize: EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20,
              })}
            />
          ) : null}
          {sequencePhase === 'arrival' &&
          exploreSurface === 'fair' &&
          EXPLORE_UNKNOWN_PORTAL_LAYOUT.showFairLayoutGrid ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
              zIndex={13200}
              tooltipFooter={t('exploreUnknownPage.fairGridHint', {
                gridSize: EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20,
              })}
            />
          ) : null}
          {sequencePhase === 'arrival' &&
          exploreSurface === 'lavender' &&
          EXPLORE_UNKNOWN_PORTAL_LAYOUT.showLavenderLayoutGrid ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
              zIndex={13200}
              tooltipFooter={t('exploreUnknownPage.lavenderGridHint', {
                gridSize: EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20,
              })}
            />
          ) : null}
          {sequencePhase === 'arrival' &&
          exploreSurface === 'kendrex' &&
          EXPLORE_UNKNOWN_PORTAL_LAYOUT.showKendrexLayoutGrid ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
              zIndex={13200}
              tooltipFooter={t('exploreUnknownPage.kendrexGridHint', {
                gridSize: EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20,
              })}
            />
          ) : null}
          {sequencePhase === 'arrival' &&
          exploreSurface === 'productionhell' &&
          EXPLORE_UNKNOWN_PORTAL_LAYOUT.showProductionHellLayoutGrid ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
              zIndex={13200}
              tooltipFooter={t('exploreUnknownPage.productionHellGridHint', {
                gridSize: EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20,
              })}
            />
          ) : null}
          {sequencePhase === 'arrival' &&
          exploreSurface === 'awakening' &&
          EXPLORE_UNKNOWN_PORTAL_LAYOUT.showAwakeningLayoutGrid ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
              zIndex={13200}
              tooltipFooter={t('exploreUnknownPage.awakeningGridHint', {
                gridSize: EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20,
              })}
            />
          ) : null}
          {sequencePhase === 'arrival' &&
          exploreSurface === 'autobahn' &&
          EXPLORE_UNKNOWN_PORTAL_LAYOUT.showAutobahnLayoutGrid ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
              zIndex={13200}
              tooltipFooter={t('exploreUnknownPage.autobahnGridHint', {
                gridSize: EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20,
              })}
            />
          ) : null}
          {sequencePhase === 'arrival' &&
          showExploreUnknownMapDevWaypointEditor &&
          typeof document !== 'undefined' &&
          devWaypointBarHidden
            ? createPortal(
                <button
                  type="button"
                  className="explore-unknown-dev-waypoint-bar-show"
                  aria-label="Show development waypoint editor"
                  onClick={() => setDevWaypointBarHidden(false)}
                >
                  Dev waypoints
                </button>,
                document.body
              )
            : null}
          {sequencePhase === 'arrival' &&
          showExploreUnknownMapDevWaypointEditor &&
          typeof document !== 'undefined' &&
          !devWaypointBarHidden
            ? createPortal(
                <div className="explore-unknown-dev-waypoint-bar" role="region" aria-label="Development waypoint editor">
                  <span className="explore-unknown-dev-waypoint-bar-title">Dev: waypoints</span>
                  <button
                    type="button"
                    className="explore-unknown-dev-waypoint-bar-button explore-unknown-dev-waypoint-bar-button--hide"
                    onClick={() => {
                      setDevWaypointBarHidden(true);
                      setWaypointEditMode(false);
                    }}
                  >
                    Hide
                  </button>
                  <button
                    type="button"
                    className="explore-unknown-dev-waypoint-bar-button"
                    disabled={mapImageNaturalSize == null}
                    onClick={() => setWaypointEditMode((v) => !v)}
                  >
                    {waypointEditMode ? 'Stop editing' : 'Drag to edit'}
                  </button>
                  <button
                    type="button"
                    className="explore-unknown-dev-waypoint-bar-button"
                    onClick={() => handleResetDevWaypoints()}
                  >
                    Reset to layout file
                  </button>
                  <button
                    type="button"
                    className="explore-unknown-dev-waypoint-bar-button"
                    onClick={() => {
                      void handleCopyDevWaypoints().catch((err) => {
                        setDevWaypointUiError(err instanceof Error ? err.message : String(err));
                      });
                    }}
                  >
                    {waypointCopyDone ? 'Copied' : 'Copy array for layout file'}
                  </button>
                  <button
                    type="button"
                    className={`explore-unknown-dev-waypoint-bar-button${devPathEditMode === 'normal' ? ' explore-unknown-dev-waypoint-bar-button--active' : ''}`}
                    onClick={() => setDevPathEditMode('normal')}
                  >
                    Normal route
                  </button>
                  <button
                    type="button"
                    className={`explore-unknown-dev-waypoint-bar-button${devPathEditMode === 'altMinus1' ? ' explore-unknown-dev-waypoint-bar-button--active' : ''}`}
                    onClick={() => setDevPathEditMode('altMinus1')}
                  >
                    Alternative 1
                  </button>
                  <button
                    type="button"
                    className={`explore-unknown-dev-waypoint-bar-button${devPathEditMode === 'altMinus2' ? ' explore-unknown-dev-waypoint-bar-button--active' : ''}`}
                    onClick={() => setDevPathEditMode('altMinus2')}
                  >
                    Alternative 2
                  </button>
                  <button
                    type="button"
                    className={`explore-unknown-dev-waypoint-bar-button${devPathEditMode === 'exitPreviewNormal' ? ' explore-unknown-dev-waypoint-bar-button--active' : ''}`}
                    onClick={() => setDevPathEditMode('exitPreviewNormal')}
                  >
                    Exit preview (normal)
                  </button>
                  <button
                    type="button"
                    className={`explore-unknown-dev-waypoint-bar-button${devPathEditMode === 'exitPreviewAltMinus1' ? ' explore-unknown-dev-waypoint-bar-button--active' : ''}`}
                    onClick={() => setDevPathEditMode('exitPreviewAltMinus1')}
                  >
                    Exit preview (alt −1)
                  </button>
                  <button
                    type="button"
                    className={`explore-unknown-dev-waypoint-bar-button${devPathEditMode === 'exitPreviewAltMinus2' ? ' explore-unknown-dev-waypoint-bar-button--active' : ''}`}
                    onClick={() => setDevPathEditMode('exitPreviewAltMinus2')}
                  >
                    Exit preview (alt −2)
                  </button>
                  <span className="explore-unknown-dev-waypoint-bar-surface">
                    {(exploreSurface === 'productionhell'
                      ? 'productionhell'
                      : exploreSurface === 'autobahn'
                      ? 'autobahn'
                      : exploreSurface === 'kendrex'
                      ? 'kendrex'
                      : exploreSurface === 'pixelpast'
                      ? 'pixelpast'
                      : exploreSurface === 'boat'
                      ? 'boat'
                      : exploreSurface === 'treetop'
                      ? 'treetop'
                      : exploreSurface === 'lavender'
                      ? 'lavender'
                      : exploreSurface === 'fair'
                        ? 'fair'
                        : exploreSurface === 'desert'
                          ? 'desert'
                          : exploreSurface === 'cave'
                            ? 'cave'
                            : 'arrival') + ' · localStorage'}
                  </span>
                  {mapImageNaturalSize == null ? (
                    <span className="explore-unknown-dev-waypoint-bar-hint">Wait for map image to load before editing.</span>
                  ) : null}
                  {mapImageNaturalSize != null ? (
                    <span className="explore-unknown-dev-waypoint-bar-hint">
                      {waypointEditMode
                        ? isExitPreviewDevPathMode(devPathEditMode)
                          ? 'Drag the gold handle on the active route thumbnail. Copy outputs normal + alt −1 + alt −2.'
                          : 'Drag the magenta handles. Each tab shows only that route.'
                        : 'Select a route tab or exit-preview tab, then click Drag to edit.'}
                    </span>
                  ) : null}
                  {devWaypointStorageError ? (
                    <span className="explore-unknown-dev-waypoint-bar-error" role="alert">
                      {devWaypointStorageError}
                    </span>
                  ) : null}
                  {devWaypointUiError ? (
                    <span className="explore-unknown-dev-waypoint-bar-error" role="alert">
                      {devWaypointUiError}
                    </span>
                  ) : null}
                </div>,
                document.body
              )
            : null}
        </div>
      </div>
    </div>
    {showTutorialExploreUnknown &&
      createPortal(
        <TutorialMessageBox
          wrapperClassName="explore-unknown-first-visit-tutorial"
          image="/images/tutorial_explore_unknown.png"
          text={t('exploreUnknownPage.portalTutorialText')}
          voiceKey="exploreUnknownPage.portalTutorialText"
          continueButtonImagePath={BATTLE_UI_END_TURN_BUTTON_PATH}
          continueButtonClassName="strategy-modal-close-btn"
          paintedButtonSurfaceFilter={MENU_PAINTED_SURFACE_FILTER}
          continueLabelKey="exploreUnknownPage.tutorialClose"
          onContinue={async () => {
            if (!isReplayingExploreTutorial) {
              const token = localStorage.getItem('token');
              if (token) {
                await fetch(`${API_URL}/tutorial/mark-seen`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ messageKey: 'explore_unknown' }),
                });
                markSeenLocally('explore_unknown');
              }
            }
            setShowTutorialExploreUnknown(false);
          }}
        />,
        document.body
      )}
    {showResignRunDialog &&
      createPortal(
        <div
          className="explore-unknown-resign-dialog-overlay"
          data-resign-dialog-overlay
          data-block-global-esc-back
          onClick={() => {
            if (!resignRunBusy) handleCancelResignRun();
          }}
        >
          <div
            className="explore-unknown-resign-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="explore-unknown-resign-dialog-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="explore-unknown-resign-dialog-title" className="explore-unknown-resign-dialog-title">
              {t('exploreUnknownPage.resignRunConfirmTitle')}
            </h3>
            <p className="explore-unknown-resign-dialog-body">
              {t('exploreUnknownPage.resignRunConfirmBody')}
            </p>
            {resignRunError ? (
              <p className="explore-unknown-resign-dialog-error" role="alert">
                {resignRunError}
              </p>
            ) : null}
            <div className="explore-unknown-resign-dialog-actions">
              <button
                type="button"
                className={`explore-unknown-resign-dialog-button explore-unknown-resign-dialog-button--confirm${
                  resignRunDialogFocus === 'confirm'
                    ? ' explore-unknown-resign-dialog-button--focused'
                    : ''
                }`}
                disabled={resignRunBusy}
                onClick={() => void handleConfirmResignRun()}
                onFocus={() => setResignRunDialogFocus('confirm')}
              >
                {resignRunBusy
                  ? t('exploreUnknownPage.resignRunBusy')
                  : t('exploreUnknownPage.resignRunConfirmYes')}
              </button>
              <button
                type="button"
                className={`explore-unknown-resign-dialog-button explore-unknown-resign-dialog-button--cancel${
                  resignRunDialogFocus === 'cancel'
                    ? ' explore-unknown-resign-dialog-button--focused'
                    : ''
                }`}
                disabled={resignRunBusy}
                onClick={handleCancelResignRun}
                onFocus={() => setResignRunDialogFocus('cancel')}
              >
                {t('exploreUnknownPage.resignRunConfirmCancel')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {isExploreUnknownDevPlayer && showExploreUnknownPlayer1DevUi ? (
        <HideDevElementsForScreenshotButton
          user={user}
          onHide={hideDevElementsForScreenshotNow}
        />
      ) : null}
    </>
  );
}

export default ExploreUnknown;
